import type { ReactNode } from "react";
import React from "react";
import { listFormatConjunction } from "../lib/time/intl";
import type fr from "./fr";
import { capitalize, lowercase, printListStrings } from "diagnostics-lib/string";
import { en as DashboardsI18nEn } from "../components/Project/Dashboards/i18n";
import { en as FaultEventsI18nEn } from "../components/Project/Events/i18n";
import { MAX_ANALYSIS_COUNT, MAX_QUANTITY_COUNT } from "diagnostics-domain/constants";
import type { Reparability } from "../api/Reparability";
import type { Compliance } from "diagnostics-domain/quantity/compliance";
import formatNumber from "@ui/i18n/formatNumber";
import type { CustomTranslations } from "./types";
import { enValidations } from "./enValidations";
import { MAX_PROCESS_INDICATOR_COUNT } from "diagnostics-domain/quantity/processIndicator";

const renderFileText = (modelType: string, fileNumber: number) => (
  <>{`Model (file ${fileNumber}/${modelType === "DYMOLA" ? "2" : "3"}) :`}</>
);

const renderFileValue = (modelType: string, fileNumber: number) => {
  switch (fileNumber) {
    case 1:
      return <>{modelType === "DYMOLA" ? " dymosim" : " .fmu"}</>;
    case 2:
      return (
        <>
          {modelType === "DYMOLA"
            ? " dsin.txt"
            : " .csv (name containing iteration_variables / optional)"}
        </>
      );
    case 3:
      return " .csv (name containing parameters / optional)" as string;
    default:
      return;
  }
};

const en: typeof fr = {
  "diagnosis.charts.symptoms.title.qoi.one": "Performance indicator",
  "project.settings.quantity.side_panel.section.formula_input.usage_period_label":
    "Application start date",
  "quantities.formula_version.version_count": (n: number) =>
    n < 2 ? `${n} version` : `${n} versions`,
  ...DashboardsI18nEn,
  ...FaultEventsI18nEn,
  "action.confirm": "Confirm",
  "action.reset": "Reset",
  "activitities.refresh.tooltip": ({ count }: { count: number }) =>
    count === 1 ? "Display the new activity" : `Display ${count} new activities`,
  "activity.badge.project.deleted": "Project deleted",
  "activity.blank_state.global_description": "All actions will be displayed here.",
  "activity.blank_state.project_description":
    "All actions related to this project will be displayed here.",
  "activity.blank_state.title": "No activity",
  "activity.body.details.title": "Details",
  "activity.body.see_more": "Load more",
  "activity.event.badge.manual": "Manual",
  "activity.header.title": "Activity",
  "activity.hide_automatic_actions": "Hide automatic actions",
  "activity.hide_automatic_actions.tooltip":
    "Hide actions performed without user action (data import, running simulation and diagnosis computations)",
  "activity.paragraph.title": "Activity feed",
  "activity.selectFilter.activity": "Activity",
  "activity.selectFilter.authors": "User",
  "activity.selectFilter.events": "Event",
  "activity.selectFilter.projects": "Project",
  "activity.title": "Activity",
  "administration.application.api_key.column.api_key": "API key",
  "administration.application.api_key.column.created_at": "Created at",
  "administration.application.api_key.column.created_by": "Created by",
  "administration.application.api_key.modal.copy.button": "Copy API key",
  "administration.application.api_key.modal.create.button": "Generate API key",
  "administration.application.api_key.modal.create.text":
    "The API key generated will be used for authenticating the live data flow. You can only consult it at generation and in case of loss or corruption, the key should be deleted and a new one should be generated.",
  "administration.application.api_key.modal.create.title": "Generate API key",
  "administration.application.api_key.modal.revoke.button": "Delete",
  "administration.application.api_key.modal.revoke.text":
    "This action cannot be undone. This will permanently delete the API key, and the data flow using it will not work anymore without the use of another API key. Do you want to delete the API key?",
  "administration.application.api_key.modal.revoke.title": "Delete API Key",
  "administration.application.api_key.title": "Live data workflow",
  "administration.application.api_key.transaction.copy.failure": "Failed to copy the API key",
  "administration.application.api_key.transaction.copy.success": "API key copied to the clipboard",
  "administration.application.api_key.transaction.create.failure": "Failed to generate the API key",
  "administration.application.api_key.transaction.create.success": "API key generated",
  "administration.application.api_key.transaction.revoke.failure": "Failed to delete the API key",
  "administration.application.api_key.transaction.revoke.success": "API key deleted",
  "administration.classification.header.failures": "Faults",
  "administration.classification.header.quantities": "Quantities",
  "administration.failures.modal.action.confirm": (count: number) =>
    count === 1 ? "1 fault has been updated" : `${count} faults have been updated`,
  "administration.failures.table.header.actions.update": (count: number) =>
    count === 0 ? "Edit faults" : `Edit faults (${count})`,
  "administration.failures.table.header.category": "Category",
  "administration.failures.table.header.degradation_type": "Degradation type",
  "administration.failures.table.header.magnitude": "Amplitude",
  "administration.failures.table.header.max": "Max",
  "administration.failures.table.header.min": "Min",
  "administration.failures.table.header.reparability": "Outage time for repair",
  "administration.failures.table.header.suspectedElements": "Suspected elements",
  "administration.header.classification": "Asset management",
  "administration.header.customization": "Application",
  "administration.header.processes": "Processes",
  "administration.header.title": "Administration",
  "administration.header.users": "Users",
  "administration.modal.multiple_value": "Multiple values",
  "administration.processes.batches.last_updated_at": "Last updated at",
  "administration.processes.batches.priority": "Priority",
  "administration.processes.batches.process_type": "Process type",
  "administration.processes.batches.progress": "Progress",
  "administration.processes.batches.progress.done": "done",
  "administration.processes.batches.progress.queued": "queued",
  "administration.processes.batches.progress.running": "running",
  "administration.processes.batches.project_name": "Project",
  "administration.processes.batches.status": "Status",
  "administration.processes.batches.status.done": "Done",
  "administration.processes.batches.status.pending": "Pending",
  "administration.processes.batches.status.running": "Running",
  "administration.processes.batches.title": "Process batches",
  "administration.processes.empty": "No processes found.",
  "administration.processes.filters.all": "All",
  "administration.processes.filters.any": "Any",
  "administration.processes.filters.project.filter_this": "Filter by this project",
  "administration.processes.filters.status": "Status",
  "administration.processes.filters.type": "Type",
  "administration.processes.filters.updated_since": "Updated since",
  "administration.processes.filters.updated_since.any": "Any",
  "administration.processes.filters.updated_since.last_30_minutes": "Last 30 minutes",
  "administration.processes.filters.updated_since.last_6_hours": "Last 6 hours",
  "administration.processes.filters.updated_since.last_day": "Last day",
  "administration.processes.filters.updated_since.last_week": "Last week",
  "administration.processes.overview.by_project": "By project",
  "administration.processes.overview.by_status_type": "By status and type",
  "administration.processes.overview.count": "Count",
  "administration.processes.overview.project": "Project",
  "administration.processes.overview.status": "Status",
  "administration.processes.overview.title": "Process counts",
  "administration.processes.project_name.unknown": "Unknown project",
  "administration.processes.subtab.monitoring": "Monitoring",
  "administration.processes.subtab.trigger": "Trigger",
  "administration.processes.title": "Process monitoring",
  "administration.quantities.modal.action.confirm": (count: number) =>
    count === 1 ? "1 quantity has been updated" : `${count} quantities have been updated`,
  "administration.quantities.table.header.actions.edit": "Edit quantities",
  "administration.quantities.table.header.actions.update": ({ count }: { count: number }) =>
    count === 0 ? "Edit quantities" : `Edit quantities (${count})`,
  "administration.quantities.table.header.project_name": "Project",
  "administration.quantities.table.header.project_setup": "Setup",
  "administration.quantities.table.header.project_site": "Site",
  "administration.quantities.table.header.project_type": (
    translations: CustomTranslations,
  ): string => capitalize(translations.EN.TYPE),
  "administration.quantities.table.header.system": "System",
  "administration.table.failures.selection_button_title": "Select faults in the table",
  "administration.table.quantities.selection_button_title": "Select quantities in the table",
  "analysis.chart.tooltip.description.analysisPublication_status.included_in_reporting":
    "Analysis published and included in reporting",
  "analysis.chart.tooltip.description.analysisPublication_status.not_included_in_reporting":
    "Analysis published and not included in reporting\n(another more recent analysis published for the same month)",
  "app.administration.application.link.modal.form.delete.content": (nameLink: ReactNode) => (
    <>
      You are about to permanently delete the link to {nameLink}.
      <br />
      <br />
      This will remove it from the application menu.
    </>
  ),
  "app.administration.application.link.modal.form.edit.link_name.error.empty_field":
    "The display name must not be empty.",
  "app.administration.application.link.modal.form.edit.url.error.empty_field":
    "URL must not be empty.",
  "app.administration.application.link.modal.form.input.error.cannot_exceed_250_characters":
    "The display name cannot exceed 250 characters.",
  "app.administration.application.link.modal.form.input.url.error.cannot_exceed_4000_characters":
    "URL cannot exceed 4000 characters.",
  "app.administration.application.link.modal.form.text_en.label": "English display name",
  "app.administration.application.link.modal.form.text_fr.label": "French display name",
  "app.administration.application.link.modal.title.delete": "Delete the link",
  "app.administration.application.link.modal.title.edit": "Edit link",
  "app.customization.aggregation_period.power_outputs.button": "Cache power outputs",
  "app.customization.aggregation_period.power_outputs.description":
    "Queue power output calculations for all production projects.",
  "app.customization.aggregation_period.power_outputs.queued":
    "Power output caching has been queued.",
  "app.customization.aggregation_period.select.label": "Aggregation period",
  "app.customization.aggregation_period.select.tooltip":
    "The selected period corresponds to the default period displayed in graphs and used to calculate aggregate values in summary and events pages in particular.",
  "app.customization.algorithm.select.label": "Default version",
  "app.header.project_spinner.awaiting_step_evaluation": ({
    percentage,
    step,
  }: {
    percentage: number;
    step: string;
  }) => `${percentage}% awaiting ${step} evaluation`,
  "app.header.project_spinner.project_initialization_in_progress":
    "Project initialization in progress",
  "app.header.project_spinner.step.diagnosis_inputs": "diagnosis inputs",
  "app.header.project_spinner.step.external_gap_inputs": "external gap inputs",
  "app.header.project_spinner.step.simulation_inputs": "simulation inputs",
  "app.header.project_spinner.update_in_progress_for_analyses": ({
    outdatedCount,
    activeAnalysisCount,
  }: {
    outdatedCount: number;
    activeAnalysisCount: number;
  }) => `Update in progress for ${outdatedCount} analyses (out of ${activeAnalysisCount} total)`,
  "app.legals_modal.legals_link": "Consult the legal notices",
  "app.legals_modal.skip_message": "Don't show me this message next time I log on",
  "app.modal.legals.title": (year: string) => `Information METROSCOPE ${year}`,
  "button.refresh": "Refresh",
  "chart.display_options.tooltip": "Chart display options",
  "chart.period.no_analysis_yet": "No analysis available yet",
  "chart.period.no_data_yet": "No data available yet",
  "chart.period.now": "Now",
  "chart.period.zoom_all": "No data available for this period. Show full period",
  "chart.validity.reset_zoom.title": "Reset the zoom",
  "chart.validity.reset_zoom.tootltip":
    "The displayed range will include all valid measurements, excluding certain aberrant invalid measurements",
  "charts.flag.item.description": ({ name, description }: { name: string; description: string }) =>
    `${name}: ${description}`,
  "charts.info_tooltip.more": "Learn more",
  "charts.legend.confidence_area": "Confidence area",
  "charts.legend.internal_losses": (translations: CustomTranslations): string =>
    `Internal ${lowercase(translations.EN.LOSSES)}`,
  "charts.legend.internal_losses_global": (translations: CustomTranslations): string =>
    `${capitalize(translations.EN.LOSSES)}`,
  "charts.legend.remaining_symptom": "Remaining symptoms",
  "charts.legend.symptom": "Symptoms",
  "charts.legend.unexplained_losses": (translations: {
    FR: {
      LOSSES: string;
      UNEXPLAINED: string;
    };
    EN: {
      LOSSES: string;
      UNEXPLAINED: string;
    };
  }): string => `${capitalize(translations.EN.UNEXPLAINED)} ${lowercase(translations.EN.LOSSES)}`,
  "classification.title": "Asset management",
  "comments.answer": "Reply",
  "comments.author": "Author",
  "comments.blank_state_analysis.call_to_action": "Write a comment",
  "comments.blank_state_analysis.title": "No comment",
  "comments.create.placeholder": "Write a new comment",
  "comments.fault_event.menu.description": ({
    startsAt,
    closedAt,
    status,
  }: {
    startsAt: string;
    closedAt: string | null;
    status: string;
  }) => {
    if (closedAt === null) return `First detection: ${startsAt}, ${status}`;
    return `${startsAt} - ${closedAt}, ${status}`;
  },
  "comments.filters.event": "Event",
  "comments.filters.user": "User",
  "comments.setup.label": "Setup comment",
  "comments.setup.toggle": "Create a setup comment",
  "comments.setup.tooltip":
    "Create a comment reserved for users with setup permissions, visible in setup mode.",
  "comments.tooltip.analysis_outside_of_event_range":
    "Analysis outside of event range, association not possible",
  "comments.tooltip.associate_event": "Associate with an event",
  "comments.tooltip.connect_current_analysis": "Associate with selected analysis",
  "comments.tooltip.feature_under_development": "Feature under development, not accessible yet",
  "comments.tooltip.toggle_current_analysis_comments":
    "Display comments associated to the current selection only",
  "comments.tooltip.toggle_filters": "Filter on comments",
  "commom.widgets.failures.title": "Faults",
  "commom.widgets.losses.title": (translations: CustomTranslations): string =>
    `${capitalize(translations.EN.LOSSES)}`,
  "commom.widgets.processIndicators.title": "Process indicators",
  "common.button.add_comment_on_analysis": "Add a comment",
  "common.button.export_analysis": "Export analysis data",
  "common.date.last_analysis": "Latest analysis",
  "common.date.no_last_analysis": "No analysis",
  "common.date.no_published_analysis": "No published analysis",
  "common.empty.activeAnalyses.action": "Go to Data page",
  "common.empty.activeAnalyses.description": "Please create an analysis in Data page",
  "common.empty.activeAnalyses.title": "The project does not contain any analysis",
  "common.fault_status.legacy_rejected.relaunch": "(without diagnostic relaunch)",
  "common.fault_status.rejected.relaunch": "(with diagnostic relaunch)",
  "common.file": "File:",
  "common.file.import": "Import",
  "common.legal_notice": "Legal notice:",
  "common.legal_notice.title": "Import legal notice file",
  "common.none": "None",
  "common.processIndicators.blank.title": "No process indicator",
  "common.processIndicators.blank.title.description": "You can define them in the Settings page",
  "common.search.no_result": "No result",
  "common.see_more": "See more",
  "common.select": "Select",
  "common.select.empty": "None",
  "common.symptom.expected": "Expected",
  "common.symptom.measured": "Measured",
  "common.value": "Value",
  "commons.analysis_title": "Detail of the selected analysis",
  "commons.analysis_title.group": ({ group }: { group: string }) => `Cycle ${group}`,
  "commons.analysis_title.group_and_date": ({ group, date }: { group: string; date: string }) =>
    `Cycle ${group} - ${date}`,
  "commons.analysis_title.group_and_label": ({ group, label }: { group: string; label: string }) =>
    `Cycle ${group} - Test ${label}`,
  "commons.analysis_title.group_and_label_and_date": ({
    group,
    label,
    date,
  }: {
    group: string;
    label: string;
    date: string;
  }) => `Cycle ${group} - Test ${label} - ${date}`,
  "commons.analysis_title.label": ({ label }: { label: string }) => `Test ${label}`,
  "commons.analysis_title.test_and_date": ({ label, date }: { label: string; date: string }) =>
    `Test ${label} - ${date}`,
  "commons.button.activate": "Create",
  "commons.button.cancel": "Cancel",
  "commons.button.deactivate": "Delete",
  "commons.button.publish": "Publish",
  "commons.button.save": "Save",
  "commons.button.unpublish": "Unpublish",
  "commons.copyDetail": "Copy detail",
  "commons.copyDetail.toast": "Detail has been copied",
  "commons.date_picker.from": "From",
  "commons.date_picker.to": "to",
  "commons.details": "Details",
  "commons.failures.table.manualTitle": "Non-modeled fault",
  "commons.input.error.text_to_long": "Text exceeding the character limit",
  "commons.message_card.answer": "Reply",
  "commons.message_card.answers": ({ count }: { count: number }) =>
    count === 1 ? `${count} reply` : `${count} replies`,
  "commons.message_card.ask_delete": "Delete message?",
  "commons.message_card.ask_delete_thread": "Delete the thread?",
  "commons.message_card.cancel": "Cancel",
  "commons.message_card.confirm": "Confirm",
  "commons.message_card.confirm_delete": "Delete",
  "commons.message_card.delete": "Delete",
  "commons.message_card.delete_thread": "Delete the thread",
  "commons.message_card.edit": "Edit",
  "commons.message_card.export": "Export",
  "commons.message_card.replace": "Replace",
  "commons.modal.confirm_loading.message": "Do you confirm files loading?",
  "commons.modal.expected_file.link": "Example file",
  "commons.modal.expected_file_format": "Expected file format:",
  "commons.modal.identified_file": "Identified file:",
  "commons.modal.identified_files": "Identified files:",
  "commons.modal.not_valid_form.message": ({ icon }: { icon: React.ReactNode }) => (
    <>{icon} File import is blocked. Please perform required changes before trying again.</>
  ),
  "commons.no_quantities_yet.text": "No quantities exist in the project.",
  "commons.none_value": ({ icon }: { icon: React.ReactNode }) => (
    <>
      <> none {icon}</>
    </>
  ),
  "commons.overview.header.legend.filled": "Confirmed / False detection",
  "commons.overview.header.legend.filled.description": "Confirmed or declared as a false detection",
  "commons.overview.header.legend.manual_failure": "Non-modeled fault",
  "commons.overview.header.legend.manual_failure.description": "Non-modeled fault",
  "commons.overview.header.legend.not_filled": "Suspected",
  "commons.overview.header.legend.not_filled.description":
    "Suspected fault, under analysis and needing confirmation to be shown in the summary page",
  "commons.overview.header.legend.to_be_analysed.description":
    "Fault proposed by the algorithm, needing an analysis and a status change",
  "commons.placeholder.search": "Search",
  "commons.quantities.selectFilter.filters": "Filters",
  "commons.quantities.selectFilter.filters.activation": "Analysis",
  "commons.quantities.selectFilter.filters.metrology": "Metrology",
  "commons.quantities.selectFilter.indicator": "Indicator",
  "commons.quantities.selectFilter.role": "Role",
  "commons.quantities.selectFilter.system": "System",
  "commons.quantities.selectFilter.threshold": "Validity thresholds",
  "commons.quantities.selectFilter.type": "Type",
  "commons.quantities.selectFilter.type.dashboard": "Dashboard",
  "commons.quantities.selectFilter.type.formula": "Formula",
  "commons.quantities.selectFilter.type.setup": "Setup",
  "commons.quantities.selectFilter.unit": "Unit",
  "commons.quantities.selectTypeOption.calculated_value": "Calculated",
  "commons.quantities.selectTypeOption.indicator.performance": "Performance indicator",
  "commons.quantities.selectTypeOption.indicator.process": "Process indicator",
  "commons.quantities.selectTypeOption.measure": "Measured",
  "commons.quantities.selectTypeOption.observable": "Observable",
  "commons.quantities.selectTypeOption.others": "None",
  "commons.quantities.selectTypeOption.simulationInput": "Model input",
  "commons.quantities.selectTypeOption.simulationOutput": "Model output",
  "commons.quantities.threshold.atleast.one.label": "At least one threshold",
  "commons.quantities.threshold.none.label": "No thresholds",
  "commons.select.manage_columns": "Manage columns",
  "commons.title.plant_state": "Plant state",
  "commons.tooltip.research": "Research project",
  "commons.tooltip.warning.selected_analysis_not_filter_compliant":
    "The selected analysis does not respect the analysis filters defined in the settings",
  "commons.tooltip.warning.selected_analysis_not_filter_compliant.view_activation_filters":
    "View analysis filters",
  "commons.tooltip.warning.selected_analysis_not_filter_compliant_with_details": (
    compliance: Compliance,
    link: ReactNode,
    active: boolean,
  ) => {
    const filters = Object.entries(compliance).reduce<[string, React.JSX.Element][]>(
      (acc, [quantityName, { unit, missing, min, max, maxStd }]) => {
        if (missing === true) acc.push([quantityName, <>missing value</>]);

        if (min) {
          const fValue = formatNumber(min.actual, { precision: 2 });
          const fThreshold = formatNumber(min.threshold, { unit });
          acc.push([
            quantityName,
            <>
              under threshold value ({fValue} &lt; {fThreshold})
            </>,
          ]);
        }

        if (max) {
          const fValue = formatNumber(max.actual, { precision: 2 });
          const fThreshold = formatNumber(max.threshold, { unit });
          acc.push([
            quantityName,
            <>
              above threshold value ({fValue} &gt; {fThreshold})
            </>,
          ]);
        }

        if (maxStd)
          if (maxStd.actual === null) {
            acc.push([quantityName, <>missing standard deviation value</>]);
          } else {
            const fStd = formatNumber(maxStd.actual, { precision: 2 });
            const fStdThreshold = formatNumber(maxStd.threshold, { unit });
            acc.push([
              quantityName,
              <>
                above threshold standard deviation ({fStd} &gt; {fStdThreshold})
              </>,
            ]);
          }

        return acc;
      },
      [],
    );

    filters.sort((a, b) => a[0].localeCompare(b[0]));
    return (
      <>
        {active ? "The selected analysis" : "The selected point"} does not respect{" "}
        {filters.length > 1 ? `these ${filters.length} filters` : "this filter"}:
        <br />
        <ul>
          {filters.map(([name, desc]) => (
            <li key={`${name}/${desc}`}>
              - <b>{name}</b>: {desc}
            </li>
          ))}
        </ul>
        <br />
        {link}
      </>
    );
  },
  "commun.tooltip.label_value"({
    label,
    value,
  }: {
    label: React.ReactNode;
    value: React.ReactNode;
  }) {
    return (
      <>
        {label}: {value}
      </>
    );
  },
  customization: "Application",
  "customization.algorithm.tooltip.text":
    "This version will be assigned to all new setups upon their creation. Changing the default version will have no impact on existing setups.",
  "customization.application.link.modal.subtitle":
    "Enter below the display name and URL you wish to appear in the menu",
  "customization.application.link.modal.title": "Add a link",
  "customization.demo_environment.action": "Action",
  "customization.demo_environment.checking_refresh_status": "Checking refresh status…",
  "customization.demo_environment.keep_demos_updated": "Sources to keep updated automatically",
  "customization.demo_environment.keep_demos_updated.tooltip":
    "The application automatically shifts the selected sources' data to the current date each day.",
  "customization.demo_environment.keep_demos_updated_form.toast":
    "The sources to keep recent have been updated.",
  "customization.demo_environment.refresh_in_progress":
    "A source refresh is in progress. Actions are unavailable until it finishes.",
  "customization.demo_environment.refresh_source": "Refresh this source now",
  "customization.demo_environment.refresh_sources.button": "Refresh all sources now",
  "customization.demo_environment.refresh_sources.toast": "Source refresh completed.",
  "customization.demo_environment.remove_source": "Remove source",
  "customization.demo_environment.search_sources": "Search sources to add",
  "customization.demo_environment.selected_sources": ({ count }: { count: number }) =>
    `${count} source${count === 1 ? "" : "s"} selected for daily refresh`,
  "customization.legals.modal_prompt": "Content of the GDPR modal",
  "customization.modal.edit.translation.subtitle":
    "Enter below the terms you wish to appear in the interface",
  "customization.modal.edit.translation.title": "Change the terminology used",
  "customization.modalForm.impactedElement": "Impacted element",
  "customization.modalForm.input.english": "English term",
  "customization.modalForm.input.error.cannot_be_empty": "This field cannot be empty",
  "customization.modalForm.input.error.text_to_long": `This field is limited to 100 characters`,
  "customization.modalForm.input.french": "French term",
  "customization.section.ab.db_prisma_replace_enabled":
    "Enable PRISMA database for certain node-pg features. See grafana db-test to compare runtimes.",
  "customization.section.advanced_features.title": "Advanced features",
  "customization.section.cac.dispatch.button": ({ count }: { count: number }) =>
    `Dispatch the CAC for ${count} project${count > 1 ? "s" : ""}`,
  "customization.section.cac.dispatch.label": "dispatch",
  "customization.section.cac.empty_state": "No projects match your search.",
  "customization.section.cac.filter.show_production_projects": "Display production projects",
  "customization.section.cac.filter.show_research_projects": "Display research projects",
  "customization.section.cac.global_status": ({
    running,
    waiting,
    success,
    failed,
  }: {
    running: number;
    waiting: number;
    success: number;
    failed: number;
  }) => {
    const done = success + failed;
    return `${running} in progress, ${waiting} waiting, ${done} done (${success} successful, ${failed} failed)`;
  },
  "customization.section.cac.launch.button": ({ count }: { count: number }) =>
    `Launch the CAC for ${count} project${count > 1 ? "s" : ""}`,
  "customization.section.cac.launch.label": "launch",
  "customization.section.cac.modal.title": "Select the projects to dispatch the CAC to",
  "customization.section.cac.no_operation_launched": "No operation launched",
  "customization.section.cac.open_modal_button": "Dispatch the CAC to multiple projects…",
  "customization.section.cac.project_status.failed": "Failed",
  "customization.section.cac.project_status.running": "In progress…",
  "customization.section.cac.project_status.selected": "Selected",
  "customization.section.cac.project_status.success": "Done",
  "customization.section.cac.project_status.waiting": "Waiting",
  "customization.section.cac.reset.button": ({ count }: { count: number }) =>
    `Reset the CAC for ${count} project${count > 1 ? "s" : ""}`,
  "customization.section.cac.reset.label": "reset",
  "customization.section.cac.running_hint": ({ operation }: { operation: string }) =>
    `${capitalize(operation)} in progress: selection is locked until completion.`,
  "customization.section.cac.search.aria_label": "Search a project",
  "customization.section.cac.search.placeholder": "Search by name…",
  "customization.section.cac.select_all": ({ total }: { total: number }) => `Select all (${total})`,
  "customization.section.cac.title": "CAC management",
  "customization.section.data_display.switch.display_financial_impact_dashboard.text":
    "Enable display of estimated financial impact (Dashboards)",
  "customization.section.data_display.switch.display_publication_markers.text":
    "Display publication markers by default",
  "customization.section.data_display.switch.group_quantities_by_system.text":
    "Group quantities by system (Data and Symptoms pages)",
  "customization.section.data_display.switch.hide_symptoms_global_impact.text":
    "Hide symptoms in Global Impact view (Faults page)",
  "customization.section.data_display.title": "Data display",
  "customization.section.data_display.tooltips.display_publication_markers.text":
    "In all graphs, display by default the mark that indicates wether the analysis is published",
  "customization.section.data_display.tooltips.group_quantities_by_system.text":
    "In the Data and Symptoms pages, presents the quantities in the selector on the left grouped according to their system",
  "customization.section.data_display.tooltips.hide_symptoms_global_impact.text":
    "In Faults page, hides data related to symptoms and residual symptoms when graph is in Global Impact view",
  "customization.section.data_import.switch.active_context_file_format.text":
    "Activate use of .context file format",
  "customization.section.data_import.switch.active_data_import.text":
    "Activate data import from a project",
  "customization.section.data_import.switch.source_import_conflict.text":
    "Enable data replacement in case of conflict",
  "customization.section.data_import.title": "Data import",
  "customization.section.data_import.tooltips.active_data_import.text":
    "Adds a button for importing data from a project page",
  "customization.section.data_import.tooltips.source_import_conflict.text":
    "If enabled, it will be possible to replace snapshots on which a conflict has been identified during data import.",
  "customization.section.digital_twin.title": "Digital twin",
  "customization.section.event.enable_manual_events_dev_mode":
    "Enable creation and display of manual events",
  "customization.section.events.admin.switch": "Enable events (Admin only)",
  "customization.section.events.disable_event_group_constraint":
    "Ignore event group when aggregating fault events",
  "customization.section.events.grouping": "Enable fault event grouping and ungrouping",
  "customization.section.events.priority_toggle": "Disable Medium priority",
  "customization.section.events.switch": "Enable events",
  "customization.section.failure_analysis.migration.description":
    "Test the migration of manual failures to manual fault events (DRY-RUN mode)",
  "customization.section.failure_analysis.migration.dry_run.blocked": ({
    blocked,
    total,
  }: {
    blocked: number;
    total: number;
  }) =>
    `Migration preview downloaded. ${blocked} of ${total} detections are blocked and require attention.`,
  "customization.section.failure_analysis.migration.dry_run.button": "Download migration preview",
  "customization.section.failure_analysis.migration.dry_run.loading": "Generating preview...",
  "customization.section.failure_analysis.migration.dry_run.success": ({
    total,
  }: {
    total: number;
  }) => `Migration preview downloaded. All ${total} detections are ready to migrate.`,
  "customization.section.failure_analysis.migration.execute.button": "Launch the migration",
  "customization.section.failure_analysis.migration.execute.close": "Close",
  "customization.section.failure_analysis.migration.execute.description":
    "Migrate manual failures to manual fault events (mode RUN).",
  "customization.section.failure_analysis.migration.execute.error":
    "An error occurred during migration.",
  "customization.section.failure_analysis.migration.execute.failure_reason": "Failure Reason",
  "customization.section.failure_analysis.migration.execute.failures_title": "Migration Failures",
  "customization.section.failure_analysis.migration.execute.loading": "Running migration...",
  "customization.section.failure_analysis.migration.execute.project_id": "Project ID",
  "customization.section.failure_analysis.migration.execute.show_more": "Show more",
  "customization.section.failure_analysis.migration.execute.success": ({
    migratedProjectsCount,
    migratableProjectsCount,
    failedProjectMigrationsCount,
    blockedCount,
  }: {
    migratedProjectsCount: number;
    migratableProjectsCount: number;
    failedProjectMigrationsCount: number;
    blockedCount: number;
  }) =>
    `Migrated ${migratedProjectsCount} projects out of ${migratableProjectsCount} with ${failedProjectMigrationsCount} failed migrations. There remain ${blockedCount} blocked detections that require attention before migration.`,
  "customization.section.failure_analysis.migration.rollback.description":
    "Delete manual fault events created during the migration for selected projects.",
  "customization.section.failure_analysis.migration.rollback.empty_state":
    "No projects are available for rollback.",
  "customization.section.failure_analysis.migration.rollback.global_status": ({
    deleting,
    failed,
    success,
    waiting,
  }: {
    deleting: number;
    failed: number;
    success: number;
    waiting: number;
  }) => `Rolling back ${deleting}. Queued ${waiting}. Success ${success}. Failed ${failed}.`,
  "customization.section.failure_analysis.migration.rollback.load_error":
    "Unable to load rollback projects.",
  "customization.section.failure_analysis.migration.rollback.loading":
    "Loading rollback projects...",
  "customization.section.failure_analysis.migration.rollback.modal.title": "Rollback management",
  "customization.section.failure_analysis.migration.rollback.open_modal_button":
    "Open rollback manager for manual fault events",
  "customization.section.failure_analysis.migration.rollback.project_events": ({
    count,
  }: {
    count: number;
  }) => `${count} manual fault events`,
  "customization.section.failure_analysis.migration.rollback.project_status.deleting":
    "Rolling back",
  "customization.section.failure_analysis.migration.rollback.project_status.failed": "Failed",
  "customization.section.failure_analysis.migration.rollback.project_status.selected": "Selected",
  "customization.section.failure_analysis.migration.rollback.project_status.success": "Rolled back",
  "customization.section.failure_analysis.migration.rollback.project_status.waiting": "Queued",
  "customization.section.failure_analysis.migration.rollback.rollback.button": ({
    count,
  }: {
    count: number;
  }) => `Rollback ${count} projects`,
  "customization.section.failure_analysis.migration.rollback.running_hint":
    "Rollback is running. Keep this window open until it completes.",
  "customization.section.failure_analysis.migration.rollback.search.aria_label":
    "Search rollback projects",
  "customization.section.failure_analysis.migration.rollback.search.placeholder": "Search projects",
  "customization.section.failure_analysis.migration.rollback.select_all": ({
    total,
  }: {
    total: number;
  }) => `Select all (${total})`,
  "customization.section.failure_analysis.migration.rollback.title": "Rollback management",
  "customization.section.failure_analysis.migration.short_title": "Migration of manual failures",
  "customization.section.failure_analysis.migration.title":
    "Migration of manual failures to manual fault events",
  "customization.section.failure_analysis.switch.allow_dymosim_fmu_t0.text":
    "Allow use of dymosim and FMU_T0 files",
  "customization.section.failure_analysis.switch.restart_diagnostics_manually.text":
    "Automatically restart diagnostics upon false detection of a fault",
  "customization.section.failure_analysis.title": "Fault analysis",
  "customization.section.failure_analysis.tooltips.allow_dymosim_fmu_t0.text":
    "Allow the use of dymosim and FMU_T0_INJECTION files as models in the setup",
  "customization.section.failure_analysis.tooltips.restart_diagnostics_manually.text":
    "If the option is not enabled, the user will be able to manually restart the diagnosis.",
  "customization.section.failure_grouping.switch": "Enable fault grouping",
  "customization.section.fleet_view.repairability_matrix":
    "Enable repairability matrix for fleet view",
  "customization.section.fleet_view.switch": "Enable fleet view",
  "customization.section.formula_versions_enabled":
    "Enable creation and display of formula versions",
  "customization.section.hide_legacy_summary_page": "Hide legacy summary page",
  "customization.section.legals.title": "Management of legal notices",
  "customization.section.link.title": "Customize links",
  "customization.section.link.title.button.add": "Add a link",
  "customization.section.quantities.new_navigation": "Enable new navigation within a project",
  "customization.section.quantity.versioning.name":
    "Enable creating multiple versions for a quantity",
  "customization.section.setup_mode": "Enable setup mode",
  "customization.section.show_legacy_summary_badge": "Display legacy badge on summary tab",
  "customization.section.terminology.title": "Customize the terminology used",
  "customization.settings.update.success": "Application settings have been successfuly updated.",
  "customization.table.translation.average": "Average",
  "customization.table.translation.dashboard": "Dashboard",
  "customization.table.translation.dashboards": "Dashboards",
  "customization.table.translation.en": "English term",
  "customization.table.translation.explained": "Explained",
  "customization.table.translation.fr": "French term",
  "customization.table.translation.key": "Name",
  "customization.table.translation.loss": "Loss",
  "customization.table.translation.losses": "Losses",
  "customization.table.translation.reparability_0": "Outage time for repair level 0",
  "customization.table.translation.reparability_1": "Outage time for repair level 1",
  "customization.table.translation.reparability_2": "Outage time for repair level 2",
  "customization.table.translation.type": "Project type",
  "customization.table.translation.unexplained": "Unexplained",
  "customization.title": "Application administration",
  "dataSource.import.analysis.section.title": "Imported data analysis",
  "dataSource.import.conflict.check.confirmation_force_conflict.text":
    "Confirm deletion and replacement",
  "dataSource.import.conflict.confirmation_force_conflict.text":
    "By confirming, the old values will be deleted and replaced with the new ones.",
  "dataSource.import.error.fileTypeNotSupported": (type: string) =>
    `The file type ${type} is not supported. Please upload a .json, .csv or .context file.`,
  "dataSource.import.error.multipleFileTypes": (types: string[]) =>
    `Multiple file types detected (${types
      .join(",")
      .replace("", "context")}). Please upload only one type of file at a time.`,
  "dataSource.import.error.unexpected_source_id":
    "The source id contains invalid characters ([a-zA-Z0-9_]{0,49}).",
  "dataSource.import.extraInfo.section.title": "Additional import information",
  "dataSource.import.info.timezoneDefault": ({ projectTimeZone }: { projectTimeZone: boolean }) =>
    `The time zone of the data to be imported was not detected in the file. Please confirm the time zone to use (by default, your ${
      projectTimeZone ? "project" : "browser"
    }).`,
  "dataSource.import.parsing.error.conflicting_sensor_value": () =>
    "Sensor values for a snapshot do not match",
  "dataSource.import.parsing.error.conflicting_source_id": () => "Snapshot sources do not match",
  "dataSource.import.parsing.error.duplicated_snapshot_date":
    "2 snapshots cannot be related to the exact same date",
  "dataSource.import.parsing.error.empty_data_extraction": "No data extracted from file",
  "dataSource.import.parsing.error.invalid_group_too_long": () =>
    "Invalid group format: 100 characters max with alphanumeric or _",
  "dataSource.import.parsing.error.invalid_json": "JSON file format is incorrect",
  "dataSource.import.parsing.error.invalid_label_too_long": () =>
    "Invalid label format: 100 characters max with alphanumeric or _",
  "dataSource.import.parsing.error.invalid_sensor_value": () =>
    "The value of a sensor in a snapshot is invalid",
  "dataSource.import.parsing.error.invalid_sensor_value_missing_mean": () =>
    "The average value of a snapshot sensor is missing",
  "dataSource.import.parsing.error.invalid_source_id": () =>
    "SourceId value must not exceed 100 characters",
  "dataSource.import.parsing.error.malformed_json": "JSON file syntax is incorrect",
  "dataSource.import.parsing.error.missing_snapshot_date": () => `Snapshot date is missing`,
  "dataSource.import.parsing.error.missing_source_id": "SourceId is mandatory for .json format",
  "dataSource.import.parsing.error.snapshot_metadata_missmatch": () =>
    "Snapshot metadata does not match",
  "dataSource.import.project.bypass_source_id_missmatch": (currentSourceId: string) =>
    `Continue the import into the source ${currentSourceId}`,
  "dataSource.import.project.source_id_missmatch": ({
    expectedSourceId,
    currentSourceId,
  }: {
    expectedSourceId: string;
    currentSourceId: string;
  }) =>
    `The source of the file (${currentSourceId}) is not the same as the one of the current project (${expectedSourceId}).`,
  "dataSource.import.projectInfo": ({
    projectCount,
    sourceId,
    productionProjectNames,
    researchProjectNames,
    inProject,
  }: {
    projectCount: number;
    sourceId: string;
    productionProjectNames: string[];
    researchProjectNames: string[];
    inProject: boolean;
  }) => {
    if (
      (productionProjectNames.length === 1 || researchProjectNames.length === 1) &&
      projectCount === 1
    ) {
      return (
        <>
          Imported data will flow into the {inProject ? "other" : ""}{" "}
          {productionProjectNames.length === 1
            ? "production"
            : researchProjectNames.length === 1
              ? "research"
              : ""}{" "}
          project <strong>{productionProjectNames[0]}</strong> associated with the source{" "}
          <strong>{sourceId}</strong>.
        </>
      );
    }

    if (productionProjectNames.length === 1 && projectCount > 1) {
      return (
        <>
          The imported data will flow into{" "}
          <strong>
            the {projectCount} {inProject ? " other" : ""} projects
          </strong>{" "}
          associated with the data source <strong>{sourceId}</strong>, whose project{" "}
          {productionProjectNames[0]}.
        </>
      );
    }

    return (
      <>
        The imported data will flow into{" "}
        {projectCount === 1 ? (
          <>
            <strong>the {inProject ? "other" : ""} associated project</strong>
          </>
        ) : (
          <>
            <strong>
              the {projectCount} {inProject ? " other" : ""} associated projects
            </strong>
          </>
        )}{" "}
        with the data source <strong>{sourceId}</strong>
        {productionProjectNames?.length > 1 ? (
          <>
            , whose production projects <strong>{productionProjectNames.join(", ")}</strong>.
          </>
        ) : productionProjectNames?.length === 1 ? (
          <>
            , whose production project <strong>{productionProjectNames.join(", ")}</strong>.
          </>
        ) : (
          <>.</>
        )}
      </>
    );
  },
  "dataSource.import.projectInfo.details.summary": (researchProjectCount: number) => (
    <>Impacted research projects ({researchProjectCount})</>
  ),
  "dataSource.import.snapshot.conflict.export": "Point details (.json)",
  "dataSource.import.snapshotInfo": ({
    sensorCount,
    snapshotCount,
    startDate,
    endDate,
  }: {
    sensorCount: number;
    snapshotCount: number;
    startDate: string;
    endDate: string;
  }) => {
    if (snapshotCount === 1) {
      return (
        <>
          The identified data includes <strong>{sensorCount} sensors</strong> and corresponds to{" "}
          <strong>{startDate}</strong>:
        </>
      );
    }

    return (
      <>
        The identified data includes <strong>{sensorCount} sensors</strong> and corresponds{" "}
        {startDate === endDate ? (
          <>at {startDate}</>
        ) : (
          <>
            to the period
            <strong>
              {" "}
              {startDate} - {endDate}
            </strong>
          </>
        )}
        {snapshotCount > 1 && (
          <>
            {" "}
            (<strong>{snapshotCount}</strong> data points)
          </>
        )}
        :
      </>
    );
  },
  "dataSource.import.snapshotInfo.details.text.conflictSnapshot": (conflictSnapshotCount: number) =>
    `Existing points, with conflict: ${conflictSnapshotCount}`,
  "dataSource.import.snapshotInfo.details.text.conflictSnapshot_info":
    "The values differ from those already present in the current data source.",
  "dataSource.import.snapshotInfo.details.text.ignoredSnapshot": (ignoredSnapshot: number) =>
    `Existing points, not impacted: ${ignoredSnapshot}`,
  "dataSource.import.snapshotInfo.details.text.mergedSnapshot": (mergedSnapshotCount: number) =>
    `Existing points, completed: ${mergedSnapshotCount}`,
  "dataSource.import.snapshotInfo.details.text.newSnapshot": (newSnapshotCount: number) =>
    `New points: ${newSnapshotCount}`,
  "dataSource.import.snapshotInfo.empty":
    "The import is not necessary because all the data is already present.",
  "dataSource.import.source_id_input_name": "Source",
  "dataSource.import.warning.expected_source_id":
    "No source detected in file[s]. Please fill in the source below.",
  "dateSource.import.timezone.selectLabel": "Timezone",
  "datetime.fractional": ({
    type,
    years,
    days,
    months,
    withConjunction = false,
  }: {
    type: "date" | "duration";
    years: number;
    days: number;
    months: number;
    withConjunction?: boolean;
  }) => {
    const parts: string[] = [];
    if (years > 0) {
      parts.push(`${years} year${years > 1 ? "s" : ""}`);
    }
    if (months > 0) {
      parts.push(`${months} month${months > 1 ? "s" : ""}`);
    }
    if (days > 0 || (years === 0 && months === 0 && parts.length === 0)) {
      parts.push(`${days} day${days > 1 ? "s" : ""}`);
    }
    let formattedString =
      parts.length > 1
        ? parts.slice(0, 2).join(withConjunction ? " and " : ", ")
        : parts.length > 0
          ? (parts[0] ?? "")
          : "0 day";
    if (type === "date") {
      formattedString += " ago";
    }
    return formattedString;
  },
  deleted: "Deleted",
  "diagnosis.chart.evolution.axis.y": (
    performanceIndicatorName: string,
    performanceIndicatorUnit: string | null,
  ) =>
    `Impact on ${performanceIndicatorName} ${performanceIndicatorUnit ? `(${performanceIndicatorUnit})` : ""}`,
  "diagnosis.chart.flag_selected_quantities": "Flag selected quantities",
  "diagnosis.chart.flag_selected_quantities.tooltip.description":
    "Flagging selected quantities results in their synchronization between this histogram and Data and Symptoms pages. Besides, these quantities will be displayed on the left of the histogram, whatever the sorting option.",
  "diagnosis.chart.legend.tooltip.is_manual_failure": "Non-modeled fault",
  "diagnosis.chart.show_deactivated_observables": "Display deactivated observables",
  "diagnosis.chart.show_deactivated_observables.tooltip":
    "Deactivated observables will appear with a grayed-out name.",
  "diagnosis.chart.tooltip.amplitude": "Amplitude",
  "diagnosis.chart.tooltip.button.confirmed": "Confirmed",
  "diagnosis.chart.tooltip.button.false_detection": "False detection",
  "diagnosis.chart.tooltip.button.suspected": "Suspected",
  "diagnosis.chart.tooltip.button.to_be_analysed": "To be analysed",
  "diagnosis.chart.tooltip.description.diagnosticError": (statusInfo: string) =>
    `Diagnosis error\n${statusInfo}\n...\nClick on the red marker to copy the full error message`,
  "diagnosis.chart.tooltip.description.no_failure_suspected": "No fault found in diagnosis",
  "diagnosis.chart.tooltip.description.uncompletedDiagnostic": "Diagnosis not carried out",
  "diagnosis.chart.tooltip.event.link": "View",
  "diagnosis.chart.tooltip.event.title": "Event:",
  "diagnosis.chart.tooltip.losses"({ qoi }: { qoi: string }) {
    return `Impact on ${qoi}`;
  },
  "diagnosis.chart.tooltip.message.incompatible_status": (falseDetectionCount: number) =>
    `The selection contains ${falseDetectionCount} element${
      falseDetectionCount > 1 ? "s" : ""
    } in "False detection", bulk status modification is impossible.`,
  "diagnosis.chart.tooltip.message.relaunch_diagnostic":
    "No diagnosis was relaunched following this false detection",
  "diagnosis.chart.tooltip.selectedPoints": ({ count }: { count: number }) =>
    count === 1 ? `${count} selected fault` : `${count} selected faults`,
  "diagnosis.chart.tooltip.status.action.reintegrate_in_diagnosis": "Reintegrate in diagnosis",
  "diagnosis.chart.tooltip.status.action.relaunch_diagnostic": "Relaunch the diagnosis",
  "diagnosis.chart.tooltip.subtitle_link.update_reparability": "Update outage time for repair:",
  "diagnosis.chart.tooltip.subtitle_link.update_status": "Update status:",
  "diagnosis.charts.symptoms.axis.y": (
    performanceIndicatorName: string,
    performanceIndicatorUnit: string | null,
  ) => `Impact on ${performanceIndicatorName} (${performanceIndicatorUnit})`,
  "diagnosis.charts.symptoms.legend.confirmed": "Confirmed",
  "diagnosis.charts.symptoms.legend.suspected": "Suspected",
  "diagnosis.charts.symptoms.legend.symptoms": "Symptoms",
  "diagnosis.charts.symptoms.legend.tooltip.confirmed": "Fault manually confirmed by a user",
  "diagnosis.charts.symptoms.legend.tooltip.no_symptom": "Unavailable value for this observable",
  "diagnosis.charts.symptoms.legend.tooltip.sigma":
    "Area outside of which a gap on an observable is deemed as a symptom",
  "diagnosis.charts.symptoms.legend.tooltip.suspected":
    "Suspected fault, needing confirmation to be shown in the summary page",
  "diagnosis.charts.symptoms.no_symptoms": "Non-calculable symptom",
  "diagnosis.charts.symptoms.observable.axis.y": "Normalized impact",
  "diagnosis.charts.symptoms.title.obs": "Observables",
  "diagnosis.charts.symptoms.title.obs.many": ({ count }: { count: number }) =>
    `Observables (${count})`,
  "diagnosis.charts.symptoms.title.obs.one": "Observable",
  "diagnosis.charts.symptoms.title.qoi": "Performance indicators",
  "diagnosis.charts.symptoms.title.qoi.many": ({ count }: { count: number }) =>
    `Performance indicators (${count})`,
  "diagnosis.header.dropdown.manage_manual_failure": "Manage non-modelled faults",
  "diagnosis.legend.empty_period": "No detection in displayed period",
  "diagnosis.legend.group_faults.button": "Manage fault groups",
  "diagnosis.legend.group_faults.column.failure": "Fault",
  "diagnosis.legend.group_faults.column.group": "Group",
  "diagnosis.legend.group_faults.empty": "No faults available",
  "diagnosis.legend.group_faults.modal.subtitle": "Type to search or create a group",
  "diagnosis.legend.group_faults.modal.title": "Manage fault groups",
  "diagnosis.legend.group_faults.search.empty": "No matching faults",
  "diagnosis.legend.group_faults.search.placeholder": "Search faults",
  "diagnosis.legend.group_faults.select_placeholder": "Type to search or create a group",
  "diagnosis.legend.group_faults.toast.saved": "Fault groups updated",
  "diagnosis.legend.group_faults.ungrouped": "Ungrouped",
  "diagnosis.legend.marker.to_be_analysed": "To be analysed",
  "diagnosis.legend.no_failure": "No fault",
  "diagnosis.overview.amplitude": "Amplitude",
  "diagnosis.overview.card.title": "Fault evolution",
  "diagnosis.overview.header.option": ({ name }: { name: string }) => `${name}`,
  "diagnosis.overview.header.option.global": "Global impact",
  "diagnosis.overview.header.option.global.description":
    "This is the preferred display mode to show the system state globally.\nY axis quantifies the deviation to the expected behavior, using all the observables.\nSymptoms curves correspond the norm of the gaps while faults curves correspond to the norms of the impacts.",
  "diagnosis.overview.header.option.qoi": "Impact on performance indicators",
  "diagnosis.overview.header.option.qoi.description": ({
    translations,
  }: {
    translations: {
      FR: {
        LOSSES: string;
        UNEXPLAINED: string;
      };
      EN: {
        LOSSES: string;
        UNEXPLAINED: string;
      };
    };
  }) =>
    `This display offers a focused view on a specific indicator, along with associated internal and ${lowercase(
      translations.EN.UNEXPLAINED,
    )} ${lowercase(translations.EN.LOSSES)}. This view also allows for displaying other faults in the project and their impact on these indicators.`,
  "diagnosis.overview.header.option.title": "Display mode",
  "diagnosis.overview.legend.tooltip.confidence_area":
    "Area in which the symptom is deemed as not significant",
  "diagnosis.overview.legend.tooltip.internal_gap.global":
    "Norm of the gaps between the system and its expected behavior, on the basis of all observables",
  "diagnosis.overview.legend.tooltip.internal_losses":
    "Difference between the expected and measured values of the performance indicator",
  "diagnosis.overview.legend.tooltip.no_failure": "No fault has been diagnosed by the Metroscope",
  "diagnosis.overview.legend.tooltip.unexplained_gap.global":
    "Norm of the remaining gaps between the system and its expected behavior, taking into account the impact of confirmed faults",
  "diagnosis.overview.legend.tooltip.unexplained_gap.integrate_suspected":
    "Integrate the impact of unconfirmed faults",
  "diagnosis.overview.legend.tooltip.unexplained_losses":
    "Remaining difference between the expected and measured values of the performance indicator, taking into account the impact of faults",
  "diagnosis.symptoms.legend.tooltip.quantity_gap":
    "Difference between the measured and expected values of the quantity",
  "diagnosis.symptoms.legend.tooltip.quantity_unexplained_gap":
    "Remaining difference between the measured and expected values of the quantity, taking into account the impact of faults",
  "diagnosis.title.observables.order.alphabetical": "A-Z",
  "diagnosis.title.observables.order.alphabetical.description": "Alphabetical order",
  "diagnosis.title.observables.order.decreasing.impact": "Decreasing",
  "diagnosis.title.observables.order.decreasing.impact.description":
    "Observables are ordered by decreasing visible bar size.\nFor example, if only remaining symptoms are displayed using the legend, quantities displayed first are the ones whose symptoms have the poorest explanation.",
  "doNotbreakBetweenGroups.text": "Display inter-cycles",
  "errors.backend_error": "Unexpected server response. Reload the page or retry.",
  "errors.conflict": "An element with the same ID already exists",
  "errors.field_cannot_be_empty": "This field must be filled out",
  "errors.field_must_be_less_than_1_year": "The total interval must be less than 1 year.",
  "errors.field_must_be_number": "This field must contain a number",
  "errors.field_must_be_strictly_positive_number":
    "This field must contain a strictly positive number",
  "errors.forbidden":
    "You do not have access to some of the requested resources, please contact your administrator.",
  "errors.network": "No connection, are you online?",
  "errors.not_found": "Resource not found",
  "errors.profile_not_found":
    "You’re successfully connected, but you don’t yet have access rights to this application. Please reach out to support@metroscope.tech so we can grant you the proper permissions.",
  "errors.should_be_positive_integer": "It should be a positive integer",
  "errors.tmp_backend_error": "A temporary error occurred. Please retry later.",
  "errors.unauthorized": "You are disconnected",
  "errors.unexpected_error": "An error occurred. Reload the page or retry.",
  "failureTooltip.equipments": "Equipment",
  "failureTooltip.notInSetup": "This fault is not in the setup of the selected analysis",
  "failureTooltip.suspectedElements": "Suspected elements",
  "failureTooltip.system": "System",
  "fallback.reconnect": "Reconnect",
  "fallback.retry": "Retry",
  "fault.chart.evolution.tooltip.status.confirmed": "Confirmed",
  "fault.chart.evolution.tooltip.status.rejected": "False detection",
  "fault.chart.evolution.tooltip.status.suspected": "Suspected",
  "fault.chart.evolution.tooltip.status.to_be_analysed": "To be analysed",
  "flags_legend.allData_activeAnalysis": "Data point used for analysis",
  "flags_legend.allData_activeAnalysis.description": "Data point used for analysis",
  "flags_legend.fault_flag.multiple_statuses": "Several fault statuses detected on the period",
  "flags_legend.intercycles": "Inter-cycle",
  "flags_legend.intercycles.description": "Inter-cycle: period between two cycles",
  "flags_legend.noDataIntervals": "Data not available",
  "flags_legend.noDataIntervals.description": "Data not available",
  "flags_legend.out_of_fault_event_period": "Outside of the event",
  "flags_legend.out_of_fault_event_period.description": "Outside of the period of the event",
  "flags_legend.published_analysis_in_reporting": "Included in reporting",
  "flags_legend.published_analysis_in_reporting.description": "Included in reporting",
  "flags_legend.published_analysis_not_in_reporting": "Published and not included in reporting",
  "flags_legend.published_analysis_not_in_reporting.description":
    "Published and not included in reporting",
  "flags_legend.quantity_flag.invalid_value": "Invalidated value",
  "flags_legend.quantity_flag.invalid_value.description":
    "Invalidated value: additional information on hover over the point",
  "flags_legend.quantity_flag.invalid_value.median":
    "At least one invalidated value on the period (not included in the aggregation)",
  "flags_legend.quantity_flag.missing_value": "No value",
  "flags_legend.quantity_flag.missing_value.description":
    "Missing value: additional information on hover over the point",
  "flags_legend.quantity_flag.missing_value_and_invalid_value": "Missing and invalidated value",
  "flags_legend.quantity_flag.missing_value_and_invalid_value.description":
    "Missing and invalidated value: additional information on hover over the point",
  "flags_legend.sigma": "Confidence area",
  "flags_legend.sigma.amplitude.description":
    "Area outside of which a measurement is deemed symptomatic",
  "flags_legend.sigma.gap.description": "Area outside of which a gap is deemed a symptom",
  "flags_legend.simulation_diagnosis_error": "Simulation / diagnosis failed",
  "flags_legend.simulation_diagnosis_error.description":
    "Simulation or diagnosis failed: hover on the marker to see the specific reason",
  "flags_legend.synchronizing": "Synchronizing",
  "flags_legend.unavailable_simulation": "Unavailable simulation",
  "flags_legend.unavailable_simulation.description":
    "Simulation not possible: additional information on hover over the point",
  "flags_legend.uncomplete_simulation_diagnosis": "Simulation / Diagnosis not carried out",
  "flags_legend.uncomplete_simulation_diagnosis.description":
    "Simulation / Diagnosis in progress or not started yet",
  "form.no_value": "Empty value",
  "hamburger.activity.title": "Activity feed",
  "hamburger.group_title.user_name": ({ name }: { name: string }) => `${name}`,
  "hamburger.help.title": "Help",
  "hamburger.legals.title": "Legals",
  "hamburger.option.datasources": "Data sources",
  "hamburger.option.jupyterHub": "JupyterHub",
  "hamburger.option.permalink": "Copy link to current page",
  "hamburger.option.permalink.toast.success": "Page link copied to clipboard!",
  "hamburger.option.print": "Export page to .png",
  "hamburger.option.print.toast.error": "Export failed",
  "hamburger.option.print.toast.in_progress": "Export in progress",
  "hamburger.option.print.toast.success": "Export done",
  "hamburger.setup_mode": "Setup mode",
  "hamburger.setup_mode.tooltip":
    "Shows setup user interface elements according to your permissions on each project.",
  "hamburger.user.logout": "Log out",
  "hamburger.user.profile": "Profile",
  "hamburger.user.projects_count": ({ count }: { count: number }) =>
    count === 1 ? `${count} project` : `${count} projects`,
  "hamburger.user_guide.tooltip": "User guide",
  "header.help_center.documentation": "Documentation",
  "header.help_center.releases.latest_badge": "Latest",
  "header.help_center.releases.new_badge": "New",
  "header.help_center.releases.title": "What's new",
  "header.help_center.releases.unread_aria": "New release note available",
  "header.help_center.support.copy_aria": "Copy support e-mail",
  "header.help_center.support.copy_error": "Unable to copy support e-mail",
  "header.help_center.support.copy_success": "Support e-mail copied to clipboard",
  "header.help_center.support.message":
    "Need additional help or want to report an issue? Send an e-mail to:",
  "header.help_center.title": "Help center",
  "manualFailure.modal.form.add.line": "New non-modeled fault",
  "manualFailure.modal.form.everyAnalyses": ({ analysesCount }: { analysesCount: number }) =>
    analysesCount === 0 ? "None" : analysesCount === 1 ? "1 analysis" : `${analysesCount} analyses`,
  "manualFailure.modal.form.header.linkedAnalyses": "Related analyses",
  "manualFailure.modal.form.header.name": "Name",
  "manualFailure.modal.form.header.qoi": ({ name, unit }: { name: string; unit: string | null }) =>
    `Impact on ${name}` + (unit === null ? "" : ` (${unit})`),
  "manualFailure.modal.form.header.reparability": "Outage time for repair",
  "manualFailure.modal.submit.success": (impactedAnalyses: number) =>
    `Non-modeled faults have been edited on ${
      impactedAnalyses === 1 ? "1 analysis" : impactedAnalyses + " analyses"
    }.`,
  "manualFailure.modal.subtitle": ({
    analysesCount,
    lowerBoundary,
    upperBoundary,
  }: {
    analysesCount: number;
    lowerBoundary: string;
    upperBoundary: string;
  }) => {
    if (analysesCount === 1) {
      return `Focus on selected period (1 analysis on ${lowerBoundary})`;
    }
    return `Focus on selected period (${analysesCount} analyses from ${lowerBoundary} to ${upperBoundary})`;
  },
  "manualFailure.modal.title": "Management of non-modelled faults",
  "measures.chart.analysis.modal.activate.description":
    "Do you want to create an analysis based on this point?",
  "measures.chart.analysis.modal.activate.success": "The analysis has been added.",
  "measures.chart.analysis.modal.activate.warning.description":
    "Data at this date do not respect analysis filters.",
  "measures.chart.analysis.modal.deactivate.description": () => (
    <>
      Analysis deletion triggers the deletion of simulation and diagnosis results. Data remains
      stored in data source.
      <br />
      <br />
      Do you confirm the deletion of this analysis?
    </>
  ),
  "measures.chart.analysis.modal.deactivate.success": "The analysis has been deleted.",
  "measures.chart.analysis.modal.toggle.analysis": ({ isActive }: { isActive: boolean }) =>
    isActive ? <>Delete</> : <>Analyze</>,
  "measures.chart.analysis.modal.toggle.analysis.title": ({ active }: { active: boolean }) =>
    active ? <>Delete analysis</> : <>Analyze data point</>,
  "measures.chart.legend.analysisFilter.description": "Excluded by analysis filters",
  "measures.chart.legend.analysisFilter.title": "Excluded by analysis filters",
  "measures.chart.legend.invalid.inherited.name": "Invalidated due to a parent",
  "measures.chart.legend.invalid_auto.name": "Automatically invalidated",
  "measures.chart.legend.invalid_manual.name": "Manually invalidated",
  "measures.chart.legend.modal.invalid_auto.description": () => (
    <>
      Measure invalidated due to :
      <br />• non-compliance with metrological criteria,
      <br />• or cross-filtered,
      <br />• or due to an invalid measurement on a parent of the quantity
    </>
  ),
  "measures.chart.legend.modal.invalid_manual.description":
    "Measurement invalidated by user action",
  "measures.chart.legend.modal.valid_auto.description":
    "Measurement complying with various metrological criteria",
  "measures.chart.legend.modal.valid_manual.description":
    "Measurement automatically invalidated and validated by user action",
  "measures.chart.legend.no_validation.name": "Inactive",
  "measures.chart.legend.std.max.description": "Maximum standard deviation for raw value",
  "measures.chart.legend.std.max.title": "Maximum standard deviation",
  "measures.chart.legend.valid_auto.name": "Automatically validated",
  "measures.chart.legend.valid_manual.name": "Manually validated",
  "measures.chart.legend.value.minmax.description": "Thresholds for selected quantity",
  "measures.chart.legend.value.minmax.title": "Quantity thresholds",
  "measures.chart.not.used.analysis": "Data point not used",
  "measures.chart.not.visible.analysis": "Data point not visible - zoom in",
  "measures.chart.selected.analyses": ({
    showInactiveAnalyses,
    snapshotCount,
    activeAnalysisCount,
  }: {
    showInactiveAnalyses: boolean;
    snapshotCount: number;
    activeAnalysisCount: number;
  }) => {
    if (showInactiveAnalyses) {
      return `${snapshotCount} data point${snapshotCount > 1 ? "s" : ""} (${activeAnalysisCount} ${
        activeAnalysisCount > 1 ? "analyses" : "analysis"
      })`;
    } else {
      return `${activeAnalysisCount} ${
        activeAnalysisCount > 1 ? "analyses" : "analysis"
      } (${snapshotCount} data point${snapshotCount > 1 ? "s" : ""})`;
    }
  },
  "measures.chart.selected.analyses.activate.button.disabled.tooltip":
    "No data points available to create analyses for the selected period.",
  "measures.chart.selected.analyses.activate.button.name": "Create analyses",
  "measures.chart.selected.analyses.activate.deactivate.button": "Create / Delete analyses",
  "measures.chart.selected.analyses.deactivate.button.name": "Delete analyses",
  "measures.chart.selected.analyses.deactivate.button.name.disabled.no_active_analysis":
    "No analysis to delete on the selected period",
  "measures.chart.selected.analyses.modal.activate.buttons.submit": "Create",
  "measures.chart.selected.analyses.modal.activate.errors.frequency_too_small":
    "The frequency must be higher than the current frequency of analysis.",
  "measures.chart.selected.analyses.modal.activate.errors.frequency_too_small_for_selected_period":
    "The duration between 2 points must be less than the duration of the selected period.",
  "measures.chart.selected.analyses.modal.activate.errors.frequency_too_small_to_create_analyses":
    "The frequency is not large enough to create new analyses.",
  "measures.chart.selected.analyses.modal.activate.form.extra_data_points.checkbox.label": ({
    filtersLink,
  }: {
    filtersLink: React.ReactNode;
  }) => <>Authorize the usage of data points not respecting the {filtersLink}</>,
  "measures.chart.selected.analyses.modal.activate.form.extra_data_points.label": (
    points: number,
  ) => {
    const pointsText = points > 1 ? `${points} extra data points` : "1 extra data point";

    return `Check the box below to use ${pointsText}, outside of the analysis filters, for creating analysis.`;
  },
  "measures.chart.selected.analyses.modal.activate.form.labels.days": "Days",
  "measures.chart.selected.analyses.modal.activate.form.labels.hours": "Hours",
  "measures.chart.selected.analyses.modal.activate.form.labels.minutes": "Minutes",
  "measures.chart.selected.analyses.modal.activate.information.indicate_interval":
    "Please indicate the target frequency of analysis:",
  "measures.chart.selected.analyses.modal.activate.labels.analyses_to_create":
    "Analyses to create:",
  "measures.chart.selected.analyses.modal.activate.labels.target_frequency.information":
    "To select the analyses to be created, the selected period will be split in regular intervals based on the target analysis frequency. For each interval without an existing analysis, an analysis will be created if possible.",
  "measures.chart.selected.analyses.modal.activate.labels.target_frequency.title":
    "Target analysis frequency",
  "measures.chart.selected.analyses.modal.activate.title": "Create analyses",
  "measures.chart.selected.analyses.modal.checkbox.filters.link": "analysis filters",
  "measures.chart.selected.analyses.modal.checkbox.label": ({
    filtersLink,
    invalidFiltersCount,
  }: {
    invalidFiltersCount: number;
    filtersLink: React.ReactNode;
  }) => (
    <>
      Allow the use of points that do not respect the {filtersLink} ({invalidFiltersCount} point
      {invalidFiltersCount > 1 ? "s" : ""})
    </>
  ),
  "measures.chart.selected.analyses.modal.deactivate.form.errors.erase_count": ({
    count,
  }: {
    count: number;
  }) => `The amount of analyses to delete cannot pass ${count}.`,
  "measures.chart.selected.analyses.modal.deactivate.form.labels.erase_count":
    "Number of analyses to be deleted",
  "measures.chart.selected.analyses.modal.deactivate.information.analyses": "Analyses:",
  "measures.chart.selected.analyses.modal.deactivate.information.current_analysis_frequency":
    "Current analysis frequency:",
  "measures.chart.selected.analyses.modal.deactivate.information.data_points": "among data points:",
  "measures.chart.selected.analyses.modal.deactivate.information.erase_count":
    "Please indicate how many analyses you wish to delete:",
  "measures.chart.selected.analyses.modal.deactivate.information.remaining_analyses":
    "Remaining analyses:",
  "measures.chart.selected.analyses.modal.deactivate.information.resulting_frequency":
    "Resulting analysis frequency:",
  "measures.chart.selected.analyses.modal.deactivate.information.selected_period":
    "Selected period:",
  "measures.chart.selected.analyses.modal.deactivate.title": "Delete analyses",
  "measures.chart.selected.analyses.select.range.success": (analysesDifferenceCount: number) => {
    if (analysesDifferenceCount > 0) {
      return analysesDifferenceCount > 1
        ? analysesDifferenceCount + " analyses have been created"
        : "1 analysis has been created";
    }
    return analysesDifferenceCount < -1
      ? `${Math.abs(analysesDifferenceCount)} analyses have been deleted`
      : "1 analysis has been deleted";
  },
  "measures.chart.selected.analysis.activated.date": "Data point used for an analysis",
  "measures.chart.selected.analysis.deactivated.date": "No analysis at this date",
  "measures.chart.title": "Data",
  "measures.chart.title.switch": "All points",
  "measures.chart.toggle.byStd": "Standard deviation",
  "measures.chart.toggle.byStd.description":
    "The standard deviation is a measure of the dispersion of the raw data within the sample used to calculate the point (e.g. 20-minute period for 1 point every 20 minutes). The standard deviation is only available for certain quantities, when imported with the data, and is not available for calculated quantities.",
  "measures.chart.toggle.byValue": "Value",
  "measures.chart.toggle.byValue.description":
    "Evolution of measured or calculated values of the selected quantity(ies).\nThe value corresponds to an aggregation of the raw data on the sample which led to the calculation of the point (e.g. period of 20 minutes for 1 point every 20 minutes).",
  "measures.chart.tooltip.button.invalidate": "Invalidate",
  "measures.chart.tooltip.button.validate": "Validate",
  "measures.chart.tooltip.description.invalid": "Invalidated value",
  "measures.chart.tooltip.description.invalid_auto": "Invalidated due to its own value",
  "measures.chart.tooltip.description.invalid_auto_cross_filter": (quantityName: string) =>
    `Invalidated by a cross filter with quantity ${quantityName}`,
  "measures.chart.tooltip.description.invalid_herited": "Invalidated due to an invalidated parent",
  "measures.chart.tooltip.description.invalid_manual": "Manually invalidated",
  "measures.chart.tooltip.description.missingValue": "Missing value",
  "measures.chart.tooltip.description.noModelOutput": "Non-calculable gap",
  "measures.chart.tooltip.description.valid_auto": "Validated automatically",
  "measures.chart.tooltip.description.valid_manual": "Validated manually",
  "measures.chart.tooltip.description.valid_partial_inputs": (
    inputCount: number,
    missingSmartArgsCount: number,
  ) =>
    `Missing or invalid values were ignored (${missingSmartArgsCount}/${inputCount}) in the formula computation`,
  "measures.chart.tooltip.quantity_field.label": "Measured value",
  "measures.chart.tooltip.selectedPoints": ({ count }: { count: number }) =>
    count === 1 ? `${count} selected point` : `${count} selected points`,
  "measures.chart.tooltip.std": "Standard deviation",
  "measures.empty.title": "Data page is empty",
  "measuresSymptoms.blankstate.no_quantities_found": "No quantity found",
  "measuresSymptoms.collapse.noGroup": "No system",
  "measuresSymptoms.collapse.title": "quantities",
  "measuresSymptoms.quantitiy.quantityIdentityCard.deleteFormulaAction": "Delete",
  "measuresSymptoms.quantitiy.quantityIdentityCard.deleteFormulaAction.disabled.locked":
    "Cannot delete because the formula is locked",
  "measuresSymptoms.quantitiy.quantityIdentityCard.deleteFormulaAction.disabled.usedInSetup": (
    setupName: string,
  ) =>
    `Cannot delete because the quantity (or a quantity depending on it) is used as an input or output of the model in the setup ${setupName}`,
  "measuresSymptoms.quantitiy.quantityIdentityCard.editFormulaAction": "Edit",
  "measuresSymptoms.quantitiy.quantityIdentityCard.equipments": "Equipment:",
  "measuresSymptoms.quantitiy.quantityIdentityCard.formule": "Formula:",
  "measuresSymptoms.quantitiy.quantityIdentityCard.lockedFormula": "Locked",
  "measuresSymptoms.quantitiy.quantityIdentityCard.offset": "Offset",
  "measuresSymptoms.quantitiy.quantityIdentityCard.process": ({ order }: { order: number }) =>
    `Process indicator (order ${order})`,
  "measuresSymptoms.quantitiy.quantityIdentityCard.qoi": ({ order }: { order: number }) =>
    `Performance indicator (order ${order})`,
  "measuresSymptoms.quantitiy.quantityIdentityCard.role.observable": "Observable",
  "measuresSymptoms.quantitiy.quantityIdentityCard.role.setupName": (setupName: ReactNode) => (
    <>
      in the setup <strong>{setupName}</strong>
    </>
  ),
  "measuresSymptoms.quantitiy.quantityIdentityCard.role.simulationInput": "Model input",
  "measuresSymptoms.quantitiy.quantityIdentityCard.role.simulationOutput": "Model output",
  "measuresSymptoms.quantitiy.quantityIdentityCard.roles": `Roles:`,
  "measuresSymptoms.quantitiy.quantityIdentityCard.sensorTag": "Sensor tag:",
  "measuresSymptoms.quantitiy.quantityIdentityCard.sigma": "Sigma",
  "measuresSymptoms.quantitiy.quantityIdentityCard.system": "System:",
  "measuresSymptoms.quantitiy.quantityIdentityCard.type": "Type:",
  "measuresSymptoms.quantitiy.quantityIdentityCard.unit": "Unit:",
  "measuresSymptoms.quantityIdentityCard.type.calculated_value": "Calculated",
  "measuresSymptoms.quantityIdentityCard.type.measure": "Measured",
  "measuresSymptoms.sectionType.noGrouping": "no grouping",
  "measuresSymptoms.sectionType.system": "by System",
  "measuresSymptoms.sectionType.type": "by Type",
  "measuresSymptoms.sectionType.unit": "by Unit",
  "measuresSymptoms.toast.analyses_invalidation": ({ count }: { count: number }) =>
    count === 1 ? `${count} measurement invalidated.` : `${count} measurements invalidated.`,
  "measuresSymptoms.toast.analyses_validation": ({ count }: { count: number }) =>
    count === 1 ? `${count} measurement validated.` : `${count} measurements validated.`,
  "measures_symptoms.axis_grouped_by_unit": "Group axis by unit",
  "millions.sampling.description": ({
    samplingLevel,
    humanDuration,
  }: {
    samplingLevel: number;
    humanDuration: string;
  }) => `Sampling of data displayed: one data point every ${humanDuration} (${samplingLevel}%)`,
  "modal.delete": "Delete",
  "modal.duplicate": "Duplicate",
  "modalForm.button.name.add": "Add",
  "modalForm.button.name.create": "Create",
  "modalForm.button.name.delete": "Delete",
  "modalForm.button.name.duplicate": "Duplicate",
  "modalForm.button.name.edit": "Edit",
  "modalForm.button.name.import": "Import",
  "modalForm.button.name.modify": "Edit",
  "modalForm.button.name.pause": "Pause",
  "modalForm.button.name.remove": "Remove",
  "modalForm.button.name.save": "Save",
  "modalForm.button.name.update": "Edit",
  "period.selector.1m.long": "Last month",
  "period.selector.1y": "1Y",
  "period.selector.1y.long": "Last year",
  "period.selector.2m.long": "Last 2 months",
  "period.selector.2y.long": "Last 2 years",
  "period.selector.30d.long": "Last 30 days",
  "period.selector.3m": "3M",
  "period.selector.3m.long": "Last 3 months",
  "period.selector.3w": "3W",
  "period.selector.3w.long": "Last 3 weeks",
  "period.selector.6m.long": "Last 6 months",
  "period.selector.7d.long": "Last 7 days",
  "period.selector.all": "All",
  "period.selector.all.long": "Whole period",
  "period.selector.custom": "Custom",
  "period.selector.default": "Default",
  "period.selector.description": () => (
    <>
      These options facilitate navigation within the graph, via predefined time windows:
      <br /> - Current cycle: last production cycle of the installation
      <br /> - 3W: Last 3 weeks
      <br /> - 3M: Last 3 months
      <br /> - 1A: Last year
      <br /> - All: full period
    </>
  ),
  "period.selector.last_group": "Current cycle",
  "period.selector.last_two_groups": "Last 2 cycles",
  "project.analysis.toggle.display_invalid_points.text": "Display invalidated data points",
  "project.analysisActive.count_exceeded": ({
    count,
    countLimit,
  }: {
    count: number;
    countLimit: number;
  }) => (
    <>
      <strong>Limit reached:</strong> project has too many analyses: {formatNumber(count)}/
      {formatNumber(countLimit)} max. Delete some analyses.
    </>
  ),
  "project.button.copying_project_analyses": "Copy selection from another project",
  "project.button.navbar.actions.launch_selection_of_analyses": "Launch analysis creation",
  "project.chart.analysis.outside_event_period": "(outside the event range)",
  "project.chart.legend.fault_event.switch.display_all_faults.label": "Display all faults",
  "project.charts.action.menu.modal.analysis_export.title": "Export analysis data",
  "project.charts.confirm_analysis_publish_modal.subtitle": ({
    count,
    action,
  }: {
    count: number;
    action: "publish" | "unpublish";
  }): string =>
    count === 1
      ? `${
          action === "publish"
            ? "You are about to publish the following analysis:"
            : "You are about to unpublish the following analysis:"
        }`
      : `${
          action === "publish"
            ? "You are about to publish the following analyses:"
            : "You are about to unpublish the following analyses:"
        }`,
  "project.charts.confirm_analysis_publish_modal.title": ({
    action,
  }: {
    action: "publish" | "unpublish";
  }): string => `${action === "publish" ? "Publish analyses" : "Unpublish analyses"}`,
  "project.charts.modal.form.analysis_export.button.name": "Export",
  "project.charts.modal.form.analysis_export.timezone": (tz: string) =>
    `All dates will be presented in project time zone (${tz}).`,
  "project.charts.publish_analysis.success": ({
    count,
    action,
  }: {
    count: number;
    action: "publish" | "unpublish";
  }): string =>
    count === 1
      ? `1 analysis has been ${action === "publish" ? "published" : "unpublished"}.`
      : `${count} analyses have been ${action === "publish" ? "published" : "unpublished"}.`,
  "project.charts.title.no_data_points_selected": ({
    showInactiveAnalyses,
  }: {
    showInactiveAnalyses: boolean;
  }): string => (showInactiveAnalyses ? "No data point selected" : "No analysis selected"),
  "project.charts.title.selected_points": ({
    selectedPointsText,
  }: {
    selectedPointsText: React.ReactNode;
  }) => <>Selection: {selectedPointsText}</>,
  "project.charts.title.selected_points.one.details": ({
    showInactiveAnalyses,
  }: {
    showInactiveAnalyses: boolean;
  }): string => {
    if (showInactiveAnalyses) return "1 data point";
    else return "1 analysis";
  },
  "project.charts.tooltip.description.manual_failure": "Non-modeled fault",
  "project.comment.subtitle.deleted_analysis": "Analysis deleted",
  "project.dashboard.actions.create": (translations: CustomTranslations) =>
    `Create a ${translations.EN.DASHBOARD.toLowerCase()}`,
  "project.dashboard.actions.create.error": (translations: CustomTranslations) =>
    `An error occurred while creating the ${translations.EN.DASHBOARD.toLowerCase()}`,
  "project.dashboard.actions.create.success": (translations: CustomTranslations) =>
    `The ${translations.EN.DASHBOARD.toLowerCase()} has been created`,
  "project.dashboard.editor.gui.no_system": "No system",
  "project.dashboard.editor.gui.section.annotation_settings.show_description":
    "Display description",
  "project.dashboard.editor.gui.section.annotation_settings.show_label": "Display label",
  "project.dashboard.editor.priority_events.title": "Priority event",
  "project.dashboard.status.published": `Shared`,
  "project.dashboard.status.unpublished": `Private`,
  "project.dashboards.empty.subtitle": (translations: CustomTranslations) =>
    `Create a ${translations.EN.DASHBOARD.toLowerCase()} to display your data`,
  "project.dashboards.empty.title": (translations: CustomTranslations) =>
    `No ${translations.EN.DASHBOARD.toLowerCase()} available`,
  "project.dashboards.import.button.text": (translations: CustomTranslations) =>
    `Import a ${translations.EN.DASHBOARD.toLowerCase()}`,
  "project.dashboards.navigation": (translations: CustomTranslations) => translations.EN.DASHBOARDS,
  "project.dashboards.table.column.author": "Author",
  "project.dashboards.table.column.published": "Status",
  "project.dashboards.table.search.placeholder": "Search",
  "project.diagnosis.action_menu.option.redirection_events_page.description":
    "through the creation of a manual event",
  "project.diagnosis.action_menu.option.redirection_events_page.title": "Create non-modelled fault",
  "project.diagnosis.symptom.histogram.tooltip.fault_type.gap": "Gap",
  "project.diagnosis.symptom.histogram.tooltip.fault_type.impact": "Impact",
  "project.display_filter.no_data.description": "Try modifying the filters or the selected period.",
  "project.display_filter.no_data.reset": "Reset filters to default",
  "project.display_filter.no_data.title":
    "No analysis matches the selected display filters for this period.",
  "project.display_filter.select.footer.title": "View filter settings",
  "project.display_filter.select.title": (count: number, on: number) =>
    `Display filters${count > 0 ? ` (${count}/${on})` : ""}`,
  "project.display_filter.select.ungrouped.title": ({
    filterName,
    selectedCount,
    totalCount,
  }: {
    filterName?: string;
    selectedCount: number;
    totalCount: number;
  }) => {
    const remainingCount = totalCount - selectedCount;
    return filterName === undefined
      ? "Other filters"
      : remainingCount > 0
        ? `Other filters: ${filterName}, +${remainingCount}`
        : `Other filters: ${filterName}`;
  },
  "project.display_filter.select.ungrouped_vanilla.title": "Filters",
  "project.display_filter.toggle.title": "Display filters",
  "project.display_settings_saved": "Color display settings have been edited",
  "project.document.title.dashboards": "Dashboards",
  "project.document.title.diagnosis": "Faults",
  "project.document.title.fault_events": "Events",
  "project.document.title.measures": "Data",
  "project.document.title.new_navigation.exploration": "Exploration",
  "project.document.title.new_navigation.summary": "Home",
  "project.document.title.settings": "Settings",
  "project.document.title.simulation": "Simulation",
  "project.document.title.summary": "Summary",
  "project.document.title.summary.legacy_badge": "Legacy",
  "project.document.title.symptoms": "Symptoms",
  "project.fault_event.live.widget.average_losses.link": "Access the graph",
  "project.filters.modal.import.toast.success": ({ count }: { count: number }) =>
    count === 1 ? `${count} filter has been defined.` : `${count} filters have been defined.`,
  "project.filters.table.definitions.column.max_std": "Max standard deviation",
  "project.filters.table.definitions.column.max_value": "Max",
  "project.filters.table.definitions.column.min_value": "Min",
  "project.filters.table.definitions.column.quantity": "Quantity",
  "project.filters.title": "Analysis filters",
  "project.header.actions.computations.computations_running": ({ count }: { count: number }) =>
    count === 1 ? `Ongoing computation (${count})` : `Ongoing computations (${count})`,
  "project.header.actions.computations.start.success": "Processes have been started.",
  "project.header.actions.computations.start_computations": ({ count }: { count: number }) =>
    count === 1 ? `Start computation (${count})` : `Start computations (${count})`,
  "project.header.actions.computations.stop.success": "Computations stopped.",
  "project.header.actions.computations.stop_computations": ({ count }: { count: number }) =>
    count === 1 ? "Stop computation" : "Stop computations",
  "project.header.dropdown.delete": "Delete project",
  "project.header.dropdown.duplicate": "Duplicate project",
  "project.header.dropdown.edit_project": "Edit project",
  "project.header.dropdown.promote_production": "Promote to production",
  "project.header.dropdown.relegate_to_research": "Change to research",
  "project.header.toast.promoted_production": "Project promoted to production",
  "project.header.toast.relegated_to_research": "Project changed to research",
  "project.measure.legend_right.active_setup.name": (setupName: string) =>
    `Active setup: ${setupName}`,
  "project.measures.title": "Data",
  "project.modal.analysis_export.option.format.csv_helium": ".csv - Helium version",
  "project.modal.analysis_export.option.format.csv_lithium": ".csv - Lithium version",
  "project.modal.analysis_export.radio_button.title.all_analyses": (nbActiveAnalyses: number) =>
    `All project analyses [${nbActiveAnalyses}]`,
  "project.modal.analysis_export.radio_button.title.selected_analyses": (
    nbSelectedAnalyses: number,
  ) => `Selected analys${nbSelectedAnalyses > 1 ? "es" : "is"} only [${nbSelectedAnalyses}]`,
  "project.modal.analysis_export.title.data_to_export": "Data to export",
  "project.modal.analysis_export.title.exported_analyses": "List of exported analyses:",
  "project.modal.copying_project_analyses.subtitle":
    "Analyses will be created on the basis of those existing in the selected project, linked to the same data source",
  "project.modal.subtitle.launch_selection_of_analyses":
    "Analyses will be created on the basis of the criteria entered",
  "project.modal.title.launch_selection_of_analyses": "Launch analysis creation",
  "project.modal_form.action_confirmation.description": (projectName: string) => (
    <>
      You are about to restart the analysis selection for the project <b>{projectName}</b>. Please
      write the project name below to confirm this action.
    </>
  ),
  "project.modal_form.additionnal_message.max_analyses_count": `Maximum number of analyses per project is ${MAX_ANALYSIS_COUNT}.`,
  "project.modal_form.button.activation_filters.with_filter": (filterCount: number) => (
    <>{filterCount > 1 ? "View filters" : "View filter"}</>
  ),
  "project.modal_form.button.activation_filters.without_filter": "Define filters",
  "project.modal_form.copying_project_analyses.additionnal_message.info": (nbAnalyses: number) =>
    `Number of project analyses: ${nbAnalyses}`,
  "project.modal_form.copying_project_analyses.ok_button_name": "Recopy selection",
  "project.modal_form.copying_project_analyses.select_label.project": "Project",
  "project.modal_form.date_range.input.date_end": "End date",
  "project.modal_form.date_range.input.date_start": "Start date",
  "project.modal_form.estimated_analyses.text": (nbAnalyses: number, nbActiveAnalyses: number) => (
    <>
      Maximum number of analyses estimated to: <b>{nbAnalyses}</b>
      {nbActiveAnalyses
        ? ` (${nbActiveAnalyses} analys${
            nbActiveAnalyses > 1 ? "es are" : "is is"
          } currently present on this project)`
        : ""}
    </>
  ),
  "project.modal_form.estimated_analyses.time_criteria.text":
    "on the basis of time criteria, before applying filters",
  "project.modal_form.launch_analysis_selection.input.end_date.error.cannot_be_empty":
    "End date date cannot be empty",
  "project.modal_form.launch_analysis_selection.input.end_date.error.date_should_be_greater_to_start_date":
    "Application end date must be later than application start date",
  "project.modal_form.launch_analysis_selection.input.end_date.error.date_should_be_inferior_or_equal_to_source":
    "The end date must be less than or equal to the end date of the source",
  "project.modal_form.launch_analysis_selection.input.start_date.error.cannot_be_empty":
    "Start date date cannot be empty",
  "project.modal_form.launch_analysis_selection.input.start_date.error.date_should_be_greater_or_equal_to_source":
    "The start date must be greater than or equal to the start date of the source",
  "project.modal_form.launch_analysis_selection.input.start_date.error.date_should_be_inferior_to_end_date":
    "Application start date must be earlier than application end date",
  "project.modal_form.ok_button_name.launch_analysis_selection": "Launch creation",
  "project.modal_form.ok_button_name.restart_analysis_selection": "Restart selection",
  "project.modal_form.restart_analysis_selection.additionnal_message.warningt.loss_of_action":
    "This action will result in the loss of all actions related to analyses (including manual creation and deletion, results, fault confirmation, etc.).",
  "project.modal_form.switch.switch.activate_live_stream":
    "Activate live analysis using these criteria",
  "project.modal_form.text.activation_filters.without_filter": "None",
  "project.modal_form.text.date_range":
    "Analyses will be created between the dates indicated. No analyses will be created outside this range.",
  "project.modal_form.text.time_criteria": "Time criteria",
  "project.modal_form.title.action_confirmation": "Action confirmation",
  "project.modal_form.title.activation_filters": "Filters at project level",
  "project.modal_form.title.date_range": "Date range",
  "project.modal_form.title.live_data_stream": "Live analysis",
  "project.modal_form.title.minimum_interval": "Average interval",
  "project.modal_form.title.overlay.activation_filters":
    "Set of analysis filters that must be met before an analysis can be created",
  "project.modal_form.title.overlay.date_range":
    "Restriction of the time range over which analyses will be created",
  "project.modal_form.title.overlay.live_data_stream":
    "The live data stream triggers the creation of analyses when new data is inserted in the source associated with the project",
  "project.modal_form.title.overlay.minimum_interval":
    "Average interval between two successive analyses",
  "project.not_found.title": "Project not found",
  "project.permalink.loaded": "Link loaded",
  "project.plant_state.block.priority_event.button_link": "Access all events",
  "project.plant_state.block.priority_event.no_fault_event": () => (
    <>
      There are no <strong>High</strong> or <strong>Medium</strong> priority events detected within
      the displayed period.
    </>
  ),
  "project.plant_state.block.priority_event.table.priority": "Priority",
  "project.plant_state.block.priority_event.title": "Priority events",
  "project.quantities.add.title": ({ newSensorCount }: { newSensorCount: number }) =>
    `Add unused sensor tags (${newSensorCount})`,
  "project.quantities.button.newQuantities": ({ newSensorCount }: { newSensorCount: number }) =>
    `Unused sensor tags (${newSensorCount})`,
  "project.quantities.delete.additionnal_message.sensor": () => (
    <>
      The associated data will no longer appear in the project, but will be retained in the data
      source.
    </>
  ),
  "project.quantities.delete.body": (quantityName: string) => (
    <>
      Do you wish to confirm the deletion of the quantity <b>{quantityName}</b>?
    </>
  ),
  "project.quantities.delete.forbidden": (quantityName: string, setupNames: string[]) => (
    <>
      The <b>{quantityName}</b> quantity cannot be deleted because it (or a quantity dependent on
      it) is used as an input or output in the model&apos;s parameters. is used as a model input or
      output in the {setupNames.length > 1 ? "setups" : "setup"}{" "}
      <b>{printListStrings(setupNames, "and")}</b>.
    </>
  ),
  "project.quantities.delete.formula.body.quantities": ({
    quantityName,
    impactedQuantitiesCount,
  }: {
    quantityName: string;
    impactedQuantitiesCount: number;
  }) => (
    <>
      This deletion will cause the deletion of{" "}
      <b>
        {impactedQuantitiesCount > 1 ? impactedQuantitiesCount + " quantities" : "the quantity"}
      </b>{" "}
      using <b>{quantityName}</b> in {impactedQuantitiesCount > 1 ? "their" : "its"} computation
      (directly or through intermediate quantities), and of associated values.
    </>
  ),
  "project.quantities.delete.formula.details.body": ({
    impactedCount,
  }: {
    impactedCount: number;
  }) => (
    <>
      Details of {impactedCount} {impactedCount > 1 ? " deleted quantities" : " deleted quantity"}:
    </>
  ),
  "project.quantities.delete.formula.details.label.level1": ({
    quantityName,
    dependencyCount,
  }: {
    quantityName: string;
    dependencyCount: number;
  }) => (
    <>
      {quantityName}{" "}
      {dependencyCount > 0 ? (
        <>and {dependencyCount > 1 ? "quantities" : "quantity"} using it:</>
      ) : (
        ""
      )}
    </>
  ),
  "project.quantities.delete.formula.details.label.level2": ({
    quantityName,
    dependencyCount,
  }: {
    quantityName: string;
    dependencyCount: number;
  }) => (
    <>
      {quantityName}{" "}
      {dependencyCount > 0 ? (
        <>
          (+ {dependencyCount} {dependencyCount > 1 ? "quantities" : "quantity"} using it)
        </>
      ) : (
        ""
      )}
    </>
  ),
  "project.quantities.delete.formula.nota": ({ quantityName }: { quantityName: string }) => (
    <>
      Note: for readability reasons, only dependent quantities of level 1 (direct) or 2 of{" "}
      <b>{quantityName}</b> are displayed
    </>
  ),
  "project.quantities.delete.title": "Delete quantity",
  "project.quantities.edit.body": (
    quantityName: string,
    isFormula: boolean,
    hasImpactOnMetrology: boolean,
  ) => {
    if (isFormula && !hasImpactOnMetrology) {
      return (
        <>
          Do you confirm the edition of formula of quantity <b>{quantityName}</b>?
        </>
      );
    } else if (isFormula && hasImpactOnMetrology) {
      return (
        <>
          Do you confirm the edition of formula and metrological criteria of quantity{" "}
          <b>{quantityName}</b>?
        </>
      );
    } else {
      return (
        <>
          Do you confirm the edition of metrological criteria of quantity <b>{quantityName}</b>?
        </>
      );
    }
  },
  "project.quantities.edit.details.impacted_quantities": "Details of impacted quantities",
  "project.quantities.edit.error": "The quantity could not be updated",
  "project.quantities.edit.formula.body.evaluation_relaunch_by_type": ({
    hasImpactOnFormula,
    hasImpactOnMetrology,
  }: {
    hasImpactOnFormula: boolean;
    hasImpactOnMetrology: boolean;
  }) => {
    if (hasImpactOnFormula && !hasImpactOnMetrology) {
      return (
        <>
          <b>The reset of formula evaluation</b> for this quantity
        </>
      );
    } else if (!hasImpactOnFormula && hasImpactOnMetrology) {
      return (
        <>
          <b>The reset of validity evaluation</b> for this quantity
        </>
      );
    } else {
      return (
        <>
          <b>The reset of formula and validity evaluation</b> for this quantity
        </>
      );
    }
  },
  "project.quantities.edit.formula.body.impacted_quantities": ({
    impactedQuantitiesCount,
  }: {
    impactedQuantitiesCount: number;
  }) => (
    <>
      <b>The reset of formula and validity evaluation</b> for the{" "}
      {impactedQuantitiesCount > 1 ? impactedQuantitiesCount + " quantities" : " quantity"}{" "}
      depending of it
    </>
  ),
  "project.quantities.edit.formula.details.label.level1": ({
    quantityName,
    dependencyCount,
  }: {
    quantityName: string;
    dependencyCount: number;
  }) => (
    <>
      {quantityName}{" "}
      {dependencyCount > 0 ? (
        <>and {dependencyCount > 1 ? "quantities" : "quantity"} using it:</>
      ) : (
        ""
      )}
    </>
  ),
  "project.quantities.edit.formula.details.label.level2": ({
    quantityName,
    dependencyCount,
  }: {
    quantityName: string;
    dependencyCount: number;
  }) => (
    <>
      {quantityName}{" "}
      {dependencyCount > 0 ? (
        <>
          (+ {dependencyCount} {dependencyCount > 1 ? "quantities" : "quantity"} using it)
        </>
      ) : (
        ""
      )}
    </>
  ),
  "project.quantities.edit.formula.download.details_impacted_quantities":
    "Details of impacted quantities (.json)",
  "project.quantities.edit.formula.nota": ({ quantityName }: { quantityName: string }) => (
    <>
      Note: for readability reasons, only dependent quantities of level 1 (direct) or 2 of{" "}
      <b>{quantityName}</b> are displayed
    </>
  ),
  "project.quantities.edit.impacts.body": "This edition will cause:",
  "project.quantities.edit.name.error.format":
    "Format error: name should begin by a letter and only be composed of alphanumeric characters and _",
  "project.quantities.edit.simulation.impacting.diagnostic": () => (
    <>
      <b>The reset of diagnosis computations</b> (at least one impacted quantity used as an
      observable).
    </>
  ),
  "project.quantities.edit.simulation.impacting.quantity": () => (
    <>
      <b>The reset of simulation and diagnosis computations</b> (at least one impacted quantity used
      as a model input)
    </>
  ),
  "project.quantities.edit.success": "The quantity has been edited",
  "project.quantities.formula.add": "New calculated quantity",
  "project.quantities.import.title": "Import quantities",
  "project.quantities.newQuantities.modal.description": ({ sourceId }: { sourceId: string }) => (
    <>
      Below sensors have been identified in data source <b>{sourceId}</b> and are not linked to a
      quantity. Select in the list sensors for which a quantity has to be created.
    </>
  ),
  "project.quantities.newQuantities.modal.table.column.name": "Name",
  "project.quantities.search.name_or_description": "Name or description",
  "project.quantity.toast.deleted.success": "The quantity has been deleted",
  "project.secondary_header.display_filters.title": "Display filters",
  "project.secondary_header.filters.title": "Analysis filters",
  "project.secondary_header.quantities.title": "Quantities",
  "project.secondary_header.settings.title": "Settings",
  "project.secondary_header.setup.setup_and_analysis_infos"({
    startDate,
    endDate,
    count,
  }: {
    startDate: string | null;
    endDate: string | null;
    count: number;
  }) {
    return (
      <>
        {startDate === null && endDate === null
          ? `Used for all dates`
          : typeof startDate === "string" &&
            typeof endDate === "string" &&
            `Used between ${startDate} and ${endDate}`}
        {typeof startDate === "string" && endDate === null && `Used from ${startDate}`}
        {startDate === null && typeof endDate === "string" && `Used until ${endDate}`} ({count}{" "}
        {count > 1 ? `analyses` : `analysis`})
      </>
    );
  },
  "project.setting.filter.modal.delete.additionnal_message.info": () => (
    <>
      This action has no impact on existing data. No analyses will be created or deleted, but points
      that don’t comply with filters will appear in a specific way. <br /> To replay the choice of
      analysis dates, use the <b>Reset choice of analysis dates</b> function in the project menu.
    </>
  ),
  "project.settings.add_new_setup.button": "New setup",
  "project.settings.add_setup.button": "Create setup",
  "project.settings.analyses.add_display_filters.modal.title": "Import display filters",
  "project.settings.analyses.add_filters": "Add",
  "project.settings.analyses.add_filters.invalid.file_type":
    "The format of the file is unexpected.",
  "project.settings.analyses.add_filters.modal.description": `Filters will be checked when new data is imported, in addition to the interval for creating analyses.`,
  "project.settings.analyses.add_filters.modal.info.description": () => (
    <>
      This action has no impact on existing data. No analyses will be created or deleted, but points
      that don’t comply with filters will appear in a specific way. <br /> To replay the choice of
      analysis dates, use the <b>Reset choice of analysis dates</b> function in the project menu.
    </>
  ),
  "project.settings.analyses.add_filters.modal.title": "Import analysis filters",
  "project.settings.analyses.delete_display_filters.modal.description":
    "Do you want to confirm the deletion of the filters?",
  "project.settings.analyses.delete_display_filters.modal.title": "Delete display filters",
  "project.settings.analyses.delete_filters.modal.description": "Do you confirm filter deletion?",
  "project.settings.analyses.delete_filters.modal.submit_button.label": "Delete",
  "project.settings.analyses.delete_filters.modal.title": "Remove analysis filters",
  "project.settings.analyses.description": "Average time between two analyses: ",
  "project.settings.analyses.edit_filters": "Edit",
  "project.settings.analyses.filters": ({ filterCount }: { filterCount: number }) => {
    let filterLabel = "None";
    if (filterCount > 0) {
      filterLabel = `${filterCount} filter${filterCount > 1 ? "s" : ""}`;
    }

    return `Analysis filters: ${filterLabel}`;
  },
  "project.settings.analyses.frequency_value": ({
    days,
    hours,
    minutes,
  }: {
    days: number;
    hours: number;
    minutes: number;
  }) => (
    <>
      {days > 0 && (days > 1 ? `${days} days` : `${days} day`)}
      {hours > 0 && (hours > 1 ? ` ${hours} hours` : ` ${hours} hour`)}
      {minutes > 0 && (minutes > 1 ? ` ${minutes} minutes` : ` ${minutes} minute`)}
    </>
  ),
  "project.settings.analyses.show_filters": "Show filters",
  "project.settings.analyses.subtitle": "Selection of analysis dates",
  "project.settings.analyses.subtitle.data_flow": "Live analysis",
  "project.settings.analyses.subtitle.data_flow.field.activation": "Status",
  "project.settings.analyses.subtitle.data_flow.field.interval": "Edit",
  "project.settings.analyses.subtitle.setup": "Setup",
  "project.settings.analyses.title": "Analyses",
  "project.settings.blank_state.add_setup.text":
    "To perform simulations and diagnoses, create your first setup",
  "project.settings.blank_state.add_setup.title": "No setup defined",
  "project.settings.computed_sensors_quantities_count": ({
    sensorCount,
    quantityCount,
  }: {
    sensorCount: number;
    quantityCount: number;
  }) =>
    quantityCount >= 1
      ? `${sensorCount} ${sensorCount > 1 ? `sensors` : `sensor`} and ${quantityCount} ${
          quantityCount > 1 ? `computed quantities ` : `computed quantity `
        } `
      : `${sensorCount} ${sensorCount > 1 ? `sensors ` : `sensor `}`,
  "project.settings.data.name": "Data source: ",
  "project.settings.data.title": "Data",
  "project.settings.data_definition.subtitle": "Data definition",
  "project.settings.data_definition.subtitle.field.quantity": "Access to page of quantities",
  "project.settings.data_flow.activated.modal.subtitle":
    "Analyses will be created for new data imported into the project source",
  "project.settings.data_flow.activated.modal.title": "Activate data flow",
  "project.settings.data_flow.disabled.modal.subtitle":
    "The import of new data to the data source will not trigger the creation of analyses",
  "project.settings.data_flow.disabled.modal.title": "Pause data flow",
  "project.settings.data_flow.status.activated": "Activated",
  "project.settings.data_flow.status.activated.link.text": "Pause",
  "project.settings.data_flow.status.disabled": "Not active",
  "project.settings.data_flow.status.disabled.link.text": "Activate",
  "project.settings.datasource.modal.title": "Edit data source",
  "project.settings.display.part_load_sensitive_kpis": "Display part-load-sensitive KPIs",
  "project.settings.display.part_load_sensitive_kpis.tooltip":
    "Controls the visibility of KPI values for the project including part-load events.",
  "project.settings.display.subtitle": "Display",
  "project.settings.display_filters.groups.default_badge": "Default",
  "project.settings.display_filters.name": ({
    filterCount,
    groupCount,
  }: {
    filterCount: number;
    groupCount: number;
  }) => {
    let filterLabel = "Aucun";
    if (filterCount > 0) {
      filterLabel = `${filterCount} filtre${filterCount > 1 ? "s" : ""}`;
    }
    return `Filtres d’affichage : ${filterLabel}, ${groupCount} groupe${groupCount > 1 ? "s" : ""}`;
  },
  "project.settings.display_filters.replace.modal.subtitle":
    "The new file will replace the current filters. Filters not present in the new file will be deleted.",
  "project.settings.display_filters.replace.modal.title": "Replace display filters",
  "project.settings.display_filters.tooltip":
    "Allow you to restrict the analyses displayed in graphs according to the values taken by a quantity.",
  "project.settings.filters.tooltip":
    "Allow you to define the conditions that data must meet in order for an analysis to be created.",
  "project.settings.link.text": "Edit",
  "project.settings.modal.data_flow.activated.additionnal_message.info.text":
    "These criteria can be edited later in the Settings page.",
  "project.settings.modal.data_flow.activated.criteria":
    "Analysis creation will be based on the following criteria",
  "project.settings.modal.data_flow.activated.criteria.activation_filter": (nbFilters: number) => (
    <>
      Filters : <b>{nbFilters > 0 ? `${nbFilters} filter${nbFilters > 1 ? "s" : ""}` : "None"}</b>
    </>
  ),
  "project.settings.modal.data_flow.activated.criteria.interval_minimal": (
    minutesBetweenActiveAnalysis: number,
    intervalMinimal: string,
  ) => (
    <>
      Average interval between two analyses:{" "}
      {minutesBetweenActiveAnalysis === 0 ? <b>None</b> : <b>{intervalMinimal}</b>}
    </>
  ),
  "project.settings.modal.data_flow.activated.ok_button_name": "Activate",
  "project.settings.modal.data_flow.activated.question.text":
    "Would you like to confirm the activation?",
  "project.settings.modal.data_flow.disabled.text": "Would you like to confirm the pausing?",
  "project.settings.none": "None",
  "project.settings.performance_indicators.max.additionnal_message":
    "You can add up to 5 performance indicators.",
  "project.settings.processIndicator.add.new": "Add a new process indicator",
  "project.settings.processIndicator.edit": "Edit",
  "project.settings.processIndicator.no.data":
    "No process indicators were defined for this project.",
  "project.settings.processIndicators.modal.title": ({ editMode }: { editMode: boolean }) =>
    `${editMode ? "Edit process indicators" : "List of process indicators"}`,
  "project.settings.processIndicators.name": ({
    processIndicatorsList,
  }: {
    processIndicatorsList: string;
  }) => `Process indicators: ${processIndicatorsList ? processIndicatorsList : "None"}`,
  "project.settings.process_indicators.max.additionnal_message": `You can add up to ${MAX_PROCESS_INDICATOR_COUNT} process indicator${MAX_PROCESS_INDICATOR_COUNT > 1 ? "s" : ""}.`,
  "project.settings.qoi.add.new": "Add a new performance indicator",
  "project.settings.qoi.edit": "Edit",
  "project.settings.qoi.modal.title": ({ editMode }: { editMode: boolean }) =>
    `${editMode ? "Edit performance indicators" : "List of performance indicators"}`,
  "project.settings.qoi.name": ({ qoiList }: { qoiList: string }) =>
    `Performance indicators: ${qoiList ? qoiList : "None"}`,
  "project.settings.qoi.no.data": "No performance indicators were defined for this project.",
  "project.settings.qoi.prompt": "None",
  "project.settings.qoi.role.power": "Power output",
  "project.settings.qoi.table.column.order": "Display order",
  "project.settings.qoi.table.column.quantity": "Quantity",
  "project.settings.qoi.table.column.referenceValue": "Reference Value",
  "project.settings.qoi.table.column.referenceValue.error": "Must be numerical",
  "project.settings.qoi.table.column.role": "Role",
  "project.settings.quantities.name": "Quantities: ",
  "project.settings.quantities.usage_icons.tooltip.dashboard": "Used in dashboards",
  "project.settings.quantities.usage_icons.tooltip.formula": "Used in formula computation",
  "project.settings.quantities.usage_icons.tooltip.setup": "Used in setup",
  "project.settings.quantity.side_panel.create.title": "Create a calculated quantity",
  "project.settings.quantity.side_panel.edit.title": "Quantity details",
  "project.settings.quantity.side_panel.edition.impacts_warning": "Impacts related to the changes",
  "project.settings.quantity.side_panel.section.definition_quantity.intermediate_calculation":
    "Intermediate calculation",
  "project.settings.quantity.side_panel.section.definition_quantity.intermediate_calculation_tooltip":
    "Hide this quantity by default outside quantity settings. Setup users can display it from quantity selectors.",
  "project.settings.quantity.side_panel.section.definition_quantity.title":
    "Definition of the quantity",
  "project.settings.quantity.side_panel.section.formula_input.duplicate_usage_period_error":
    "Another formula version already starts on this date.",
  "project.settings.quantity.side_panel.section.formula_input.fill_from_cycle": "Cycle",
  "project.settings.quantity.side_panel.section.formula_input.fill_from_cycle_empty":
    "No cycle available",
  "project.settings.quantity.side_panel.section.formula_input.fill_from_cycle_label":
    "Fill start date from cycle",
  "project.settings.quantity.side_panel.section.formula_input.fill_from_cycle_search_placeholder":
    "Search a cycle",
  "project.settings.quantity.side_panel.section.formula_input.fill_from_prefix": "Find via",
  "project.settings.quantity.side_panel.section.formula_input.fill_from_separator": "or",
  "project.settings.quantity.side_panel.section.formula_input.fill_from_setup": "Setup",
  "project.settings.quantity.side_panel.section.formula_input.fill_from_setup_empty":
    "No setup available",
  "project.settings.quantity.side_panel.section.formula_input.fill_from_setup_label":
    "Fill start date from setup",
  "project.settings.quantity.side_panel.section.formula_input.fill_from_setup_search_placeholder":
    "Search a setup",
  "project.settings.quantity.side_panel.section.formula_input.help_link": "Learn formula syntax",
  "project.settings.quantity.side_panel.section.formula_input.input_label": "Formula",
  "project.settings.quantity.side_panel.section.formula_input.lock_title": "Lock the formula",
  "project.settings.quantity.side_panel.section.formula_input.lock_tooltip":
    "Once locked, the formula can only be modified by users with a setup role and admins",
  "project.settings.quantity.side_panel.section.formula_input.locked_due_to_dependency":
    "This formula is automatically locked due to its use in a setup or a locked formula. It can only be modified by a user with a setup role or an admin",
  "project.settings.quantity.side_panel.section.formula_input.locked_warning":
    "This formula is manually locked and can only be modified by a user with a setup role or an admin",
  "project.settings.quantity.side_panel.section.formula_input.title": "Formula input",
  "project.settings.quantity.side_panel.section.formula_input.unnamed_cycle": "Unnamed cycle",
  "project.settings.quantity.side_panel.section.formula_input.usage_period": (
    from: string | null,
    to: string | null,
  ) => {
    if (from && !to) return `From ${from}`;
    else if (!from && to) return `Until ${to}`;
    else if (from && to) return `From ${from} to ${to}`;
    else return "";
  },
  "project.settings.quantity.side_panel.section.metrological_filters.title": "Metrological filters",
  "project.settings.quantity.side_panel.tab.formulae": "Formulas",
  "project.settings.quantity.side_panel.tab.formulae.add": "Add formula",
  "project.settings.quantity.side_panel.tab.general_information": "General information",
  "project.settings.setup.add_update_setup.modal.placeholder.name": "Name",
  "project.settings.setup.add_update_setup.modal.title.description": "Description",
  "project.settings.setup.add_update_setup.modal.title.effective_start": "Start date",
  "project.settings.setup.add_update_setup.modal.title.name": "Name",
  "project.settings.timezone": ({ timeZone }: { timeZone: string }) =>
    `Data display time zone : ${timeZone}`,
  "project.settings.timezone.modal.title": "Edit time zone",
  "project.setup.header.dropdown.delete_setup": "Delete setup",
  "project.setup.header.dropdown.duplicate_setup": "Duplicate setup",
  "project.setup.header.dropdown.edit_setup": "Edit setup",
  "project.setup.header.dropdown.export_setup": "Export setup",
  "project.symptom.chart_tooltip.expected": "Expected value",
  "project.symptom.chart_tooltip.measured": "Measured value",
  "project.symptoms.title": "Symptoms",
  "project.widget.average_losses.value": (losses: string) => `${capitalize(losses)}:`,
  "project.widget.productio_monitoring.tooltip.value": "Power output:",
  "project.widget.production_monitoring.no_active_analysis": "No analysis at this date",
  "project.widget.production_monitoring.title": "Production monitoring",
  "project.widget.production_monitoring.yAxis.max_value": (value: string) => `Max: ${value}`,
  "projects.actions.delete": "Delete",
  "projects.actions.delete.disabled.locked_formula": "Cannot delete because the formula is locked",
  "projects.actions.delete.disabled.side_panel_open":
    "Action disabled while the side panel is open",
  "projects.actions.duplicate": "Duplicate",
  "projects.actions.edit": "Edit",
  "projects.actions.edit.disabled.side_panel_open": "Action disabled while the side panel is open",
  "projects.actions.new_calculated_quantity.disabled.side_panel_open":
    "Action disabled while the side panel is open",
  "projects.actions.sensor_tag.disabled.side_panel_open":
    "Action disabled while the side panel is open",
  "projects.actions.view": "Details",
  "projects.badge.datalake": "Data source",
  "projects.badge.datalake.description": ({ sourceId }: { sourceId: string }) =>
    `Connected to data source: ${sourceId}`,
  "projects.blank_state.ask_administrator_for_project_access":
    "Ask your administrator to give you access to a project",
  "projects.blank_state.create_project": "Create a new project to perform your first diagnosis",
  "projects.blank_state.title": "Welcome to Metroscope!",
  "projects.favourite_card.title.confirmed_faults": "Confirmed faults",
  "projects.favourite_card.title.internal_losses": (translations: CustomTranslations): string =>
    `Internal ${lowercase(translations.EN.LOSSES)}`,
  "projects.favourite_card.title.power_produced": "Produced power",
  "projects.header.actions.cancel_selection": "Cancel",
  "projects.header.actions.edit_projects": "Edit projects",
  "projects.header.actions.update_projects": ({ count }: { count: number }) =>
    `Edit projects (${count})`,
  "projects.header.actions.update_projects.info": "Select projects in the table",
  "projects.header.actions.update_projects_disabled": "Edit projects",
  "projects.header.display_draft": "Show research projects",
  "projects.header.display_manual_failures": "Non-modeled faults",
  "projects.header.title": "Projects",
  "projects.modal.analysis_export.title.format": "Format",
  "projects.modal.create_missing_analyses_cac.title": "Create missing analyses",
  "projects.modal.create_or_edit.input.name.error.cannot_be_empty": "Project name cannot be empty",
  "projects.modal.create_or_edit.input.name.error.name_already_exists":
    "Project name already exists",
  "projects.modal.create_or_edit.input.name.error.too_long": "Name must not exceed 100 characters",
  "projects.modal.create_or_edit.input.publishedAnalysesLastDate": "Latest analysis published",
  "projects.modal.create_or_edit.input.site": "Site",
  "projects.modal.create_or_edit.input.type": (translations: CustomTranslations) =>
    capitalize(translations.EN.TYPE),
  "projects.modal.dataSource": "Datasource",
  "projects.modal.delete.are_you_sure": ({ name }: { name: string }) => (
    <>
      You’re about to definitely delete project <strong>{name}</strong>.
      <br />
      Please write down below the name of the project to confirm deletion.
    </>
  ),
  "projects.modal.delete.delete_project": "Delete project",
  "projects.modal.delete.input_placeholder": "Name of the project",
  "projects.modal.delete.prod.project.tooltip":
    "Deleting projects in production is not possible. Please put the project in research for deleting it",
  "projects.modal.delete.project_name": "Name of the project to delete",
  "projects.modal.dispatch_cac.title": "Dispatch CAC",
  "projects.modal.duplicate.duplicated_name": ({ name }: { name: string }) => `Copy of ${name}`,
  "projects.modal.duplicate.input.additional_message_info.deleted_comments":
    "If comments are attached to events, they will be deleted.",
  "projects.modal.duplicate.input.include_comments": "Include comments",
  "projects.modal.duplicate.input.include_dashboards": (translations: CustomTranslations) =>
    `Include ${translations.EN.DASHBOARDS.toLowerCase()} (private and shared)`,
  "projects.modal.duplicate.input.include_events": "Include events",
  "projects.modal.duplicate.input.include_user_project_roles": "Include project-level rights",
  "projects.modal.edit.input.dataSource.error": ({
    missingSensorTags,
  }: {
    missingSensorTags: string[];
  }) => (
    <>
      Selected data source is not compatible with this project, {missingSensorTags.length} sensor
      tags used in the project are missing. Please delete associated quantities in the project or
      add these sensor tags to the source.
      <br />
      <br />
      List of missing sensor tags:
      <br />
      <br />
      {missingSensorTags.join(", ")}
    </>
  ),
  "projects.modal.edit.input.dataSource.tooltip":
    "Data source cannot be changed on multiple projects",
  "projects.modal.edit.update_project.toast": ({
    projectName,
    name,
    description,
    timeZone,
  }: {
    projectName: string;
    name: boolean;
    description: boolean;
    timeZone: boolean;
  }) => {
    const properties: string[] = [];
    if (name) {
      properties.push("name");
    }
    if (description) {
      properties.push("description");
    }
    if (timeZone) {
      properties.push("time zone");
    }
    const propertiesList = listFormatConjunction(properties);

    return `${
      properties.length > 1
        ? `The properties ${propertiesList} have been edited`
        : `The property ${propertiesList} has been edited`
    } for the project ${projectName}`;
  },
  "projects.modal.edit.update_projects.toast": ({
    projectsCount,
    name,
    description,
    timeZone,
  }: {
    projectsCount: number;
    name: boolean;
    description: boolean;
    timeZone: boolean;
  }) => {
    const properties: string[] = [];
    if (name) {
      properties.push("name");
    }
    if (description) {
      properties.push("description");
    }
    if (timeZone) {
      properties.push("time zone");
    }
    const propertiesList = listFormatConjunction(properties);

    return `${
      properties.length > 1
        ? `The properties ${propertiesList} have been edited`
        : `The property ${propertiesList} has been edited`
    } for ${projectsCount} project${projectsCount > 1 ? "s" : ""}`;
  },
  "projects.modal.launch_cac.title": "Launch CAC",
  "projects.modal.multiple_value": "Multiple Values",
  "projects.modal.name": "Name",
  "projects.modal.reset_cac.title": "Reset CAC",
  "projects.modal.restart_analysis_selection.subtitle":
    "The current list of analyses will be deleted and the analysis process will be restarted, creating new analyses based on the established criteria",
  "projects.modal.restart_analysis_selection.title": "Restart analysis selection",
  "projects.modal.title.create_project": "Create project",
  "projects.modal.title.duplicate_project": "Duplicate project",
  "projects.modal.toast.create_project.text": ({ name }: { name: string }) =>
    `Project ${name} created.`,
  "projects.modal.toast.delete_project": ({ name }: { name: string }) => `Project ${name} deleted`,
  "projects.modal.toast.delete_project_in_progress": ({ name }: { name: string }) =>
    `Project ${name} deletion in progress, it may take a few minutes`,
  "projects.modal.update_project": "Edit project",
  "projects.modal.update_projects.title.dataSource": "Data source",
  "projects.modal.update_projects.title.description": "Description",
  "projects.modal.update_projects.title.name": "Name",
  "projects.modal.update_projects.title.timezone": "Time zone to display data",
  "projects.paragraphs.actions.create_project": "New project",
  "projects.paragraphs.favourites": "Favorites",
  "projects.paragraphs.projects": "Projects",
  "projects.paragraphs.projects.table.column.description": "Description",
  "projects.paragraphs.projects.table.column.lastAnalysisDate": "Last analysis",
  "projects.paragraphs.projects.table.column.loss": (translations: CustomTranslations): string =>
    `Internal ${lowercase(translations.EN.LOSSES)}`,
  "projects.paragraphs.projects.table.column.measured": "Produced power",
  "projects.paragraphs.projects.table.column.name": "Name",
  "projects.paragraphs.projects.table.column.nbConfirmedFaults": "Confirmed faults",
  "projects.paragraphs.projects.table.column.sourceId": "Data source",
  "projects.quantities.create.quantity.success.toast.title": "The quantity has been created",
  "projects.quantities.too_many": `Maximum number of quantities is ${MAX_QUANTITY_COUNT}.`,
  "projects.quantity.action.delete.disabled.has_impact_on_setups": (setupNames: string[]) => (
    <>
      Cannot delete because the quantity (or a quantity depending on it) is used as an input or
      output of the model in{" "}
      {setupNames.length > 1 ? (
        <>
          the setups{" "}
          <strong>
            {printListStrings(
              setupNames.map(name => name.toUpperCase()),
              "and",
            )}
          </strong>
        </>
      ) : (
        <>
          the setup <strong>{setupNames[0]?.toUpperCase()}</strong>
        </>
      )}
      .
    </>
  ),
  "projects.selectFilter.data_source": "Data source",
  "projects.selectFilter.site": "Site",
  "projects.selectFilter.type": (translations: CustomTranslations) =>
    capitalize(translations.EN.TYPE),
  "projects.settings.analyses_frequency.additionnal_message.info.criteria": () => (
    <>
      This action has no impact on existing data. No analyses will be created or deleted, but points
      that don’t comply with filters will appear in a specific way. <br /> To replay the choice of
      analysis dates, use the <b>Reset choice of analysis dates</b> function in the project menu.
    </>
  ),
  "projects.settings.analyses_frequency.modal.edit.input.days": "Days",
  "projects.settings.analyses_frequency.modal.edit.input.hours": "Hours",
  "projects.settings.analyses_frequency.modal.edit.input.minutes": "Minutes",
  "projects.settings.analyses_frequency.modal.submit.error.intervalToBig":
    "Selected interval must be less than 1 year",
  "projects.settings.analyses_frequency.modal.submit.toast.success.completed":
    "Analysis creation is over.",
  "projects.settings.analyses_frequency.modal.submit.toast.success.started":
    "Analysis creation has started.",
  "projects.settings.analyses_frequency.modal.switch.text": "Do not use interval",
  "projects.settings.analyses_frequency.modal.title": "Average interval",
  "projects.title": "Projects",
  "quantities.blankState.subtitle": "Import quantities list",
  "quantities.blankState.title": "No quantity defined",
  "quantities.crossFilter.blankState.button_label": "Import",
  "quantities.crossFilter.blankState.subtitle": "Import the list of quantities cross-filters",
  "quantities.crossFilter.blankState.title": "No cross-filter defined",
  "quantities.crossFilter.info": (quantityName: string, filters: string) =>
    `With ${quantityName} value ${filters}`,
  "quantities.crossFilter.modal.delete.title": "Delete filters",
  "quantities.crossFilter.modal.import.title": "Import filters",
  "quantities.crossFilter.table.evaluatedQuantity": "Source quantity",
  "quantities.crossFilter.table.invalidatedQuantity": "Invalidated quantity",
  "quantities.crossFilter.table.max": "Max",
  "quantities.crossFilter.table.maxStd": "Max standard deviation",
  "quantities.crossFilter.table.min": "Min",
  "quantities.cross_filter.modal.delete.additionnal_message.warning":
    "Following this action, measurement validities will be re-evaluated for the quantities on which filters apply. Simulation or diagnostic calculations can be restarted, depending on the change in validity and the role of the quantities.",
  "quantities.cross_filter.modale.delete.button_name": "Delete",
  "quantities.cross_filter.modale.delete.question": "Do you wish to confirm filter removal ?",
  "quantities.cross_filter.modale.import.info.metrological_filters_are_ignored":
    "Simple metrological filters are ignored for this import.",
  "quantities.header.cross_filters": "Cross filters",
  "quantities.header.quantities": "Quantities",
  "quantities.import.dry_run.message.analysis_filter_deleted": (quantityNames: string[]) =>
    `Analysis filter associated with ${
      quantityNames.length > 1 ? "the quantities " : "quantity "
    } ${quantityNames.join(", ")} will be deleted.`,
  "quantities.import.dry_run.message.cross_filter_deleted": (quantityNames: string[]) =>
    `Cross filter associated with ${
      quantityNames.length > 1 ? "the quantities " : "quantity "
    } ${quantityNames.join(", ")} will be deleted for.`,
  "quantities.import.dry_run.message.error.blocked_invalid_sensor_tag": (quantityName: string) =>
    `Quantity ${quantityName}: the sensorTag is invalid.`,
  "quantities.import.dry_run.message.error.blocked_link_setup": (
    quantityName: string,
    setupNames: string[],
  ) =>
    `Quantity ${quantityName}: the quantity must be present in the file because it is used in ${
      setupNames.length > 1 ? "setups" : "the setup"
    } ${setupNames.join(", ")}.`,
  "quantities.import.dry_run.message.formula_version": (
    quantityName: string,
    startsAt: string,
    oldValue: string,
    newValue: string,
  ) =>
    `Quantity ${quantityName}: formula version starting on ${startsAt} changed from "${oldValue}" to "${newValue}"`,
  "quantities.import.dry_run.message.formula_version_header": (quantityName: string) =>
    `Quantity ${quantityName}: formula versions will change`,
  "quantities.import.dry_run.message.formula_version_line_added": (
    startsAt: string,
    newValue: string,
  ) => `${startsAt} — will be added: ${newValue}`,
  "quantities.import.dry_run.message.formula_version_line_modified": (
    startsAt: string,
    oldValue: string,
    newValue: string,
  ) => `${startsAt} — will be modified: ${oldValue} → ${newValue}`,
  "quantities.import.dry_run.message.formula_version_line_removed": (
    startsAt: string,
    oldValue: string,
  ) => `${startsAt} — will be removed: ${oldValue}`,
  "quantities.import.dry_run.message.formula_versions_info":
    "Formula versions in the import may add, modify, or remove existing formulas.",
  "quantities.import.dry_run.message.formula_versions_summary": "Show formula version changes",
  "quantities.import.dry_run.message.performance_indicator_deleted": (quantityNames: string[]) =>
    `Performance indicator associated with ${
      quantityNames.length > 1 ? "the quantities " : "quantity "
    } ${quantityNames.join(", ")} will be deleted.`,
  "quantities.import.dry_run.message.process_indicator_deleted": (quantityNames: string[]) =>
    `Process indicator associated with ${
      quantityNames.length > 1 ? "the quantities " : "quantity "
    } ${quantityNames.join(", ")} will be deleted.`,
  "quantities.import.dry_run.message.reset_formula": () =>
    `Following this action, the calculation of formulas and metrology filters will be reset.`,
  "quantities.import.dry_run.message.reset_formula_simulation_diagnostic": () =>
    `Following this action, the calculation of formulas, metrology filters, simulations and diagnostics will be reinitialized, as at least one of these impacted quantities is used in a setup.`,
  "quantities.metrologyFilter": "Metrology filters:",
  "quantities.parse.error.cross_filter_by_pass":
    "The file only contains ‘IMPORT’ or simple metrological filters. It’s not usable for import.",
  "quantities.parse.error.data_invalidated_quantity_empty": `The 'invalidatedQuantity' field must not be empty.`,
  "quantities.parse.error.data_source_quantity_empty": `The 'source' field must not be empty.`,
  "quantities.parse.error.file_invalid_content":
    "Format error: expected columns are 'invalidatedQuantity', 'source', 'minimumValue', 'maximumValue' and 'maximumStd'.",
  "quantities.parse.error.only_import_and_metrology_filters":
    "The file contains only 'IMPORT' and metrological filters.",
  "quantitySelector.actionDisabledBecauseDrawerOpen":
    "Action disabled while the side panel is open",
  "quantitySelector.actions.create": "Create",
  "quantitySelector.actions.viewList": "See list",
  "quantitySelector.display_options.tooltip": "Quantity display options",
  "quantitySelector.filter.tooltip": "Filter quantities",
  "quantitySelector.group.alert.measures.current":
    "Unavailable in at least one analysis of the selected period.",
  "quantitySelector.group.alert.measures.inRange": ({
    alertCount,
    analysisCount,
    alertPercentage,
  }: {
    alertCount: number;
    analysisCount: number;
    alertPercentage: string;
  }) =>
    `At least one unavailable quantity in ${alertPercentage}% (${alertCount}/${analysisCount}) of analyses in the time frame.`,
  "quantitySelector.group.alert.symptoms.current":
    "At least one symptomatic quantity in the selected period.",
  "quantitySelector.group.alert.symptoms.inRange": ({
    alertCount,
    analysisCount,
    alertPercentage,
  }: {
    alertCount: number;
    analysisCount: number;
    alertPercentage: string;
  }) =>
    `At least one symptomatic quantity in ${alertPercentage}% (${alertCount}/${analysisCount}) of analyses in the time frame (when the quantity is available).`,
  "quantitySelector.quantity.alert.measures.current":
    "Unavailable in at least one analysis of the selected period.",
  "quantitySelector.quantity.alert.measures.inRange": ({
    alertCount,
    analysisCount,
    alertPercentage,
  }: {
    alertCount: number;
    analysisCount: number;
    alertPercentage: string;
  }) =>
    `Unavailable in ${alertPercentage}% (${alertCount}/${analysisCount}) of analyses in the time frame.`,
  "quantitySelector.quantity.alert.symptoms.current":
    "Symptomatic in at least one analysis of the selected period.",
  "quantitySelector.quantity.alert.symptoms.inRange": ({
    alertCount,
    analysisCount,
    alertPercentage,
  }: {
    alertCount: number;
    analysisCount: number;
    alertPercentage: string;
  }) =>
    `Symptomatic in ${alertPercentage}% (${alertCount}/${analysisCount}) of analyses in the time frame (when the quantity is available).`,
  "quantitySelector.selected.title": "Selected quantities",
  "quantitySelector.toggle.showDescriptions": "Display descriptions",
  "quantitySelector.toggle.showIntermediateCalculations": "Display intermediate calculations",
  "quantitySelector.toggle.symptomatic": "Display symptomatic values only",
  "quantitySelector.toggle.unavailable": "Display unavailable values only",
  "quantitySelector.toogle.filter": "Filter quantities",
  reparability: (reparability: Reparability, translations: CustomTranslations) => {
    switch (reparability) {
      case "REPARABILITY_0":
        return translations.EN.REPARABILITY_0;
      case "REPARABILITY_1":
        return translations.EN.REPARABILITY_1;
      case "REPARABILITY_2":
        return translations.EN.REPARABILITY_2;
      default:
        throw new Error(reparability satisfies never);
    }
  },
  "reparability.none": "Not specified",
  "reporting.chart.aggregation.failure.other": (translations: CustomTranslations) =>
    `Other ${lowercase(translations.EN.EXPLAINED)} ${lowercase(translations.EN.LOSSES)}`,
  "reporting.chart.aggregation.failure.remaining_losses": (translations: CustomTranslations) =>
    `${capitalize(translations.EN.UNEXPLAINED)} ${lowercase(translations.EN.LOSSES)}`,
  "reporting.chart.aggregation.failure.total_losses": (translations: CustomTranslations) =>
    `Internal ${lowercase(translations.EN.LOSSES)} - Total`,
  "reporting.chart.aggregation.other": "Others",
  "reporting.chart.failure_aggregation.category": "Per category",
  "reporting.chart.failure_aggregation.degradationType": "Per degradation type",
  "reporting.chart.failure_aggregation.failure": "Per fault",
  "reporting.chart.failure_aggregation.project": "Per project",
  "reporting.chart.failure_aggregation.reparability": "Per outage time for repair",
  "reporting.chart.failure_aggregation.site": "Per site",
  "reporting.chart.failure_aggregation.system": "Per system",
  "reporting.chart.failure_aggregation.type": (translations: CustomTranslations): string =>
    `Per ${translations.EN.TYPE}`,
  "reporting.chart.info.title": "Graph content",
  "reporting.chart.subtitle"({
    // filteredProjectCount,
    includedProjectCount,
    draft,
    siteCount,
  }: {
    // filteredProjectCount: number;
    includedProjectCount: number;
    draft: boolean;
    siteCount: number;
  }) {
    return `${includedProjectCount} ${draft ? "" : "production "}project${
      includedProjectCount > 1 ? "s" : ""
    } displayed (${siteCount} site${siteCount > 1 ? "s" : ""})`;
  },
  "reporting.chart.subtitle.filtered_failures"({ failureCount }: { failureCount: number }) {
    return `${failureCount} faults displayed`;
  },
  "reporting.chart.subtitle.selected_month"({
    selectedMonth,
    selectedYear,
  }: {
    selectedMonth: string;
    selectedYear: string;
  }) {
    return ` - ${selectedMonth} ${selectedYear}`;
  },
  "reporting.chart.switch.chart": "Chart",
  "reporting.chart.switch.matrix": "Matrix",
  "reporting.charts.reparability_impacts_matrix.failure": "Fault",
  "reporting.charts.reparability_impacts_matrix.impact": "Impact",
  "reporting.charts.reparability_impacts_matrix.reparability": "Outage time for repair",
  "reporting.errors.copy_details": "Copy the detail of these projects",
  "reporting.errors.multi_published_analyses"({ count }: { count: number }) {
    return `On the displayed period, for ${count} project${
      count > 1 ? "s" : ""
    }, several analyses have been published on a same month.\nIn that case, data displayed for the month corresponds to the latest published analysis.`;
  },
  "reporting.errors.no_published_analyses"({ count }: { count: number }) {
    return `On the displayed period, for ${count} project${
      count > 1 ? "s" : ""
    }, no data has been published on at least a month.\nIn that case, data displayed is the last published value in the history. Raw data is visible when the Fill in data toggle is unchecked.`;
  },
  "reporting.export": "Export",
  "reporting.export.matrix": "Export matrix as xlsx file",
  "reporting.failure_impact_reparability.description":
    "This view presents all the faults present over a month, with an analysis according to their reparability and their impact.",
  "reporting.failure_impact_reparability.options.impact.description":
    "Quantification of the impact of the fault according to the selected performance indicator",
  "reporting.failure_impact_reparability.options.impact.title": "Impact (ordinate)",
  "reporting.failure_impact_reparability.options.reparability.description":
    "Index of ability to repair a fault",
  "reporting.failure_impact_reparability.options.reparability.title":
    "Outage time for repair (x-axis)",
  "reporting.failure_impact_reparability.title":
    "Analysis of faults according to their impact and outage time for repair",
  "reporting.failure_impacts.info.description": (translations: CustomTranslations) =>
    `This view presents internal ${lowercase(
      translations.EN.LOSSES,
    )} by fault, with a possibility to aggregate it per fault category, outage time for repair, degradation type or system.`,
  "reporting.failure_impacts.title": "Fleet-wide faults",
  "reporting.filters.failure.category": "Fault category",
  "reporting.filters.failure.degradationType": "Degradation type",
  "reporting.filters.failure.equipmentType": "Equipment type",
  "reporting.filters.failure.system": "System",
  "reporting.filters.project.project": "Project",
  "reporting.fleet_internal_gaps.chart.mean_expected": "Average expected",
  "reporting.fleet_internal_gaps.chart.mean_internal_gap": (translations: CustomTranslations) =>
    `Average internal ${lowercase(translations.EN.LOSSES)}`,
  "reporting.fleet_internal_gaps.chart.relative_gaps": (translations: CustomTranslations) =>
    `Relative internal ${lowercase(translations.EN.LOSSES)}`,
  "reporting.fleet_internal_gaps.chart.tootip.evolution": "Evolution",
  "reporting.fleet_internal_gaps.info.description": (translations: CustomTranslations) =>
    `The value for each column is the average percentage of internal ${lowercase(
      translations.EN.LOSSES,
    )} on the selected period.\nThe blue column corresponds to the same computation for the previous period of same duration.`,
  "reporting.fleet_internal_gaps.title": (translations: CustomTranslations) =>
    `${capitalize(translations.EN.LOSSES)} evolution vs. time`,
  "reporting.fleet_performance.chart.expected": "Expected value",
  "reporting.fleet_performance.chart.internal_losses": (translations: CustomTranslations) =>
    `Internal ${lowercase(translations.EN.LOSSES)}`,
  "reporting.fleet_performance.chart.measured": "Measured value",
  "reporting.fleet_performance.info.description": (translations: CustomTranslations) =>
    `This view shows the evolution of measured and expected performance indicator values, leading to the calculation of internal ${lowercase(
      translations.EN.LOSSES,
    )}.`,
  "reporting.fleet_performance.title": "Fleet performance",
  "reporting.fleet_performance.tooltip.project_subtitle": (
    projectCount: number,
    filledCount: number,
    percentProject: number,
  ) =>
    filledCount
      ? `Values based on ${projectCount} project${
          projectCount > 1 ? "s" : ""
        } (${percentProject}% — including ${filledCount} with values from the previous month)`
      : `Values based on ${projectCount} project${projectCount > 1 ? "s" : ""} (${percentProject}%)`,
  "reporting.header.display_draft": "Include research projects",
  "reporting.header.fill_gaps": "Fill in data gaps",
  "reporting.header.title": "Fleet view",
  "reporting.internal_gaps": (translations: CustomTranslations) =>
    `Internal ${lowercase(translations.EN.LOSSES)}`,
  "reporting.internal_gaps.description":
    "Expected value - Measured value\n(for the selected performance indicator)",
  "reporting.internal_gaps.on_expected": (translations: CustomTranslations) =>
    `Internal ${lowercase(translations.EN.LOSSES)} (% of expected value)`,
  "reporting.internal_gaps_evolution.chart.internal_gaps": (translations: CustomTranslations) =>
    `Internal ${lowercase(translations.EN.LOSSES)} - Total`,
  "reporting.internal_gaps_evolution.info.description": (translations: CustomTranslations) =>
    `This view presents internal ${lowercase(
      translations.EN.LOSSES,
    )} by project, with a possibility to aggregate it per ${lowercase(
      translations.EN.TYPE,
    )} or site.`,
  "reporting.internal_gaps_evolution.title": (translations: CustomTranslations) =>
    `${capitalize(translations.EN.LOSSES)}`,
  "reporting.internal_gaps_percentage": (translations: CustomTranslations) =>
    `Internal ${lowercase(translations.EN.LOSSES)} (%)`,
  "reporting.internal_gaps_percentage.description": (translations: CustomTranslations) =>
    `Internal ${lowercase(
      translations.EN.LOSSES,
    )} / Expected value\n(for the selected performance indicator)`,
  "reporting.no_results": ({ from, to }: { from: string | null; to: string | null }) =>
    `No report available${
      from && to ? `, from ${from} to ${to} period.` : ""
    }, with selected parameters.`,
  "reporting.page.diagnosis": "Diagnosis",
  "reporting.page.performance": "Performance",
  "reporting.period_selector.1y": "1Y",
  "reporting.period_selector.3m": "3M",
  "reporting.period_selector.6m": "6M",
  "reporting.period_selector.all_time": "All",
  "reporting.repairability.matrix.cell_impact.cost":
    "Total cost of the impact of all faults in this outage time for repair category: impact (MW) × repair time (hours) × 60€/MWh",
  "reporting.repairability.matrix.empty_cell": "No data to display",
  "reporting.repairability.matrix.fault_impact": "Total fault impact across all affected units",
  "reporting.repairability.matrix.legend.badge":
    "On-line: 3 weeks | 1-3 days: 2 months | 4+ days: 7000h",
  "reporting.repairability.matrix.legend.cost":
    "Estimated cost calculation: Impact (MW) × Repair time (hours) × 60€/MWh",
  "reporting.repairability.matrix.legend.recurring_faults":
    "Recurring faults from the previous month:",
  "reporting.repairability.matrix.legend.unknown_repairability": ({ atf }: { atf: string }) =>
    `Unknown outage time for repair (grouped in ${atf}):`,
  "reporting.repairability.matrix.minor_faults.hide": "Hide minor faults",
  "reporting.repairability.matrix.minor_faults.hide_text": ({
    count,
    icon,
  }: {
    count: number;
    icon: string;
  }) => `${icon} Hide ${count} minor fault${count > 1 ? "s" : ""} (impact < 0.5 MW)`,
  "reporting.repairability.matrix.minor_faults.show": "Show minor faults",
  "reporting.repairability.matrix.minor_faults.show_text": ({
    count,
    icon,
  }: {
    count: number;
    icon: string;
  }) => `${icon} Show ${count} minor fault${count > 1 ? "s" : ""} (impact < 0.5 MW)`,
  "reporting.repairability.matrix.total": "Total:",
  "reporting.repairability.matrix.total_financial_impact": "Total financial impact:",
  "reporting.reparability_impact_matrix.chart.otherFailureCategory": "Other",
  "reporting.title.failures.date.range": (years: number, months: number) => {
    const r = [
      years > 0 && `${years} year${years > 1 ? "s" : ""}`,
      years > 0 && months > 0 && "and",
      months > 0 && `${months} month${months > 1 ? "s" : ""}`,
    ]
      .filter(Boolean)
      .join(" ");
    return `(${r})`;
  },
  "reporting.title.failures.order.alphabetical": "Alphabetical",
  "reporting.title.failures.order.impact": "Descending",
  "setting.cross_filter.import.error.data_quantity_duplicated": ({
    quantityName,
    source,
  }: {
    quantityName: string;
    source: string;
  }) =>
    `Quantity ${quantityName} - Source ${source}: two filters cannot be defined based on the same source.`,
  "setting.cross_filter.import.error.invalid_source_quantity": ({
    quantityName,
    source,
  }: {
    quantityName: string;
    source: string;
  }) =>
    `Quantity ${quantityName} - Source ${source}: 'maximumStd' field must remain empty for calculated quantities.`,
  "setting.filter.import.error.column_not_expected": ({ columnName }: { columnName: string }) =>
    `Format error: ${columnName} column is not expected for this file type.`,
  "setting.filter.import.error.columns_expected": `Format error: expected columns are 'quantity', 'minimumValue', 'maximumValue' and 'maximumStd'.`,
  "setting.filter.import.error.duplicate_quantity_name": ({
    quantityName,
  }: {
    quantityName: string;
  }) => `Quantity ${quantityName}: cannot appear on two different lines.`,
  "setting.filter.import.error.empty_csv": `The csv is empty.`,
  "setting.filter.import.error.invalid_number_value": ({
    quantityName,
  }: {
    quantityName: string;
  }) => `Quantity ${quantityName}: 'minimumValue' field must contain numerical values.`,
  "setting.filter.import.error.least_one_filter_value_required": ({
    quantityName,
  }: {
    quantityName: string;
  }) =>
    `Quantity ${quantityName}: at least one of the three fields 'minimumValue', 'maximumValue' or 'maximumStd' must contain a value.`,
  "setting.filter.import.error.max_inferior_to_min": ({ quantityName }: { quantityName: string }) =>
    `Quantity ${quantityName}: 'minimumValue' field must contain a value smaller than the one in 'maximumValue'.`,
  "setting.filter.import.error.maximumStd_isnt_allowed_for_formulas": ({
    quantityName,
  }: {
    quantityName: string;
  }) => `Quantity ${quantityName}: 'maximumStd' field must remain empty for calculated quantities.`,
  "setting.filter.import.error.quantity_not_existing_in_project": ({
    quantityName,
  }: {
    quantityName: string;
  }) => `Quantity ${quantityName}: it must be a quantity in the project.`,
  "setting.filter.import.error.std_positive": ({ quantityName }: { quantityName: string }) =>
    `Quantity ${quantityName}: 'maximumStd' field must contain positive numerical values.`,
  "setting.parser.display_filter.import.error.default_without_group": ({
    name,
    value,
  }: {
    name: string;
    value: string;
  }) =>
    `Filter "${name}" has no group but "Default" is set to ${value}. Leave Default empty for ungrouped filters.`,
  "setting.parser.display_filter.import.error.filter_name_empty": ({ row }: { row: number }) =>
    `Filter Name is missing at row ${row}.`,
  "setting.parser.display_filter.import.error.filter_name_too_long": ({ name }: { name: string }) =>
    `Filter "${name}" cannot be longer than 50 characters.`,
  "setting.parser.display_filter.import.error.group_multiple_defaults": ({
    groupName,
  }: {
    groupName: string;
  }) =>
    `Group "${groupName}" has more than one default filter. Only one filter per group can be set as default.`,
  "setting.parser.display_filter.import.error.invalid_default_value": ({
    row,
    value,
  }: {
    row: number;
    value: string;
  }) => `Invalid value "${value}" in column isDefault at row ${row}. Expected "true" or "false".`,
  "setting.parser.display_filter.import.error.invalid_number_maximum": ({
    row,
    value,
  }: {
    row: number;
    value: string;
  }) => `Invalid value "${value}" in column Max at row ${row}. Expected a number.`,
  "setting.parser.display_filter.import.error.invalid_number_maximum_std": ({
    quantityName,
    name,
  }: {
    quantityName: string;
    name: string;
  }) => (
    <>
      Filter <strong>{name}</strong> - Quantity <strong>{quantityName}</strong>:
      &apos;maximumStd&apos; field must contain numerical values.
    </>
  ),
  "setting.parser.display_filter.import.error.invalid_number_minimum": ({
    row,
    value,
  }: {
    row: number;
    value: string;
  }) => `Invalid value "${value}" in column Min at row ${row}. Expected a number.`,
  "setting.parser.display_filter.import.error.invalid_source_quantity": ({
    quantityName,
    name,
  }: {
    quantityName: string;
    name: string;
  }) => (
    <>
      Filter <strong>{name}</strong> - Quantity <strong>{quantityName}</strong>:
      &apos;maximumStd&apos; field must remain empty for calculated quantities.
    </>
  ),
  "setting.parser.display_filter.import.error.least_one_filter_value_required": ({
    quantityName,
    name,
  }: {
    quantityName: string;
    name: string;
  }) => (
    <>
      Filter <strong>{name}</strong> - Quantity <strong>{quantityName}</strong>: at least one of the
      three fields &apos;minimumValue&apos;, &apos;maximumValue&apos; or &apos;maximumStd&apos; must
      contain a value.
    </>
  ),
  "setting.parser.display_filter.import.error.max_inferior_to_min": ({ row }: { row: number }) =>
    `Min value is greater than Max value at row ${row}.`,
  "setting.parser.display_filter.import.error.quantity_empty": ({ row }: { row: number }) =>
    `Quantity is missing at row ${row}.`,
  "setting.parser.display_filter.import.error.quantity_not_existing_in_project": ({
    quantityName,
  }: {
    quantityName: string;
  }) => `Quantity "${quantityName}" must exist in the project.`,

  "setting.parser.display_filter.import.error.std_positive": ({
    quantityName,
    name,
  }: {
    quantityName: string;
    name: string;
  }) => (
    <>
      Filter <strong>{name}</strong> - Quantity <strong>{quantityName}</strong>:
      &apos;maximumStd&apos; field must contain positive numerical values.
    </>
  ),
  "setting.parser.quantity.display_filter.import.error.filter_name_not_unique": ({
    name,
    firstRow,
    secondRow,
  }: {
    name: string;
    firstRow: number;
    secondRow: number;
  }) =>
    `Duplicate filter name "${name}" found at rows ${firstRow} and ${secondRow}. Filter names must be unique.`,
  "setting.parser.quantity.display_filter.import.error.invalid_columns": () =>
    `The required column names are 'name', 'quantity', 'minimumValue', 'maximumValue' and 'maximumStd'.`,
  "setting.parser.quantity.import.error.invalid_number_maximum": ({
    quantityName,
    source,
  }: {
    quantityName: string;
    source: string;
  }) =>
    `Quantity ${quantityName} - Source ${source}: 'maximumValue' field must contain numerical values.`,
  "setting.parser.quantity.import.error.invalid_number_maximum_std": ({
    quantityName,
    source,
  }: {
    quantityName: string;
    source: string;
  }) =>
    `Quantity ${quantityName} - Source ${source}: 'maximumStd' field must contain numerical values.`,
  "setting.parser.quantity.import.error.invalid_number_minimum": ({
    quantityName,
    source,
  }: {
    quantityName: string;
    source: string;
  }) =>
    `Quantity ${quantityName} - Source ${source}: 'minimumValue' field must contain numerical values.`,
  "setting.parser.quantity.import.error.least_one_filter_value_required": ({
    quantityName,
    source,
  }: {
    quantityName: string;
    source: string;
  }) =>
    `Quantity ${quantityName} - Source ${source}: at least one of the three fields 'minimumValue', 'maximumValue' or 'maximumStd' must contain a value.`,
  "setting.parser.quantity.import.error.max_inferior_to_min": ({
    quantityName,
    source,
  }: {
    quantityName: string;
    source: string;
  }) =>
    `Quantity ${quantityName} - Source ${source}: 'minimumValue' field must contain a value smaller than the one in 'maximumValue'.`,
  "setting.parser.quantity.import.error.quantity_not_existing_in_project": ({
    quantityName,
    source,
  }: {
    quantityName: string;
    source: string;
  }) => `Quantity ${quantityName} - Source ${source}: it must be a quantity in the project.`,
  "setting.parser.quantity.import.error.std_positive": ({
    quantityName,
    source,
  }: {
    quantityName: string;
    source: string;
  }) =>
    `Quantity ${quantityName} - Source ${source}: 'maximumStd' field must contain positive numerical values.`,
  "settings.display_filter.table.column.group": "Group name",
  "settings.display_filter.table.column.name": "Filter name",
  "settings.display_filter.table.column.quantity": "Quantity",
  "setup.diagnosis.algorithm.link_button.edit": "Edit",
  "setup.diagnosis.algorithm.modal.ok_button_name": "Edit",
  "setup.diagnosis.algorithm.modal.title": "Edit the algorithm settings",
  "setup.diagnosis.algorithm.parameters.edit.text": "Modified parameters",
  "setup.diagnosis.algorithm.title": "Algorithm",
  "setup.diagnosis.algorithm.tooltip.text":
    "The algorithm used by default is set in the application settings. You can edit it at the setup level.",
  "setup.diagnosis.failure.blank.button": "Import faults",
  "setup.diagnosis.failure.blank.subtitle": "Import the faults matrix",
  "setup.diagnosis.failure.blank.title": "No fault defined",
  "setup.diagnosis.failure.compare.axis": "Normalized impact",
  "setup.diagnosis.failure.compare.blocked":
    "At least two selected faults must be available in this setup to compare their signatures.",
  "setup.diagnosis.failure.compare.button": "Compare",
  "setup.diagnosis.failure.compare.chart.observable_count": (count: number) =>
    `${count} observable${count === 1 ? "" : "s"}`,
  "setup.diagnosis.failure.compare.chart.title": "Normalized impact by observable",
  "setup.diagnosis.failure.compare.controls": "Chart display controls",
  "setup.diagnosis.failure.compare.disabled.maximum":
    "Select no more than three faults to compare.",
  "setup.diagnosis.failure.compare.disabled.minimum": "Select two or three faults to compare.",
  "setup.diagnosis.failure.compare.disabled.mode":
    "Fault comparison is only available in the fault matrix.",
  "setup.diagnosis.failure.compare.empty":
    "No observables meet this minimum difference. Lower the minimum percentage to display them.",
  "setup.diagnosis.failure.compare.excluded": (faults: string) =>
    `Unavailable in this setup: ${faults}.`,
  "setup.diagnosis.failure.compare.minimum_difference": "Minimum difference",
  "setup.diagnosis.failure.compare.minimum_difference.summary":
    "Increase this value to display fewer observables.",
  "setup.diagnosis.failure.compare.minimum_difference.tooltip":
    "An observable is displayed when the difference between the highest and lowest normalized fault impact is greater than this percentage. Increase it to focus on the observables that best distinguish the selected faults.",
  "setup.diagnosis.failure.compare.score": "Collinearity score",
  "setup.diagnosis.failure.compare.score.level.distinct": "Distinct",
  "setup.diagnosis.failure.compare.score.level.similar": "Similar",
  "setup.diagnosis.failure.compare.score.level.very_similar": "Very similar",
  "setup.diagnosis.failure.compare.score.tooltip":
    "The score is the angle between two sigma-normalized, L2-normalized fault vectors.",
  "setup.diagnosis.failure.compare.select": (fault: string) => `Select ${fault} for comparison`,
  "setup.diagnosis.failure.compare.setup": "Setup",
  "setup.diagnosis.failure.compare.sort.alphabetical": "Alphabetical",
  "setup.diagnosis.failure.compare.sort.label": "Sort observables by",
  "setup.diagnosis.failure.compare.title": "Compare fault signatures",
  "setup.diagnosis.failure.confirm.activate.title": "Activate a fault",
  "setup.diagnosis.failure.confirm.deactivate.title": "Deactivate a fault",
  "setup.diagnosis.failure.filter_observables": "Filter observables",
  "setup.diagnosis.failure.import.new_categories":
    "The file includes new categories, which will be created upon validation of the import.",
  "setup.diagnosis.failure.import.new_categories.summary": "Details of new categories",
  "setup.diagnosis.failure.show_disabled": "Show disabled faults",
  "setup.diagnosis.failure.show_unused": "Show quantities not used by diagnosis",
  "setup.diagnosis.failure.title": "Faults",
  "setup.diagnosis.failure.tooltip.color": "Color associated with fault representation on graphs",
  "setup.diagnosis.failures.delete_failures.toast.success": "Faults have been deleted",
  "setup.diagnosis.failures.delete_observables.title": "Delete faults",
  "setup.diagnosis.failures.import_checking": "Faults have been imported",
  "setup.diagnosis.failures.modal.error.activated_invalid": ({
    nameFailures,
  }: {
    nameFailures: string;
  }) => `Fault ${nameFailures}: 'activated' field must contain 0 or 1.`,
  "setup.diagnosis.failures.modal.error.bad_impacts": ({
    failure,
    quantity,
  }: {
    failure: string;
    quantity: string;
  }) => `Fault ${failure}: impact value on ${quantity} quantity must be numeric`,
  "setup.diagnosis.failures.modal.error.bad_magnitude": ({
    nameFailures,
  }: {
    nameFailures: string[];
  }) =>
    `Magnitude must be a non zero number for ${
      nameFailures.length > 0 ? "faults" : "fault"
    } ${nameFailures}`,
  "setup.diagnosis.failures.modal.error.duplicated_technical_name": ({
    nameFaults,
  }: {
    nameFaults: string[];
  }) => `Fault(s) ${nameFaults.join(", ")}: a fault cannot appear on two different lines`,
  "setup.diagnosis.failures.modal.error.filterMinMax_must_be_number": ({
    nameFailures,
  }: {
    nameFailures: string[];
  }) =>
    `Fault(s) ${nameFailures.join(",")}: 'filterMin' and 'filterMax' must contain numerical values`,
  "setup.diagnosis.failures.modal.error.filterMin_must_be_less_than_FilterMax": ({
    nameFailures,
  }: {
    nameFailures: string[];
  }) =>
    `Fault(s) ${nameFailures.join(
      ",",
    )}: 'filterMin' field must contain a value smaller than the one in 'filterMax'.`,
  "setup.diagnosis.failures.modal.error.filter_formula_unknown_quantity": ({
    nameFailures,
  }: {
    nameFailures: string[];
  }) =>
    `Fault(s) ${nameFailures.join(
      ",",
    )}: 'filterFormula' field must be based on a quantity defined in the project`,
  "setup.diagnosis.failures.modal.error.invalid_filter_type": ({
    failure,
    allowed,
  }: {
    failure: string;
    allowed: string[];
  }) => {
    const allowedFilter =
      allowed.length === 1
        ? `‘${allowed[0]}’`
        : `‘${allowed.slice(0, -1).join("’, ‘")}’ or ‘${allowed.at(-1)}’`;
    const verb = allowed.length === 1 ? "is" : "are";
    return `Fault(s) ${failure}: option${allowed.length > 1 ? "s" : ""} possible in the filterType column ${verb} ${allowedFilter}.`;
  },
  "setup.diagnosis.failures.modal.error.max_not_a_number": ({
    nameFailures,
  }: {
    nameFailures: string[];
  }) => `Fault(s) ${nameFailures.join(",")}: 'maximumValue' field must contain numerical values`,
  "setup.diagnosis.failures.modal.error.min_greater_than_max": ({
    nameFailures,
  }: {
    nameFailures: string[];
  }) =>
    `Fault(s) ${nameFailures.join(
      ",",
    )}: 'minimumValue' field must contain a value smaller than the one in 'maximumValue'.`,
  "setup.diagnosis.failures.modal.error.min_not_a_number": ({
    nameFailures,
  }: {
    nameFailures: string[];
  }) => `Fault(s) ${nameFailures.join(",")}: 'minimumValue' field must contain numerical values`,
  "setup.diagnosis.failures.modal.error.missingMandatoryColumns": ({
    properties,
  }: {
    properties: string[];
  }) =>
    `${properties.length > 1 ? "Columns " : "The column "} ${properties.join(", ")} ${
      properties.length > 1 ? " are " : " is "
    } required`,
  "setup.diagnosis.failures.modal.error.missing_column": ({
    properties,
  }: {
    properties: string[];
  }) => `Format error: expected column names are ${properties.join(", ")}`,
  "setup.diagnosis.failures.modal.error.missing_filterFomula_dependencied": ({
    nameFailures,
  }: {
    nameFailures: string[];
  }) =>
    `Fault(s) ${nameFailures.join(
      ",",
    )}: when 'filterFormula' field is filled in, 'filterMin' or 'filterMax' must be filled in.`,
  "setup.diagnosis.failures.modal.error.name_not_human_readable": ({
    nameFailures,
  }: {
    nameFailures: string[];
  }) => `Fault(s) ${nameFailures.join(",")}: fault name has the wrong format`,
  "setup.diagnosis.failures.modal.error.name_too_long": ({
    nameFailures,
  }: {
    nameFailures: string[];
  }) =>
    `The field 'name' must contain less than 100 characters for ${
      nameFailures.length > 1 ? "faults" : "fault"
    } ${nameFailures}`,
  "setup.diagnosis.failures.modal.error.unit_too_long": ({
    nameFailures,
  }: {
    nameFailures: string[];
  }) =>
    `The field 'unit' must contain less than 100 characters for ${
      nameFailures.length > 1 ? "faults" : "fault"
    } ${nameFailures}`,
  "setup.diagnosis.failures.modal.error.unknown_quantity": ({
    failureNames,
  }: {
    failureNames: string[];
  }) => (
    <>
      Column(s) {failureNames.join(", ")}: the name of the column is not a quantity of the project
    </>
  ),
  "setup.diagnosis.failures.modal.error.wrongInputMultiValue": ({
    nameFailures,
  }: {
    nameFailures: string[];
  }) =>
    `Fault(s) ${nameFailures.join(", ")}: the element has the wrong format for a multi-value entry`,
  "setup.diagnosis.failures.modal.error.wrongInputReparability": ({
    nameFailures,
  }: {
    nameFailures: string[];
  }) => `Fault(s) ${nameFailures.join(", ")}: the type of reparability has the wrong format`,
  "setup.diagnosis.failures.modal.error.wrong_duplication_filterFomula": ({
    nameFailures,
  }: {
    nameFailures: string[];
  }) =>
    `Fault(s) ${nameFailures.join(
      ", ",
    )}: it is not possible to declare two FilterFormula filters of the same type based on the same quantity`,
  "setup.diagnosis.failures.modal.error.wrong_format_filterFormula": ({
    nameFailures,
  }: {
    nameFailures: string[];
  }) =>
    `Fault(s) ${nameFailures.join(", ")}: the format used in the filterFormula column is incorrect`,
  "setup.diagnosis.failures.modal.error.wrong_format_filterMax": ({
    nameFailures,
  }: {
    nameFailures: string[];
  }) =>
    `Fault(s) ${nameFailures.join(
      ", ",
    )}: 'filterMax' field must remain empty when 'filterFormula' field is empty.`,
  "setup.diagnosis.failures.modal.error.wrong_format_filterMin": ({
    nameFailures,
  }: {
    nameFailures: string[];
  }) =>
    `Fault(s) ${nameFailures.join(
      ", ",
    )}: 'filterMin' field must remain empty when 'filterFormula' field is empty.`,
  "setup.diagnosis.failures.modal.permissionRequired.notAllow": ({
    infos,
  }: {
    infos: string[];
  }) => (
    <>
      You do not have the rights to create new quantities types : {printListStrings(infos, "and")} .
    </>
  ),
  "setup.diagnosis.faults.modal.error.display_name_empty": ({
    nameFaults,
  }: {
    nameFaults: string[];
  }) =>
    `The 'displayName' field must be filled in when the column is present,
     it is missing for the following ${
       nameFaults.length > 1 ? "faults" : "fault"
     }: ${nameFaults.join(", ")}`,
  "setup.diagnosis.faults.modal.error.duplicated_display_name": ({
    technicalNames,
  }: {
    technicalNames: string[];
  }) => `Duplicate fault display name for faults with technical name ${technicalNames.join(", ")}`,
  "setup.diagnosis.faults.modal.error.technical_name_empty": ({ lines }: { lines: string[] }) =>
    `Technical name cannot be empty for ${lines.length > 1 ? "lines" : "line"} ${lines.join(", ")}`,
  "setup.diagnosis.faults.modal.error.technical_name_too_long": ({
    nameFaults,
  }: {
    nameFaults: string[];
  }) =>
    `The 'technicalName' field must be less than 100 characters long, it is too long for
      the following ${nameFaults.length > 1 ? "faults" : "fault"}: ${nameFaults.join(", ")}`,
  "setup.diagnosis.modal_form.algorithm.input_boolean.value_diff_default_value": (
    defaultValue: boolean,
  ) => `Modified: by default, the checkbox should be ${defaultValue ? "ticked" : "unticked"}.`,
  "setup.diagnosis.modal_form.algorithm.input_number.error.max": (max: number) =>
    `The value cannot exceed ${max}`,
  "setup.diagnosis.modal_form.algorithm.input_number.error.min": (min: number) =>
    `The value cannot be less than ${min}`,
  "setup.diagnosis.modal_form.algorithm.input_number.value_diff_default_value": (
    defaultValue: string,
  ) => `Modified: default value is ${defaultValue}.`,
  "setup.diagnosis.modal_form.algorithm.message.update_algorithm": (nbAnalyses: number) =>
    `Upon modification of the algorithm version and/or its parameters, diagnosis computations will be impacted for ${nbAnalyses} analyse${
      nbAnalyses > 1 ? "s" : ""
    }.`,
  "setup.diagnosis.modal_form.algorithm.select.label": "Version",
  "setup.diagnosis.modal_form.section.modifiable_parameters": "Modifiable parameters",
  "setup.diagnosis.observable.blank.button": "Import observables",
  "setup.diagnosis.observable.blank.subtitle": "Import the quantities to be used as observables",
  "setup.diagnosis.observable.blank.title": "No observable defined",
  "setup.diagnosis.observable.confirm.edit.body": (quantityName: string) => (
    <>
      Would you like to confirm the modification of <strong>{quantityName}</strong>?
    </>
  ),
  "setup.diagnosis.observable.confirm.edit.title": "Confirm the modification",
  "setup.diagnosis.observable.confirm.toggle.body": (enable: boolean, quantityName: string) =>
    enable ? (
      <>
        Do you confirm the deactivation of <strong>{quantityName}</strong>
      </>
    ) : (
      <>
        Do you confirm the activation of <strong>{quantityName}</strong>
      </>
    ),
  "setup.diagnosis.observable.confirm.toggle.title": (enable: boolean): string =>
    enable ? "Activate observable" : "Deactivate observable",
  "setup.diagnosis.observable.delete_observables.body": () =>
    "Do you confirm the deletion of observables?",
  "setup.diagnosis.observable.delete_observables.title": "Delete observables",
  "setup.diagnosis.observable.delete_observables.toast.success": ({ name }: { name: string }) =>
    `Observables of the setup ${name} have been deleted.`,
  "setup.diagnosis.observable.import_observables.error.data_activated_invalid": (
    quantityId: string,
  ) => `Quantity ${quantityId}: 'activatedObservable' field must contain 0 or 1`,
  "setup.diagnosis.observable.import_observables.error.data_duplicated": (quantityId: string) =>
    `Quantity ${quantityId}: a quantity cannot appear on two different lines`,
  "setup.diagnosis.observable.import_observables.error.data_quantity_id_missing":
    "The field 'name' cannot be empty.",
  "setup.diagnosis.observable.import_observables.error.data_quantity_id_not_allowed": (
    quantityId: string,
  ) => `Quantity ${quantityId}: 'name' field must contain quantities defined in the project`,
  "setup.diagnosis.observable.import_observables.error.data_sigma_activated_required": (
    quantityId: string,
  ) =>
    `Quantity ${quantityId}: 'observableSigmaDiagnosis' cannot be empty when the quantity is an observable`,
  "setup.diagnosis.observable.import_observables.error.data_sigma_invalid": (quantityId: string) =>
    `Quantity ${quantityId}: 'observableSigmaDiagnosis' field must contain positive numbers`,
  "setup.diagnosis.observable.import_observables.error.empty_file": "Empty file",
  "setup.diagnosis.observable.import_observables.error.file_size_limit_exceeded":
    "File size expected to be equal or lower to 20 Mo",
  "setup.diagnosis.observable.import_observables.error.invalid_content":
    "Format error: expected columns are 'name', 'activatedObservable' and 'observableSigmaDiagnosis'.",
  "setup.diagnosis.observable.import_observables.error.invalid_format":
    "File must be in .csv format",
  "setup.diagnosis.observable.import_observables.title": "Import observables",
  "setup.diagnosis.observable.import_observables.toast.success": ({ count }: { count: number }) =>
    count === 1
      ? `${count} observable has been defined.`
      : `${count} observables have been defined.`,
  "setup.diagnosis.observable.import_quantities.error.invalid_format":
    "The file must be in csv format.",
  "setup.diagnosis.observable.show_disabled": "Show disabled observables",
  "setup.diagnosis.observable.table.column.description": "Description",
  "setup.diagnosis.observable.table.column.description.hint":
    "Linked to the Description column in the quantity table",
  "setup.diagnosis.observable.table.column.offset.hint":
    "Linked to the Offset column in the model outputs table in the simulation settings",
  "setup.diagnosis.observable.table.column.quantity": "Quantity",
  "setup.diagnosis.observable.table.column.sigma": "Sigma",
  "setup.diagnosis.observable.table.column.unit": "Unit",
  "setup.diagnosis.observable.table.column.unit.hint":
    "Linked to the Unit column in the quantity table",
  "setup.diagnosis.observable.title": "Observables",
  "setup.diagnosis.status.complete": () => (
    <>
      The diagnosis setup is <strong>validated</strong>
    </>
  ),
  "setup.diagnosis.status.incomplete": () => (
    <>
      The diagnosis setup is <strong>incomplete</strong> because:
    </>
  ),
  "setup.diagnosis.status.incomplete.noEnabledFailure": "all faults are disabled",
  "setup.diagnosis.status.incomplete.noEnabledInput": "all observables are disabled",
  "setup.diagnosis.status.incomplete.noFailure": "no fault matrix was imported",
  "setup.diagnosis.status.incomplete.noImpact": "no impact defined in fault matrix",
  "setup.diagnosis.status.incomplete.noInput": "no list of observables was imported",
  "setup.diagnosis.status.invalid": () => (
    <>
      The diagnosis setup is <strong>invalidated</strong> because the following quantities are like
      observable but:
    </>
  ),
  "setup.diagnosis.status.invalid.missingImpact": (QuantitiesName: string[]) =>
    `are not present in the fault matrix: ${QuantitiesName.join(", ")}`,
  "setup.diagnosis.status.invalid.notSimulationOutput": (QuantitiesName: string[]) =>
    `are not outputs of the model: ${QuantitiesName.join(", ")}`,
  "setup.failure.table.failure_filter.definitions.column.failure": "Fault",
  "setup.failure.table.failure_filter.definitions.column.maximum": "Max",
  "setup.failure.table.failure_filter.definitions.column.minimum": "Min",
  "setup.failure.table.failure_filter.definitions.column.quantity": "Quantity",
  "setup.failure.table.failure_filter.definitions.column.rule": "Filter",
  "setup.failure.table.failure_filter.definitions.column.rule.tooltip": () => (
    <>
      • Constraint filter: the fault will only be considered by the diagnosis algorithm if the
      filter is respected.
      <br />• Promotion filter: the fault is more likely to be proposed by the diagnosis algorithm
      if the filter is respected.
    </>
  ),
  "setup.failure.table.failure_filter.definitions.value.rule.constraint": "Constraint",
  "setup.failure.table.failure_filter.definitions.value.rule.promotion": "Promotion",
  "setup.failures.table.selectors.filter": "Fault filters",
  "setup.failures.table.selectors.filter.disabled":
    "No fault filter defined. Import a new file to define them.",
  "setup.failures.table.selectors.matrix": "Fault matrix",
  "setup.header.nav.diagnosis": "Diagnosis",
  "setup.header.nav.simulation": "Simulation",
  "setup.import.fileName": "File loaded:",
  "setup.import.quantities.message.info.ignore_sensor_tags.details": (
    quantity: string,
    sensorTag: string,
  ) => `${quantity} ${sensorTag ? `(sensor tag : ${sensorTag}).` : ""}`,
  "setup.import.quantities.message.info.ignore_sensor_tags.details.summary":
    "Details of ignored quantities",
  "setup.import.quantities.message.info.to_be_created": `The file includes new categories, which will be created when the import is validated.`,
  "setup.import.quantities.message.info.to_be_created.details.equipment": (equipments: string[]) =>
    `Equipment${equipments.length > 1 ? "s" : ""}: ${equipments.join(", ")}.`,
  "setup.import.quantities.message.info.to_be_created.details.equipment_type": (
    equipmentType: string[],
  ) => `Equipment type${equipmentType.length > 1 ? "s" : ""} : ${equipmentType.join(", ")}.`,
  "setup.import.quantities.message.info.to_be_created.details.summary": "Details of new categories",
  "setup.import.quantities.message.info.to_be_created.details.system": (systems: string[]) =>
    `System${systems.length > 1 ? "s" : ""}: ${systems.join(", ")}.`,
  "setup.import.quantities.message.warning.ignore_sensorTag":
    "Some quantities in the file were ignored because their sensorTag was not identified in the data source associated with the project.",
  "setup.modal.add_setup.toast": ({ name }: { name: string }) => `Setup ${name} has been created`,
  "setup.modal.delete.analysis.additionnal_message.warning": ({
    simulationValid,
    diagnosisValid,
    impactedAnalysisCount,
  }: {
    simulationValid?: boolean;
    diagnosisValid: boolean;
    impactedAnalysisCount: number;
  }) => {
    const simulationResults = simulationValid ? "simulation" : "";
    const diagnosisResults = diagnosisValid ? "diagnosis" : "";
    const and = simulationValid && diagnosisValid ? "and" : "";
    const analysisCount = `${impactedAnalysisCount} ${
      impactedAnalysisCount > 1 ? "analyses" : "analysis"
    }`;
    return `Following this action, any ${simulationResults} ${and} ${diagnosisResults} results will be deleted for ${analysisCount}.`;
  },
  "setup.modal.duplicate_setup.toast.success": ({ name }: { name: string }) =>
    `Setup ${name} duplicated`,
  "setup.modal.update_setup.toast": ({
    setupName,
    name,
    description,
  }: {
    setupName: string;
    name: boolean;
    description: boolean;
  }) =>
    name && description
      ? `Name and description have been edited for setup ${setupName}`
      : name
        ? `Name has been edited for setup ${setupName}`
        : `Description has been edited for setup ${setupName}`,
  "setup.paragraphs.failures.modal.delete_failures.body": "Do you confirm the deletion of faults?",
  "setup.paragraphs.failures.modal.import_failures": "Import faults",
  "setup.paragraphs.failures.table.column.category": "Category",
  "setup.paragraphs.failures.table.column.degradationType": "Degradation type",
  "setup.paragraphs.failures.table.column.description": "Description",
  "setup.paragraphs.failures.table.column.displayName": "Display name",
  "setup.paragraphs.failures.table.column.equipmentType": "Equipment type",
  "setup.paragraphs.failures.table.column.equipments": "Equipment",
  "setup.paragraphs.failures.table.column.magnitude": "Amplitude",
  "setup.paragraphs.failures.table.column.max": "Max",
  "setup.paragraphs.failures.table.column.min": "Min",
  "setup.paragraphs.failures.table.column.reparability": "Outage time for repair",
  "setup.paragraphs.failures.table.column.separator": "Impacts",
  "setup.paragraphs.failures.table.column.suspectedElements": "Suspected elements",
  "setup.paragraphs.failures.table.column.system": "System",
  "setup.paragraphs.failures.table.column.technicalName": "Technical name",
  "setup.paragraphs.failures.table.column.unit": "Unit",
  "setup.paragraphs.failures.table.definitions.column.degradationType": "Degradation type",
  "setup.paragraphs.failures.table.definitions.column.failureCategory": "Category",
  "setup.paragraphs.failures.table.definitions.column.roles.observable.disabled.tooltip":
    "OBS: Deactivated observable",
  "setup.paragraphs.failures.table.definitions.column.roles.observable.label": "OBS",
  "setup.paragraphs.failures.table.definitions.column.roles.observable.tooltip": "OBS: Observable",
  "setup.paragraphs.failures.table.definitions.column.roles.quantity_of_interest.label": "PI",
  "setup.paragraphs.failures.table.definitions.column.roles.quantity_of_interest.tooltip":
    "PI: performance indicator",
  "setup.paragraphs.failures.table.failures.no_failures.blank_state.button": "Import faults",
  "setup.paragraphs.failures.table.failures.no_failures.blank_state.no_quantities.text":
    "Once quantities are loaded, you will be allowed to import a model in this section.",
  "setup.paragraphs.failures.table.failures.no_failures.blank_state.title":
    "The faults library is empty",
  "setup.paragraphs.model": "Model",
  "setup.paragraphs.model.actions.import.modal.select.prompt": "Select a model type",
  "setup.paragraphs.model.blank_state.button": "Import model",
  "setup.paragraphs.model.blank_state.text":
    "Import a digital twin from your device to start simulations.",
  "setup.paragraphs.model.blank_state.title": "No model has been loaded",
  "setup.paragraphs.model.error": "Error",
  "setup.paragraphs.model.error.copied": "Error message copied",
  "setup.paragraphs.model.error.copy": "Copy all error messages",
  "setup.paragraphs.model.error.download": "Downloaded error message",
  "setup.paragraphs.model.info.label.files": "Files loaded:",
  "setup.paragraphs.model.info.label.image": "Image: ",
  "setup.paragraphs.model.info.label.type": "Type: ",
  "setup.paragraphs.model.modal.delete_image.text":
    "Do you confirm the deletion of the image associated with the model?",
  "setup.paragraphs.model.modal.delete_image.title": "Delete image",
  "setup.paragraphs.model.modal.delete_image.toast.success": "Image deleted",
  "setup.paragraphs.model.modal.delete_input.text":
    "Do you confirm the deletion of the model inputs?",
  "setup.paragraphs.model.modal.delete_input.title": "Delete model inputs",
  "setup.paragraphs.model.modal.delete_model.text": "Do you confirm model deletion?",
  "setup.paragraphs.model.modal.delete_model.title": "Delete model",
  "setup.paragraphs.model.modal.delete_model.warningText": ({
    impactedAnalysisCount,
  }: {
    impactedAnalysisCount: number;
  }) => (
    <>
      Following this action, diagnostic calculations will be impacted for{" "}
      <strong>
        {impactedAnalysisCount} analys{impactedAnalysisCount > 1 ? "es" : "is"}
      </strong>
      .
    </>
  ),
  "setup.paragraphs.model.modal.delete_output.text":
    "Do you confirm the deletion of the model outputs?",
  "setup.paragraphs.model.modal.delete_output.title": "Delete model outputs",
  "setup.paragraphs.model.modal.update_image.title": "Import image associated with model",
  "setup.paragraphs.model.modal.update_image.toast.success": "Image updated",
  "setup.paragraphs.model.synoptic.no_model.title": "No picture loaded for this model.",
  "setup.paragraphs.model.synoptic.no_model.title.short": "No picture",
  "setup.paragraphs.model.synoptic.no_model.upload_a_model.text":
    "Upload an image of your process.",
  "setup.paragraphs.projects.blank_state.button": "Create new project",
  "setup.paragraphs.quantities.input": "Model inputs",
  "setup.paragraphs.quantities.input.blank_subtitle":
    "Import quantities associated to model inputs.",
  "setup.paragraphs.quantities.input.blank_title": "Model inputs are not defined.",
  "setup.paragraphs.quantities.input.import.button": "Import model inputs",
  "setup.paragraphs.quantities.output": "Model outputs",
  "setup.paragraphs.quantities.output.blank_subtitle":
    "Import quantities associated to model outputs.",
  "setup.paragraphs.quantities.output.blank_title": "Model outputs are not defined.",
  "setup.paragraphs.quantities.output.import.button": "Import model outputs",
  "setup.paragraphs.quantities.table.definitions.column.actions": "Actions",
  "setup.paragraphs.quantities.table.definitions.column.description": "Description",
  "setup.paragraphs.quantities.table.definitions.column.equipmentType": "Equipment type",
  "setup.paragraphs.quantities.table.definitions.column.equipments": "Equipment",
  "setup.paragraphs.quantities.table.definitions.column.formula": "Current formula",
  "setup.paragraphs.quantities.table.definitions.column.hidden": "Intermediate calculation",
  "setup.paragraphs.quantities.table.definitions.column.hidden.no": "No",
  "setup.paragraphs.quantities.table.definitions.column.hidden.yes": "Yes",
  "setup.paragraphs.quantities.table.definitions.column.inputModelName": "Model input variable",
  "setup.paragraphs.quantities.table.definitions.column.max": "Max",
  "setup.paragraphs.quantities.table.definitions.column.maxStd": "Max standard deviation",
  "setup.paragraphs.quantities.table.definitions.column.min": "Min",
  "setup.paragraphs.quantities.table.definitions.column.name": "Name",
  "setup.paragraphs.quantities.table.definitions.column.name.tooltip": "Calculated quantity",
  "setup.paragraphs.quantities.table.definitions.column.sensorTag": "Sensor tag",
  "setup.paragraphs.quantities.table.definitions.column.system": "System",
  "setup.paragraphs.quantities.table.definitions.column.tagDataSource": "Sensor tag",
  "setup.paragraphs.quantities.table.definitions.column.technicalName": "Technical name",
  "setup.paragraphs.quantities.table.definitions.column.type": "Nature",
  "setup.paragraphs.quantities.table.definitions.column.unit": "Unit",
  "setup.paragraphs.quantities.table.definitions.column.value": "Current formula",
  "setup.paragraphs.quantities.table.locked.tooltip":
    "Locked formula, only editable by users with a setup role and admins",
  "setup.quantities.edit.max.inferior.min.error":
    "The maximum value must be greater than the minimum value",
  "setup.quantities.edit.min.superior.max.error":
    "The minimum value must be less than the maximum value",
  "setup.quantities.import.error.missing_columns_helium":
    "The expected columns are 'technicalName', 'calculatedValueExpression', 'parameterValue' and 'tagDataSourceImageValue'.",
  "setup.quantities.import.error.missing_columns_lithium":
    "The expected columns are 'name', 'sensorTag' and 'formula'.",
  "setup.quantity.import.error.data_description_too_long": (quantityName: string) =>
    `Quantity ${quantityName}: 'description' field must contain less than 500 characters.`,
  "setup.quantity.import.error.data_file_empty": "The CSV is empty.",
  "setup.quantity.import.error.data_formula_too_long": (quantityName: string) =>
    `Quantity ${quantityName}: 'formula' column must contain less than 4000 characters.`,
  "setup.quantity.import.error.data_formula_version_base_formula_required": (
    quantityName: string,
  ) =>
    `Quantity ${quantityName}: versioned formula quantities require one base row without 'startDate' and with a formula.`,
  "setup.quantity.import.error.data_formula_version_base_sensor_tag_not_allowed": (
    quantityName: string,
  ) =>
    `Quantity ${quantityName}: the base row of a versioned formula quantity cannot define a 'sensorTag'.`,
  "setup.quantity.import.error.data_formula_version_duplicate_startsAt": (
    quantityName: string,
    startsAt: string,
  ) => {
    void startsAt;
    return `Quantity ${quantityName}: two rows cannot have the same 'startDate' for the same quantity.`;
  },
  "setup.quantity.import.error.data_formula_version_empty_formula": (
    quantityName: string,
    startsAt: string,
  ) => `Quantity ${quantityName}: 'formula' is required for the version starting at ${startsAt}.`,
  "setup.quantity.import.error.data_formula_version_metadata_mismatch": (quantityName: string) =>
    `Quantity ${quantityName}: version rows can only differ from the base row by 'formula' and 'startDate'; any other populated field must match the base row.`,
  "setup.quantity.import.error.data_formula_version_multiple_base_rows": (quantityName: string) =>
    `Quantity ${quantityName}: versioned formula quantities cannot define more than one row without 'startDate'.`,
  "setup.quantity.import.error.data_formula_version_sensor_tag_mismatch": (quantityName: string) =>
    `Quantity ${quantityName}: two versions of a quantity cannot have different sensorTag`,
  "setup.quantity.import.error.data_formula_version_unit_mismatch": (quantityName: string) =>
    `Quantity ${quantityName}: two versions of a quantity cannot have different units`,
  "setup.quantity.import.error.data_input_model_name_too_long": (quantityName: string) =>
    `Quantity ${quantityName}: 'inputModelName' column must contain less than 100 characters.`,
  "setup.quantity.import.error.data_invalid_equipments_helium": (quantityName: string) =>
    `Quantity ${quantityName}: elements in 'correspondingEquipmentType' field must be separated by a comma.`,
  "setup.quantity.import.error.data_invalid_equipments_lithium": (quantityName: string) =>
    `Quantity ${quantityName}: elements in  'equipments' field must be separated by a comma.`,
  "setup.quantity.import.error.data_invalid_formula": (quantityName: string) =>
    `Quantity ${quantityName}: the formula is invalid.`,
  "setup.quantity.import.error.data_invalid_hidden_field": (quantityName: string) =>
    `Quantity ${quantityName}: 'hidden' field must contain booleans (true or false) or be empty`,
  "setup.quantity.import.error.data_invalid_locked_field": (quantityName: string) =>
    `Quantity ${quantityName}: 'locked' field must contain booleans (true or false) or be empty`,
  "setup.quantity.import.error.data_invalid_permission_equipment_creation": (
    quantityName: string,
  ) => `Quantity ${quantityName}: permission to create new equipment required.`,
  "setup.quantity.import.error.data_invalid_permission_equipment_type_creation": (
    quantityName: string,
  ) => `Quantity ${quantityName}: permission to create new type of equipment required.`,
  "setup.quantity.import.error.data_invalid_permission_system_creation": (quantityName: string) =>
    `Quantity ${quantityName}: permission to create new system required.`,
  "setup.quantity.import.error.data_invalid_quantity_name_format_helium": (quantityName: string) =>
    `Quantity ${quantityName}: 'technicalName' field must contain only alphanumerical characters and _, without space.`,
  "setup.quantity.import.error.data_invalid_quantity_name_format_lithium": (quantityName: string) =>
    `Quantity ${quantityName}: 'name' field must contain only alphanumerical characters and _, without space.`,
  "setup.quantity.import.error.data_invalide_pair_value_formula_sensor_helium": (
    quantityName: string,
  ) =>
    `Quantity ${quantityName}: 'technicalName' and 'tagDataSourceImageValue' fields cannot be defined at the same time.`,
  "setup.quantity.import.error.data_invalide_pair_value_formula_sensor_lithium": (
    quantityName: string,
  ) =>
    `Quantity ${quantityName}: 'formula' and 'sensorTag' fields cannot be defined at the same time.`,
  "setup.quantity.import.error.data_maxStd_with_formula_invalid_helium": (quantityName: string) =>
    `Quantity ${quantityName}: 'filterMax' field must remain empty when the quantity is a formula.`,
  "setup.quantity.import.error.data_maxStd_with_formula_invalid_lithium": (quantityName: string) =>
    `Quantity ${quantityName}: 'maximumStd' field must remain empty when the quantity is a formula.`,
  "setup.quantity.import.error.data_min_max_std_invalid_helium": (quantityName: string) =>
    `Quantity ${quantityName}: 'filterMin' and 'filterMax' fields must contain numerical values.`,
  "setup.quantity.import.error.data_min_max_std_invalid_lithium": (quantityName: string) =>
    `Quantity ${quantityName}: 'minimumValue', 'maximumValue' and 'maximumStd' fields must contain numerical values.`,
  "setup.quantity.import.error.data_min_sup_max_invalid_helium": (quantityName: string) =>
    `Quantity ${quantityName}: 'filterMin' field must contain a value smaller than the one in 'filterMax'.`,
  "setup.quantity.import.error.data_min_sup_max_invalid_lithium": (quantityName: string) =>
    `Quantity ${quantityName}: 'minValue' field must contain a value smaller than the one in 'maxValue'.`,
  "setup.quantity.import.error.data_negative_maxStd_invalid_helium": (quantityName: string) =>
    `Quantity ${quantityName}: 'filterMax' column must contain positive numerical values.`,
  "setup.quantity.import.error.data_negative_maxStd_invalid_lithium": (quantityName: string) =>
    `Quantity ${quantityName}: 'maximumStd' column must contain positive numerical values.`,
  "setup.quantity.import.error.data_output_model_name_too_long": (quantityName: string) =>
    `Quantity ${quantityName}: 'outputModelName' field must contain less than 100 characters.`,
  "setup.quantity.import.error.data_quantity_duplicated": (quantityName: string) =>
    `Quantity ${quantityName}: a quantity cannot appear on two different lines.`,
  "setup.quantity.import.error.data_quantity_name_too_long_helium": (quantityName: string) =>
    `Quantity ${quantityName}: 'technicalName' field must contain less than 100 characters.`,
  "setup.quantity.import.error.data_quantity_name_too_long_lithium": (quantityName: string) =>
    `Quantity ${quantityName}: 'name' field must contain less than 100 characters.`,
  "setup.quantity.import.error.data_sensor_tag_too_long": (quantityName: string) =>
    `Quantity ${quantityName}: 'sensorTag' field must contain less than 100 characters.`,
  "setup.quantity.import.error.data_start_date_invalid_format": (quantityName: string) =>
    `Quantity ${quantityName}: the 'startDate' must be in format YYYY-MM-DD, YYYY-MM-DD HH:mm(:ss), or UTC ISO (ex. 2024-01-01T00:00:00.000Z)`,
  "setup.quantity.import.error.data_start_date_missing_base": (quantityName: string) =>
    `Quantity ${quantityName}: another row must exist for the same quantity with an empty 'startDate'`,
  "setup.quantity.import.error.data_unit_too_long": (quantityName: string) =>
    `Quantity ${quantityName}: 'unit' field must contain less than 100 characters.`,
  "setup.quantity.import.error.locked_true_without_formula": (quantityName: string) =>
    `Quantity ${quantityName}: 'locked' field can’t be set to true for a quantity linked to a sensor`,
  "setup.quantity.import.error.missing_sensortag_or_formula": (quantityName: string) =>
    `Quantity ${quantityName}: at least one of 'sensorTag' or 'formula' field must be filled in.`,
  "setup.simulation.blank_state.button": "Import all or part of the files",
  "setup.simulation.blank_state.text": "Import files defining model and its inputs and outputs",
  "setup.simulation.blank_state.title": "Simulation setup is empty",
  "setup.simulation.image_model.import.error.wrong_file_format":
    "The file must be in svg or png format.",
  "setup.simulation.import_batch.checking": "Simulation setup updated",
  "setup.simulation.import_files": "Import your files",
  "setup.simulation.import_image": "Import image",
  "setup.simulation.import_model.modal.subtitle": "Model format",
  "setup.simulation.input_model.error.data_file_empty": "The CSV is empty.",
  "setup.simulation.input_model.error.data_quantity_duplicated": (quantityName: string) =>
    `Quantity ${quantityName}: a quantity cannot appear on two different lines.`,
  "setup.simulation.input_model.error.data_quantity_name_not_allowed": (quantityName: string) =>
    `Quantity ${quantityName}: 'name' or 'technicalName' fields must contain quantities defined in the project.`,
  "setup.simulation.input_model.error.file_invalid_content":
    "Expected columns: 'name', 'inputModelName' (with optional 'orderExternalGap' and 'referenceValue').",
  "setup.simulation.input_model.error.file_invalid_type": "The file must be in csv format.",
  "setup.simulation.input_model.error.has_time_function_dependency":
    "A quantity depending on time cannot be used in the setup",
  "setup.simulation.input_model.error.name.empty":
    "The 'name' or 'technicalName' field cannot be empty.",
  "setup.simulation.input_model.error.order_external_gap.ascending":
    "The 'orderExternalGap' column must contain an increasing series of positive integers starting with 1.",
  "setup.simulation.input_model.error.order_external_gap.invalid": (quantityName: string) =>
    `Quantity ${quantityName}: 'orderExternalGap' field must contain a strictly positive integer.`,
  "setup.simulation.input_model.error.reference_value.invalid": (quantityName: string) =>
    `Quantity ${quantityName}: 'referenceValue' field must contain numerical values.`,
  "setup.simulation.input_model.import.error.external_gap.invalid": (quantityName: string) =>
    `Quantity ${quantityName}: 'referenceValue' field cannot be empty when the 'orderExternalGap' field is filled in.`,
  "setup.simulation.modal.binding.error.invalid_external_gap": ({ data }: { data: string[] }) => (
    <>
      {data.join(", ")} must have both the reference value and the order of the external gap
      defined.
    </>
  ),
  "setup.simulation.modal.binding.error.missing_column":
    "Format error: expected column names are “name”, “inputModelName“ and “outputModelName” ",
  "setup.simulation.modal.binding.error.orderExternalGapValue": ({ data }: { data: string[] }) =>
    `The 'orderExternalGap' field must contain integers for ${
      data.length > 1 ? "the quantities" : "the quantity"
    } ${data.join(", ")}.`,
  "setup.simulation.modal.binding.error.order_external_no_ascending_starting_one":
    "The order of the external gap must be an integer greater than or equal to 1 and must be in ascending order.",
  "setup.simulation.modal.binding.error.referenceValue": ({ data }: { data: string[] }) =>
    `The 'referenceValue' field must contain numerical values for ${
      data.length > 1 ? "the quantities" : "the quantity"
    } ${data.join(", ")}.`,
  "setup.simulation.modal.binding.error.unknown_quantity": ({ data }: { data: string[] }) => (
    <>
      {data.join(", ")}
      <>{data.length > 1 ? " are " : " is "}</>
      not a quantity of the project
    </>
  ),
  "setup.simulation.modal.binding.tooltip.info": "Expected format: .csv",
  "setup.simulation.modal.expected_file_format.already_existing":
    "Another file has been identified for import: ",
  "setup.simulation.modal.expected_file_format.inputs": "Model inputs :",
  "setup.simulation.modal.expected_file_format.inputs_outputs": "Inputs and outputs:",
  "setup.simulation.modal.expected_file_format.inputs_outputs.value": (displayOptional = true) =>
    `.csv ${displayOptional ? "(optional)" : ""}`,
  "setup.simulation.modal.expected_file_format.model.file": (
    modelType: string,
    fileNumber: number,
  ) => renderFileText(modelType, fileNumber),
  "setup.simulation.modal.expected_file_format.model.file.value": (
    modelType: string,
    fileNumber: number,
  ) => renderFileValue(modelType, fileNumber),
  "setup.simulation.modal.expected_file_format.model_image": "Model image:",
  "setup.simulation.modal.expected_file_format.model_image.value": ".svg or .png (optional)",
  "setup.simulation.modal.expected_file_format.non_identified": "Unused files:",
  "setup.simulation.modal.expected_file_format.outputs": "Model outputs :",
  "setup.simulation.modal.expected_file_format.unexpected_file":
    "The name or format of the file is unexpected: ",
  "setup.simulation.modal.fmu.parameters.tooltip.info":
    "Expected format: .csv (name containing parameters)",
  "setup.simulation.modal.fmu.tooltip.info":
    "Expected format: .csv (name containing iteration_variables)",
  "setup.simulation.modal.image.tooltip.info": "Expected format: .png or .svg",
  "setup.simulation.modal.main.dymola.tooltip.error": "File dymosim required for the model import",
  "setup.simulation.modal.main.fmu.tooltip.error": "File .fmu required for the model import",
  "setup.simulation.modal.variables.dymola.tooltip.error":
    "File dsin.txt required for the model import",
  "setup.simulation.output_model.error.data_quantity_duplicated": (quantityName: string) =>
    `Quantity ${quantityName}: a quantity cannot appear on two different lines.`,
  "setup.simulation.output_model.error.file_invalid_content":
    "The expected columns are 'name', 'outputModelName' and 'offsetExpected'.",
  "setup.simulation.output_model.error.file_invalid_type": "The file must be in csv format.",
  "setup.simulation.output_model.import.error.data_file_empty": "The CSV is empty.",
  "setup.simulation.output_model.import.error.data_offset_expected_invalid": (
    quantityName: string,
  ) => `Quantity ${quantityName}: 'offsetExpected' field must contain numerical values.`,
  "setup.simulation.output_model.import.error.data_offset_expected_invalid_definition": (
    quantityName: string,
  ) =>
    `Quantity ${quantityName}: 'offsetExpected' field must be empty when the quantity is not a model output.`,
  "setup.simulation.output_model.import.error.data_output_model_invalid_definition": (
    quantityName: string,
  ) =>
    `Quantity ${quantityName}: 'offsetExpected' field cannot be empty when the quantity is a model output.`,
  "setup.simulation.output_model.import.error.data_quantity_name_not_allowed": (
    quantityName: string,
  ) =>
    `Quantity ${quantityName}: 'name' or 'technicalName' fields must contain quantities defined in the project.`,
  "setup.simulation.status.incomplete": () => (
    <>
      The configuration of the simulations is <strong>incomplete</strong> because:
    </>
  ),
  "setup.simulation.status.incomplete.noInput": "No model input list was imported",
  "setup.simulation.status.incomplete.noModel": "No model was imported",
  "setup.simulation.status.incomplete.noOutput": "No model output list was imported",
  "setup.simulation.status.invalid": () => (
    <>
      The simulation setup is <strong>invalidated</strong> because the model verification failed.
    </>
  ),
  "setup.simulation.status.valid": () => (
    <>
      The simulation setup is <strong>validated</strong>.
    </>
  ),
  "setup.simulation.status.validating": () => (
    <>
      The simulation setup is <strong>being validated</strong>.
    </>
  ),
  "setup.simulation.table.input": "Model input variable",
  "setup.simulation.table.offset": "Offset",
  "setup.simulation.table.orderExternalGap": "External gap order",
  "setup.simulation.table.output": "Model output variable",
  "setup.simulation.table.quantities": "Quantity",
  "setup.simulation.table.referenceValue": "Reference value",
  "setup.simulation.table.unit": "Unit",
  "setups.modal.add_or_edit.create_setup.create": "Create a setup",
  "setups.modal.add_or_edit.create_setup.update": "Edit setup",
  "setups.modal.create_or_edit.input.application_date.error.cannot_be_empty":
    "Start date cannot be empty",
  "setups.modal.create_or_edit.input.name.error.cannot_be_empty": "Setup name cannot be empty",
  "setups.modal.create_or_edit.input.name.error.name_already_exists": "Setup name already exists",
  "setups.modal.create_or_edit.input.startDate.error.already_exists":
    "Another setup already uses this start date",
  "setups.modal.create_or_edit.input.startDate.error.cannot_be_empty": "Start date cannot be empty",
  "setups.modal.create_or_edit.input.startDate.error.should_be_greater":
    "Start date should be greater than the one of the last setup",
  "setups.modal.delete.last_setup.infos": ({ currentSetupName }: { currentSetupName: string }) => (
    <>
      Do you confirm the deletion of setup{" "}
      <>
        <strong>{currentSetupName}</strong>?
      </>{" "}
      No setup will be linked to the project.
    </>
  ),
  "setups.modal.delete.setup.infos": ({
    currentSetupName,
    linkedSetupName,
  }: {
    currentSetupName: string;
    linkedSetupName: string | undefined;
  }) => (
    <>
      Do you confirm the deletion of setup{" "}
      <>
        <strong>{currentSetupName}</strong>?
      </>{" "}
      The period currently linked to this setup will be linked to setup{" "}
      <>
        <strong>{linkedSetupName}</strong>.
      </>
    </>
  ),
  "setups.modal.delete_setup": "Delete setup",
  "setups.modal.duplicate.duplicated_name": ({ name }: { name: string }) => `Copy of ${name}`,
  "setups.modal.duplicate_setup": "Duplicate setup",
  "setups.modal.duplicate_setup.message.warning.reset_simulation_diagnostic":
    "Following this action, simulation and diagnostic results will be deleted for analyses carried out after the start date.",
  "setups.modal.duplicate_setup.title.description": "Description",
  "setups.modal.duplicate_setup.title.description.placeholder": "Description",
  "setups.modal.duplicate_setup.title.effective_start_date": "Application date",
  "setups.modal.duplicate_setup.title.name": "Name",
  "setups.modal.duplicate_setup.title.name.placeholder": "Name of the setup",
  "setups.modal.toast.delete_setup": ({ name }: { name: string }) => `Setup ${name} deleted.`,
  "setups.modal.update_analyses_frequency.description":
    "The chosen interval will correspond to the average time between two new analyses, and will apply to future data imports.",
  "setups.modal.update_analyses_frequency.title": "Edit average interval",
  "setups.table.column.analysisCount": "Analyses",
  "setups.table.column.author": "Author",
  "setups.table.column.description": "Description",
  "setups.table.column.endDate": "End date",
  "setups.table.column.name": "Name",
  "setups.table.column.startDate": "Start date",
  "setups.table.column.status": "Status",
  "showPublishedAnalysis.text": "Display publication markers",
  "sidepanel.tab.activity": "Activity",
  "sidepanel.tab.activity.fault_event": "Activity feed",
  "sidepanel.tab.comments": "Comments",
  "simulationOutput.edit.confirm.title": "Confirm the modification",
  "simulationOutput.edit.confirmation": (name: string) => (
    <>
      Would you like to confirm the modification of <strong>{name}</strong> ?
    </>
  ),
  "sources.button.import": "Load data",
  "sources.delete.forbidden.title": "Source cannot be deleted, delete its projects first",
  "sources.delete.label": "Name of the source to delete",
  "sources.delete.placeholder": "Name of the source",
  "sources.delete.title": "Delete source",
  "sources.delete.toast.info": ({ name }: { name: string }) =>
    `Source ${name} deletion in progress, it may take a few minutes`,
  "sources.delete.toast.success": ({ name }: { name: string }) =>
    `Source ${name} successfully deleted`,
  "sources.delete.warning": ({ name }: { name: string }) => (
    <>
      You’re about to definitely delete source <strong>{name}</strong>.
      <br />
      Please write down below the name of the source to confirm deletion.
    </>
  ),
  "sources.duplicate.duplicated_name": ({ name }: { name: string }) => `${name}_Copy`,
  "sources.duplicate.input.date.error.invalid_datetime": "Invalid date and time",
  "sources.duplicate.input.date.error.invalid_range":
    "The start date must be before or equal to the end date",
  "sources.duplicate.input.name.error.cannot_be_empty": "Source name cannot be empty",
  "sources.duplicate.input.name.error.name_already_exists": "Source name already exists",
  "sources.duplicate.input.name.error.too_long": "Name must not exceed 100 characters",
  "sources.duplicate.label": "New source name",
  "sources.duplicate.range.from": "From",
  "sources.duplicate.range.helper":
    "You can limit duplication to a date range by adding a start date or an end date.",
  "sources.duplicate.range.helper_bounds":
    "Selected days are interpreted in your local timezone: from starts at 00:00:00 and to ends at 23:59:59.",
  "sources.duplicate.range.to": "To",
  "sources.duplicate.title": "Duplicate source",
  "sources.duplicate.toast.info": ({
    originSourceId,
    targetSourceId,
  }: {
    originSourceId: string;
    targetSourceId: string;
  }) =>
    `Duplication of source ${originSourceId} to ${targetSourceId} in progress, it may take a few minutes`,
  "sources.duplicate.toast.success": ({
    originSourceId,
    targetSourceId,
  }: {
    originSourceId: string;
    targetSourceId: string;
  }) => `Duplication of source ${originSourceId} to ${targetSourceId} done successfully`,
  "sources.duplicate.warning": ({ name }: { name: string }) => (
    <>
      You are about to duplicate source <strong>{name}</strong>.
    </>
  ),
  "sources.groups.editor.delete.apply": "Delete cycles",
  "sources.groups.editor.delete.from": "Cycles to delete",
  "sources.groups.editor.delete.prompt": "Select cycle(s)",
  "sources.groups.editor.delete.title": "Delete cycles",
  "sources.groups.editor.error.delete_required": "Select at least one cycle.",
  "sources.groups.editor.error.group_not_found": "Cycle not found.",
  "sources.groups.editor.error.group_required": "Cycle name is required.",
  "sources.groups.editor.error.invalid_range": "Invalid date range.",
  "sources.groups.editor.error.segment_required": "Select dates first.",
  "sources.groups.editor.error.segment_same": "These dates already have this cycle.",
  "sources.groups.editor.group.label": "Cycle name",
  "sources.groups.editor.pending.clear": "Clear pending",
  "sources.groups.editor.pending.confirm": "Confirm changes",
  "sources.groups.editor.pending.empty": "No pending changes.",
  "sources.groups.editor.pending.item.delete": ({
    groups,
    count,
  }: {
    groups: string;
    count: number;
  }) => `Delete cycles: ${groups} (${count} points)`,
  "sources.groups.editor.pending.item.range": ({
    group,
    from,
    to,
    count,
  }: {
    group: string;
    from: string;
    to: string;
    count: number;
  }) => `Assign "${group}" to ${from} → ${to} (${count} points)`,
  "sources.groups.editor.pending.item.segment": ({
    group,
    from,
    to,
    count,
  }: {
    group: string;
    from: string;
    to: string;
    count: number;
  }) => `Assign "${group}" to dates ${from} → ${to} (${count} points)`,
  "sources.groups.editor.pending.title": "Pending changes",
  "sources.groups.editor.range.apply": "Assign cycle",
  "sources.groups.editor.range.copied": "Range copied from selected row.",
  "sources.groups.editor.range.from": "From",
  "sources.groups.editor.range.none":
    "Select a date range to prefill the fields, or define a custom date range.",
  "sources.groups.editor.range.title": "Assign a cycle to a date range",
  "sources.groups.editor.range.to": "To",
  "sources.groups.editor.success": "Cycles updated.",
  "sources.groups.empty": "No cycles to display.",
  "sources.groups.error": ({ details }: { details: string }) => `Unable to load cycles: ${details}`,
  "sources.groups.link": "Manage cycles",
  "sources.groups.modal.title": "Manage source cycles",
  "sources.groups.table.action.select": "Select date range",
  "sources.groups.table.column.dateRange": "Date range",
  "sources.groups.table.column.group": "Cycle",
  "sources.groups.table.group.none": "No cycle",
  "sources.groups.timeline.gap": "No data",
  "sources.header.title": "Data sources",
  "sources.patchModal.action.import.errorMultipleFiles": "Please load only 1 file",
  "sources.patchModal.action.success.action": "Create a new project linked to this source.",
  "sources.patchModal.action.success.message": ({ sourceId }) =>
    `Data loaded in data source ${sourceId}.`,
  "sources.patchModal.data.period": "Period",
  "sources.patchModal.data.sensorCount": "Sensor number",
  "sources.patchModal.data.snapshotCount": "Datapoints number",
  "sources.patchModal.source.conflicting": "Conflict - Blocking data load",
  "sources.patchModal.source.conflicting.tooltip":
    " Data already loaded are in conflict with the data of the selected file. Data load is blocked.",
  "sources.patchModal.source.ignored": "Already present - Ignored",
  "sources.patchModal.source.ignored.tooltip":
    "Data of the selected file that are already present will be ignored during import.",
  "sources.patchModal.source.merged": "Already present - Merged",
  "sources.patchModal.source.merged.tooltip":
    "Data already present will be completed with the data of selected file.",
  "sources.patchModal.source.new": "New",
  "sources.patchModal.source.title": "Source",
  "sources.selectFilter.project": "Project",
  "sources.table.column.firstDate": "First date",
  "sources.table.column.lastDate": "Last date",
  "sources.table.column.linkedProjects": "Projects",
  "sources.table.column.meanInterval": "Average interval",
  "sources.table.column.meanInterval.format"({
    days,
    hours,
    minutes,
  }: {
    days: number;
    hours: number;
    minutes: number;
  }) {
    if (days > 0) return `${days} day${days > 1 ? "s" : ""}`;
    if (hours > 0) return `${hours} hour${hours > 1 ? "s" : ""}`;
    return `${Math.ceil(minutes)} minute${minutes > 1 ? "s" : ""}`;
  },
  "sources.table.column.name": "Name",
  "sources.table.column.sensorCount": "Sensors",
  "sources.table.column.snapshotCount": "Time points",
  "sources.title": "Data sources",
  "summary.chart.evolution.noAnalysisPublished": "Analysis not published",
  "summary.chart.evolution.option.qoi.description": (translations: {
    FR: {
      LOSSES: string;
      UNEXPLAINED: string;
    };
    EN: {
      LOSSES: string;
      UNEXPLAINED: string;
    };
  }) =>
    `Display modes for performance indicators provide a view targeted on a given KPI.\nFaults are plotted based on their impacts on this quantity, ${lowercase(
      translations.EN.LOSSES,
    )} and ${lowercase(translations.EN.UNEXPLAINED)} ${lowercase(
      translations.EN.LOSSES,
    )} are those of the chosen quantity.`,
  "summary.chart.evolution.title": "Temporal evolution",
  "summary.chart.evolution.tooltip.description.confirmed": "Confirmed fault",
  "summary.chart.evolution.tooltip.description.false_detection": "False detection",
  "summary.chart.evolution.tooltip.description.suspected": "Suspected fault",
  "summary.chart.evolution.tooltip.description.to_be_analysed": "To be analyzed",
  "summary.dashboard.actions.add": (translations: CustomTranslations) =>
    `Add a ${translations.EN.DASHBOARD.toLowerCase()}`,
  "summary.dashboard.actions.edit": "Edit",
  "summary.dashboard.actions.export_pdf": "Export in PDF",
  "summary.dashboard.actions.move_left": "Move to the left",
  "summary.dashboard.actions.move_right": "Move to the right",
  "summary.dashboard.actions.remove": "Remove",
  "summary.dashboard.actions.remove.summary_disabled": "This view cannot be removed",
  "summary.dashboard.menu.tooltip.actions": "Actions",
  "summary.dashboard.modal.remove.text": (
    { dashboardName }: { dashboardName: ReactNode },
    translations: CustomTranslations,
  ) => (
    <>
      Are you sure you want to remove the {translations.EN.DASHBOARD.toLowerCase()} {dashboardName}?{" "}
      <br /> This action won’t affect the {translations.EN.DASHBOARD.toLowerCase()} itself, but it
      will no longer be visible on this page.
    </>
  ),
  "summary.dashboard.modal.remove.title": (translations: CustomTranslations) =>
    `Remove ${translations.EN.DASHBOARD.toLowerCase()}`,
  "summary.dashboard.popup.create": (translations: CustomTranslations) =>
    `Create a ${translations.EN.DASHBOARD.toLowerCase()} (shared by default)`,
  "summary.dashboard.popup.empty": (translations: CustomTranslations) =>
    `No ${translations.EN.DASHBOARD.toLowerCase()} has been added to the home page.`,
  "summary.dashboard.popup.empty.subtitle": (translations: CustomTranslations) =>
    `Get started quickly with our pre-configured and customizable standard template or create your own ${translations.EN.DASHBOARD.toLowerCase()}.`,
  "summary.dashboard.popup.template": "Add the pre-configured template",
  "summary.dashboard.tab.tooltip.see_more": "See more",
  "summary.empty.action": "Go to the settings page",
  "summary.empty.title":
    "Please add at least one performance indicator to display the results in the summary page",
  "summary.failures.table.amplitude": "Amplitude",
  "summary.failures.table.degradationType": "Degradation type",
  "summary.failures.table.equipmentType": "Equipment type",
  "summary.failures.table.equipments": "Equipment",
  "summary.failures.table.failureCategory": "Category",
  "summary.failures.table.failures": "Faults",
  "summary.failures.table.loss": (quantity: string) => `${quantity}`,
  "summary.failures.table.reparability": "Outage time for repair",
  "summary.failures.table.suspectedElements": "Suspected elements",
  "summary.failures.table.system": "System",
  "summary.financial_impact_estimation.cost.label": ({
    reparability,
    translations,
  }: {
    reparability: "REPARABILITY_0" | "REPARABILITY_1" | "REPARABILITY_2";
    translations: {
      FR: { REPARABILITY_0: string; REPARABILITY_1: string; REPARABILITY_2: string };
      EN: { REPARABILITY_0: string; REPARABILITY_1: string; REPARABILITY_2: string };
    };
  }) => `with outage time for repair ${translations.EN[reparability]}`,
  "summary.financial_impact_estimation.cost.price": "*price of 60€/MWh",
  "summary.financial_impact_estimation.title": "Estimated financial impact",
  "summary.financial_impact_estimation.tooltip": (translations: {
    FR: { REPARABILITY_0: string; REPARABILITY_1: string; REPARABILITY_2: string };
    EN: { REPARABILITY_0: string; REPARABILITY_1: string; REPARABILITY_2: string };
  }) =>
    `The financial impact is calculated by projecting, for each fault, the loss of production between now and the recovery of the associated loss, and valuing it at a price of 60€/MWh. The duration of the fault is estimated at 3 weeks for ${translations.EN.REPARABILITY_0}, 2 months for ${translations.EN.REPARABILITY_1} and 7,000 h for ${translations.EN.REPARABILITY_2}.`,
  "summary.instrumentation.state.invalid_measure": (count: number): string =>
    count > 1 ? "Invalidated measurements" : "Invalidated measurement",
  "summary.instrumentation.state.missing_measure": (count: number): string =>
    count > 1 ? "Missing sensors" : "Missing sensor",
  "summary.instrumentation.state.unavailable_formula": (count: number): string =>
    count > 1 ? "Unavailable formulas" : "Unavailable formula",
  "summary.instrumentation.table.column.name": "Name",
  "summary.instrumentation.table.column.state": "State",
  "summary.instrumentation.title": "Instrumentation",
  "summary.widget.failures.confirmed_failures": "Confirmed faults",
  "summary.widget.failures.rate": "Explanation rate",
  "summary.widget.losses.expected": "Expected",
  "summary.widget.losses.external_loss.metadata.boundary_condition": "Model input",
  "summary.widget.losses.external_loss.metadata.measured": "Measured",
  "summary.widget.losses.external_loss.metadata.order": "Order",
  "summary.widget.losses.external_loss.metadata.reference": "Reference",
  "summary.widget.losses.internal": (translations: CustomTranslations): string =>
    `Internal ${lowercase(translations.EN.LOSSES)}`,
  "summary.widget.losses.measured": "Measured",
  "summary.widget.losses.performanceIndicator": "Performance indicator",
  "summaryPage.widgets.losses.switch.label": "Details",
  "summaryPage.widgets.losses.title.external_losses": (translations: CustomTranslations): string =>
    `External ${lowercase(translations.EN.LOSSES)}`,
  "summaryPage.widgets.losses.title.internal_losses": (translations: CustomTranslations): string =>
    `Internal ${lowercase(translations.EN.LOSSES)}`,
  "symptom.chart.header.option.amplitude": "Value",
  "symptom.chart.header.option.amplitude.description":
    "Overlay of measured values (same curve as the one in the Data page) and expected values.",
  "symptom.chart.header.option.gap": "Gap",
  "symptom.chart.header.option.gap.description":
    "Evolution of the difference between measured and expected values.\nSymptom = Measured -  Expected.",
  "symptom.chart.header.option.normalizedGap": "Normalized gap",
  "symptom.chart.header.option.normalizedGap.description":
    "Gap divided by the Sigma normalization factor of the quantity, as defined in the Setup page.\nThis is the only display mode that allows symptoms comparison through quantities multi-selection.",
  "symptom.chart.header.option.title": "Display mode",
  "symptoms.chart.legend.expected.description": "Expected value computed by the digital twin",
  "symptoms.chart.legend.expected.name": "Expected",
  "symptoms.chart.legend.measured.description": "Value returned by a sensor or calculated",
  "symptoms.chart.legend.measured.name": "Measured",
  "symptoms.chart.legend.no_symptom.description": "No symptom detected",
  "symptoms.chart.legend.no_symptom.name": "No symptom",
  "symptoms.chart.legend.symptom.description": "Symptom detected",
  "symptoms.chart.legend.symptom.name": "Symptom",
  "symptoms.chart.legend.valid_partial_inputs": "Calculation based on valid data only",
  "symptoms.chart.title": "Symptoms",
  "symptoms.chart.tooltip.description.expected": "Expected value",
  "symptoms.chart.tooltip.description.missingBoundaryConditions": (missingBoundaries: string[]) =>
    missingBoundaries.length === 1
      ? `Missing model input: \n${missingBoundaries.join(", ")}`
      : `Missing model input: \n${missingBoundaries.join(", ")}`,
  "symptoms.chart.tooltip.description.simulationError": (statusInfo: string) =>
    `Simulation error\n${statusInfo}\n...\nClick on the red marker to copy the full error message`,
  "symptoms.chart.tooltip.description.uncompletedSimulation": "Simulation not carried out",
  "symptoms.chart.tooltip.selected_chart.gap": "Gap",
  "symptoms.gap.chart.noSymptom": "|Symptom| < 2σ",
  "symptoms.gap.chart.symptom": "|Symptom| > 2σ",
  "users.modal.add_project_to_user.add_project": "Add a project",
  "users.modal.add_project_to_user.select.select_project": "Select a project",
  "users.modal.create.add_user": "Add new user",
  "users.modal.create.create_user.subtitle": "User ID",
  "users.modal.create.create_user.text":
    "Please enter the user ID and, if necessary, select the global role corresponding to the desired access level for this person:",
  "users.modal.create.create_user.toast": ({ name }: { name: string }) =>
    `${name} profile created.`,
  "users.modal.create.input.id": "#",
  "users.modal.create.input.login.error.text_to_long": "Login is limited to 100 characters",
  "users.modal.create.input.name.error.cannot_be_empty": "User name cannot be empty",
  "users.modal.create.input.name.error.text_to_long": "This field is limited to 100 characters",
  "users.modal.create.select.global_role.label": "Global role",
  "users.modal.create.user_name.input.name": "Full name",
  "users.modal.create.user_name.input.name.label": "Name",
  "users.modal.create.user_name.subtitle": "Personal details",
  "users.modal.create.user_name.text":
    "Please provide your full name, so that your colleagues can identify you in Metroscope",
  "users.modal.create.user_name.title": "Your profile",
  "users.modal.create.user_name.toast": "Your name has been set.",
  "users.modal.delete.are_you_sure": ({ login }: { login: string }) => (
    <span>
      Do you confirm deletion of user <b>{login}</b>?
    </span>
  ),
  "users.modal.delete.delete_user": "Delete user",
  "users.modal.delete.delete_user.toast": ({ name }: { name: string }) =>
    `${name} profile and access rights deleted from the application.`,
  "users.modal.roles.global_role": "Global role",
  "users.modal.roles.identifier": "Identifier",
  "users.modal.roles.specific_roles": "Specific roles",
  "users.modal.roles.table.project_name": "Project name",
  "users.modal.roles.table.role": "Role",
  "users.modal.update.input.name": "Full name",
  "users.modal.update.input.name.label": "Name",
  "users.modal.update.update_user": "Personal details",
  "users.modal.update.update_user.toast": "Your name has been updated.",
  "users.paragraphs.users.table.column.global_roles": "Global roles",
  "users.paragraphs.users.table.column.id": "User ID",
  "users.paragraphs.users.table.column.name": "Name",
  "users.paragraphs.users.table.column.project_roles": "Specific roles",
  "users.selectFilter.application_admin": "Admin",
  "users.selectFilter.none": "None",
  "users.selectFilter.project": "Project",
  "users.selectFilter.project_admin": "Access manager",
  "users.selectFilter.project_prod_read_only": "Read projects in production",
  "users.selectFilter.project_reader": "Read",
  "users.selectFilter.project_setuper": "Setup",
  "users.selectFilter.project_writer": "Write",
  "users.selectFilter.role": "Global role",
  "users.table.row.add_project": "Add a project",
  "users.table.row.icons.delete.user": "Delete user",
  "users.table.row.menu.roles.application.admin.description":
    "Can edit setup and write in all projects, create/delete projects and manage users",
  "users.table.row.menu.roles.application.admin.title": "Admin",
  "users.table.row.menu.roles.application.read.description":
    "Can read all projects, including research projects",
  "users.table.row.menu.roles.application.read.title": "Read",
  "users.table.row.menu.roles.application.read_access_jupyterhub.description":
    "Can read all projects, including research projects and access to JupyterHub",
  "users.table.row.menu.roles.application.read_access_jupyterhub.title": "Read + Access JupyterHub",
  "users.table.row.menu.roles.application.read_prod_only.description":
    "Can read all projects in production",
  "users.table.row.menu.roles.application.read_prod_only.title": "Read projects in production",
  "users.table.row.menu.roles.application.setup.description":
    "Can edit setup and write in all projects",
  "users.table.row.menu.roles.application.setup.title": "Setup",
  "users.table.row.menu.roles.application.super_admin.description":
    "Full access across all projects and advanced administration capabilities",
  "users.table.row.menu.roles.application.write.description":
    "Can load data, run diagnosis and comment all projects",
  "users.table.row.menu.roles.application.write.title": "Write",
  "users.table.row.menu.roles.no_role": "None",
  "users.table.row.menu.roles.project.admin.description":
    "Can add users and assign roles in this project",
  "users.table.row.menu.roles.project.admin.title": "Access manager",
  "users.table.row.menu.roles.project.read.description": "Can read this project",
  "users.table.row.menu.roles.project.read.title": "Read",
  "users.table.row.menu.roles.project.setup.description":
    "Can edit setup and write in this project",
  "users.table.row.menu.roles.project.setup.title": "Setup",
  "users.table.row.menu.roles.project.write.description":
    "Can load data, run diagnosis and comment in this project",
  "users.table.row.menu.roles.project.write.title": "Write",
  "users.table.row.modal.content.delete_specific_role": ({ name }: { name: string }) =>
    `Do you want to remove the specific role set on the project ${name}?`,
  "users.table.row.modal.title.delete_specific_role": "Remove a specific role",
  "users.table.row.projects_count": ({ count }: { count: number }) =>
    count === 1 ? `${count} Project` : `${count} Projects`,
  "users.table.row.roles.application.toast_new_role": ({
    userName,
    role,
  }: {
    userName: string;
    role: string;
  }) => `${role} role set to ${userName}.`,
  "users.table.row.roles.application.toast_no_role": ({ userName }: { userName: string }) =>
    `No global role assigned to ${userName}.`,
  "users.table.row.roles.project.toast_add_role": ({
    userName,
    projectName,
    role,
  }: {
    userName: string;
    projectName: React.ReactNode;
    role: string;
  }) => `${role} role has been set to ${userName} on ${projectName} project.`,
  "users.table.row.roles.project.toast_remove_project": ({
    userName,
    projectName,
  }: {
    userName: string;
    projectName: string;
  }) => `${userName} access to ${projectName} project has been removed.`,
  "users.table.row.roles.project.toast_remove_role": ({
    userName,
    projectName,
    role,
  }: {
    userName: string;
    projectName: React.ReactNode;
    role: string;
  }) => `${role} role has been removed from ${projectName} project to ${userName}.`,
  "users.title": "Users",
  "validations.manualFaultEvents": enValidations.manualFaultEvents,
};

export default en;
